import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Chip, Spinner } from "@heroui/react";
import clsx from "clsx";
import { MarkdownContent } from "./ChatTimeline";
import MainStore from "./MainStore";
import useDataEvent from "./CustomHook";
import { prepareStructuredSummary } from "./utils/structuredSummary";
import ResearchKnowledgeGraph from "./ResearchKnowledgeGraph";
import AgentFlowDiagram from "./AgentFlowDiagram";
import { AgentsPane } from "./AgentsPane";

const INSPECTOR_TABS = [
  { key: "summary", label: "Summary" },
  { key: "evidence", label: "Claims" },
  { key: "flow", label: "Flow Diagram" },
  { key: "graph", label: "Knowledge Graph" },
  { key: "agents", label: "Agents" },
];

const DEFAULT_INTERACTIVE_DESIRED_SHAPE = [
  "Recent announcements",
  "Leadership and profile",
  "Financial performance",
  "Competitor landscape",
  "Suggested meeting topics",
];

// Research-run depth. The server scales every budget from it: the base
// (bootstrap/adaptive) budget, per-agent budget grants, and the global grant
// ceiling. Committed work (e.g. the peer-comparison matrix grid) is funded by
// persisted per-agent entitlements, so even Quick runs honor it.
const RUN_DEPTH_OPTIONS = [
  { key: "quick", label: "Quick", hint: "Half depth — fast pass, fewer follow-ups" },
  { key: "standard", label: "Standard", hint: "Engine default depth" },
  { key: "deep", label: "Deep", hint: "Double depth — full grids and follow-ups, slower run" },
];

const GRAPH_FACET_PRESETS = [
  { key: "hierarchy", label: "Hierarchy model" },
  { key: "entity", label: "Entity hierarchy" },
  { key: "aggregate", label: "Aggregate hierarchy" },
  { key: "source", label: "Source hierarchy" },
  { key: "overview", label: "Raw context graph" },
];

const FACET_AXIS_OPTIONS = [
  { key: "hierarchy_parent", label: "Theme group" },
  { key: "aggregate", label: "Topic" },
  { key: "entity", label: "Entity" },
  { key: "source_domain", label: "Source" },
  { key: "", label: "— None —" },
];

async function fetchJson(url, options = {}) {
  const response = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
      ...(options.headers ?? {}),
    },
    ...options,
  });
  const payload = await response.json().catch(() => null);
  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.error ?? payload?.message ?? `Request failed: ${response.status}`);
  }
  return payload?.result ?? payload;
}

function truncateText(value = "", maxLength = 120) {
  const text = String(value ?? "").trim();
  if (!text || text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, Math.max(0, maxLength - 1)).trim()}…`;
}

function toArray(value) {
  if (value === undefined || value === null) {
    return [];
  }
  return Array.isArray(value) ? value : [value];
}

function uniqueStrings(values = []) {
  return [...new Set(toArray(values).map((value) => String(value ?? "").trim()).filter(Boolean))];
}

function uniqueObjectsBy(values = [], keyFn = (value) => value) {
  const seen = new Set();
  return toArray(values).filter((value) => {
    const key = keyFn(value);
    if (key === undefined || key === null) {
      return false;
    }
    const normalizedKey = typeof key === "string" ? key : JSON.stringify(key);
    if (seen.has(normalizedKey)) {
      return false;
    }
    seen.add(normalizedKey);
    return true;
  });
}

function cloneJson(value) {
  if (value === undefined) {
    return undefined;
  }
  return JSON.parse(JSON.stringify(value));
}

const ACTIVE_WORK_SCHEDULING_FRESH_MS = 15000;

function parseTimestampMs(value = null) {
  if (!value) return null;
  const timestampMs = new Date(value).getTime();
  return Number.isFinite(timestampMs) ? timestampMs : null;
}

function slugify(value = "", fallback = "item") {
  const normalized = String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return normalized || fallback;
}

function normalizeConversationScope(scope = null) {
  if (!scope || typeof scope !== "object") {
    return { type: "root", anchorType: null, anchorIds: [], label: null };
  }
  const anchorIds = uniqueStrings(scope.anchorIds);
  return {
    type: String(scope.type ?? "root").trim() || "root",
    anchorType: scope.anchorType ? String(scope.anchorType).trim().toLowerCase() : null,
    anchorIds,
    label: scope.label ? String(scope.label).trim() : null,
  };
}

function buildFilteredHierarchyWhyNow(parent = {}) {
  const supportingAggregateIds = uniqueStrings(toArray(parent?.supporting_aggregate_ids));
  const supportingClaimIds = uniqueStrings(toArray(parent?.supporting_claim_ids));
  const childLabels = collectHierarchyChildLabels(parent?.child_items, 3);
  if (!supportingAggregateIds.length) {
    return parent?.why_now ?? "Synthesized from recurring theme signals.";
  }
  if (childLabels.length === 1) {
    return `Highlights ${childLabels[0]} across ${supportingAggregateIds.length} aggregate${supportingAggregateIds.length === 1 ? "" : "s"} and ${supportingClaimIds.length} supporting claim${supportingClaimIds.length === 1 ? "" : "s"}.`;
  }
  if (childLabels.length >= 2) {
    const head = childLabels.slice(0, -1).join(", ");
    const tail = childLabels[childLabels.length - 1];
    return `Groups ${head} and ${tail} across ${supportingAggregateIds.length} aggregate${supportingAggregateIds.length === 1 ? "" : "s"} and ${supportingClaimIds.length} supporting claim${supportingClaimIds.length === 1 ? "" : "s"}.`;
  }
  return `${supportingAggregateIds.length} aggregate signal${supportingAggregateIds.length === 1 ? "" : "s"} across ${supportingClaimIds.length} supporting claim${supportingClaimIds.length === 1 ? "" : "s"}.`;
}

function collectHierarchyNodeAggregateIds(node = {}) {
  return uniqueStrings([
    ...toArray(node?.supporting_aggregate_ids),
    String(node?.aggregate_id ?? node?.aggregateId ?? "").trim(),
    ...toArray(node?.child_items).flatMap((child) => collectHierarchyNodeAggregateIds(child)),
  ]);
}

function collectDirectHierarchyNodeAggregateIds(node = {}) {
  return uniqueStrings([
    ...toArray(node?.supporting_aggregate_ids),
    String(node?.aggregate_id ?? node?.aggregateId ?? "").trim(),
  ]);
}

function buildHierarchyGraphNodeId(node = {}, fallback = "subtheme") {
  return (
    String(node?.node_id ?? node?.parent_id ?? node?.aggregate_id ?? node?.aggregateId ?? "").trim() ||
    `hierarchy_node_${slugify(node?.label ?? fallback)}`
  );
}

function buildHierarchyGraphStructures(parents = [], visibleAggregateIds = new Set(), absorbedParentIds = new Set()) {
  const subthemeNodes = [];
  const subthemeEdges = [];
  const aggregateEdges = [];

  const appendAggregateEdge = (hierarchyNodeId, aggregateId, parentKind = "subtheme") => {
    if (!hierarchyNodeId || !aggregateId) {
      return;
    }
    aggregateEdges.push({
      hierarchy_node_id: hierarchyNodeId,
      aggregate_id: aggregateId,
      relation:
        parentKind === "parent"
          ? "aggregate_belongs_to_parent_theme"
          : "aggregate_belongs_to_subtheme",
    });
  };

  const walkNode = (node = {}, parentNodeId = null, parentKind = "parent", parentLabel = "", depth = 1) => {
    const subtreeAggregateIds = collectHierarchyNodeAggregateIds(node).filter((aggregateId) => visibleAggregateIds.has(aggregateId));
    if (!subtreeAggregateIds.length) {
      return null;
    }
    const childItems = toArray(node?.child_items);
    const descendantAggregateIds = uniqueStrings(
      childItems.flatMap((child) => collectHierarchyNodeAggregateIds(child)),
    ).filter((aggregateId) => visibleAggregateIds.has(aggregateId));
    const directAggregateIds = uniqueStrings([
      ...toArray(node?.supporting_aggregate_ids),
      String(node?.aggregate_id ?? node?.aggregateId ?? "").trim(),
    ])
      .filter((aggregateId) => visibleAggregateIds.has(aggregateId) && !descendantAggregateIds.includes(aggregateId));
    const normalizedLabel = String(node?.label ?? "").trim() || parentLabel;
    const shouldInlineWrapper = shouldUnwrapHierarchyDisplayNode(node, parentLabel);

    if (shouldInlineWrapper) {
      childItems.forEach((child) => {
        walkNode(child, parentNodeId, parentKind, normalizedLabel, depth);
      });
      directAggregateIds.forEach((aggregateId) => appendAggregateEdge(parentNodeId, aggregateId, parentKind));
      return null;
    }

    if (!childItems.length && directAggregateIds.length === 1 && parentNodeId) {
      appendAggregateEdge(parentNodeId, directAggregateIds[0], parentKind);
      return null;
    }

    const nodeId = buildHierarchyGraphNodeId(node, `subtheme_${depth}`);
    // Collapse generic wrapper / unassigned nodes under the same parent — use label as nodeId
    const dedupeId = (normalizedLabel === "Unassigned" || GENERIC_HIERARCHY_WRAPPER_LABELS.has(normalizeHierarchyLabel(normalizedLabel)))
      ? `generic:${parentNodeId ?? "root"}:${slugify(normalizedLabel)}`
      : nodeId;
    const childNodeIds = childItems
      .map((child) => walkNode(child, nodeId, "subtheme", normalizedLabel, depth + 1))
      .filter(Boolean);

    subthemeNodes.push({
      id: dedupeId,
      label: normalizedLabel || "Subtheme",
      why_now: String(node?.why_now ?? "").trim() || null,
      depth,
      claim_count: Number(node?.claim_count ?? uniqueStrings(toArray(node?.supporting_claim_ids)).length) || 0,
      agenda_score: Number(node?.agenda_score ?? 0) || 0,
      supporting_aggregate_ids: directAggregateIds,
      aggregate_id: String(node?.aggregate_id ?? node?.aggregateId ?? "").trim() || null,
    });

    if (parentNodeId) {
      subthemeEdges.push({
        child_node_id: dedupeId,
        parent_node_id: parentNodeId,
        relation: "subtheme_belongs_to_theme",
      });
    }
    directAggregateIds.forEach((aggregateId) => {
      appendAggregateEdge(dedupeId, aggregateId, "subtheme");
    });
    return dedupeId;
  };

  const aggregateParentNodes = toArray(parents)
    .map((parent) => {
      const supportingAggregateIds = uniqueStrings(collectHierarchyNodeAggregateIds(parent)).filter((aggregateId) => visibleAggregateIds.has(aggregateId));
      const parentIdForDropCheck = String(parent?.parent_id ?? "").trim();
      if (!supportingAggregateIds.length && !absorbedParentIds.has(parentIdForDropCheck)) return null;
      const parentId = parentIdForDropCheck;
      const directChildNodeIds = toArray(parent?.child_items)
        .map((child) => walkNode(child, parentId, "parent", String(parent?.label ?? "").trim(), 1))
        .filter(Boolean);
      const descendantAggregateIds = uniqueStrings(
        toArray(parent?.child_items).flatMap((child) => collectHierarchyNodeAggregateIds(child)),
      ).filter((aggregateId) => visibleAggregateIds.has(aggregateId));
      const directAggregateIds = supportingAggregateIds.filter((aggregateId) => !descendantAggregateIds.includes(aggregateId));
      directAggregateIds.forEach((aggregateId) => {
        appendAggregateEdge(parentId, aggregateId, "parent");
      });
      return {
        id: parentId,
        label: parent?.label ?? parentId,
        rank: parent?.rank ?? null,
        why_now: String(parent?.why_now ?? "").trim() || null,
        supporting_aggregate_ids: supportingAggregateIds,
        child_node_ids: directChildNodeIds,
      };
    })
    .filter(Boolean);

  return {
    aggregateParentNodes,
    aggregateSubthemeNodes: uniqueObjectsBy(subthemeNodes, (node) => node?.id),
    hierarchyNodeEdges: uniqueObjectsBy(
      subthemeEdges,
      (edge) => `${edge?.child_node_id}:${edge?.parent_node_id}:${edge?.relation}`,
    ),
    aggregateHierarchyEdges: uniqueObjectsBy(
      aggregateEdges,
      (edge) => `${edge?.hierarchy_node_id}:${edge?.aggregate_id}:${edge?.relation}`,
    ),
  };
}

function buildHierarchyFacetIndex(parents = []) {
  const index = new Map();
  const addAggregatePath = (aggregateId, parent = null, subtheme = null) => {
    const normalizedAggregateId = String(aggregateId ?? "").trim();
    if (!normalizedAggregateId || index.has(normalizedAggregateId)) {
      return;
    }
    const parentLabel = normalizeHierarchyLabel(parent?.label) || parent?.label || "Unassigned hierarchy";
    const parentId = parent?.id ?? parent?.parent_id ?? parent?.node_id ?? buildHierarchyGraphNodeId(parent ?? {}, "parent");
    const subthemeLabel = normalizeHierarchyLabel(subtheme?.label) || subtheme?.label || parentLabel;
    const subthemeId =
      subtheme?.id ??
      subtheme?.node_id ??
      (subtheme ? buildHierarchyGraphNodeId(subtheme, "subtheme") : parentId);
    index.set(normalizedAggregateId, {
      parent: {
        id: String(parentId ?? parentLabel),
        label: parentLabel,
      },
      subtheme: {
        id: String(subthemeId ?? subthemeLabel),
        label: subthemeLabel,
      },
    });
  };

  const visitChild = (node = {}, parent = null, directSubtheme = null) => {
    const childItems = toArray(node?.child_items);
    const nextSubtheme = directSubtheme ?? node;
    childItems.forEach((child) => visitChild(child, parent, nextSubtheme));
    const childAggregateIds = new Set(
      childItems.flatMap((child) => collectHierarchyNodeAggregateIds(child)),
    );
    uniqueStrings([
      ...toArray(node?.supporting_aggregate_ids),
      node?.aggregate_id,
      node?.aggregateId,
    ])
      .filter((aggregateId) => !childAggregateIds.has(aggregateId))
      .forEach((aggregateId) => addAggregatePath(aggregateId, parent, nextSubtheme));
  };

  toArray(parents).forEach((parent) => {
    toArray(parent?.child_items).forEach((child) => visitChild(child, parent, null));
    const childAggregateIds = new Set(
      toArray(parent?.child_items).flatMap((child) => collectHierarchyNodeAggregateIds(child)),
    );
    uniqueStrings([
      ...toArray(parent?.supporting_aggregate_ids),
      parent?.aggregate_id,
      parent?.aggregateId,
    ])
      .filter((aggregateId) => !childAggregateIds.has(aggregateId))
      .forEach((aggregateId) => addAggregatePath(aggregateId, parent, null));
  });

  return index;
}

function collectHierarchyChildLabels(nodes = [], maxCount = 3) {
  return uniqueStrings(
    toArray(nodes)
      .map((node) => String(node?.label ?? "").trim())
      .filter(Boolean),
  ).slice(0, Math.max(1, Number(maxCount) || 3));
}

function filterHierarchyChildItems(items = [], visibleAggregateIds = new Set()) {
  return toArray(items)
    .map((item) => {
      const filteredChildren = filterHierarchyChildItems(item?.child_items, visibleAggregateIds);
      const directAggregateIds = uniqueStrings([
        ...toArray(item?.supporting_aggregate_ids),
        String(item?.aggregate_id ?? item?.aggregateId ?? "").trim(),
      ]).filter((aggregateId) => visibleAggregateIds.has(aggregateId));
      const descendantAggregateIds = uniqueStrings(filteredChildren.flatMap((child) => collectHierarchyNodeAggregateIds(child)));
      const filteredDirectAggregateIds = directAggregateIds.filter((aggregateId) => !descendantAggregateIds.includes(aggregateId));
      if (!filteredDirectAggregateIds.length && !descendantAggregateIds.length) {
        return null;
      }
      return {
        ...item,
        supporting_aggregate_ids: filteredDirectAggregateIds,
        child_items: filteredChildren,
      };
    })
    .filter(Boolean);
}

function dedupeHierarchyParents(parents = []) {
  return uniqueObjectsBy(
    toArray(parents),
    (parent) => {
      const parentId = String(parent?.parent_id ?? "").trim();
      if (parentId) return parentId;
      return JSON.stringify({
        label: String(parent?.label ?? "").trim(),
        scope: String(parent?.scope ?? "").trim(),
        supportingAggregateIds: uniqueStrings(toArray(parent?.supporting_aggregate_ids)),
      });
    },
  );
}

function buildHierarchyDisplayItem(node = {}, aggregateById = new Map()) {
  const aggregateIds = collectHierarchyNodeAggregateIds(node);
  const descendantAggregates = uniqueObjectsBy(
    aggregateIds
      .map((aggregateId) => aggregateById.get(aggregateId))
      .filter(Boolean),
    (aggregate) => aggregate?.aggregateId,
  );
  const displayId =
    String(node?.node_id ?? node?.aggregate_id ?? node?.aggregateId ?? "").trim() ||
    `hierarchy_node_${aggregateIds.join("_") || slugify(node?.label ?? "subtheme")}`;
  const aggregateClaimIds = uniqueStrings([
    ...toArray(node?.supporting_claim_ids),
    ...descendantAggregates.flatMap((aggregate) => toArray(aggregate?.claimIds)),
  ]);
  const agendaScore = descendantAggregates.reduce(
    (sum, aggregate) => sum + Number(aggregate?.agendaScore ?? 0),
    Number(node?.agenda_score ?? 0) || 0,
  );
  return {
    displayId,
    aggregateId:
      String(node?.aggregate_id ?? node?.aggregateId ?? "").trim() ||
      descendantAggregates[0]?.aggregateId ||
      null,
    aggregateIds,
    label: String(node?.label ?? "").trim() || descendantAggregates[0]?.label || "Untitled sub-theme",
    whyNow: String(node?.why_now ?? "").trim() || null,
    claimIds: aggregateClaimIds,
    claimCount:
      aggregateClaimIds.length ||
      Number(node?.claim_count ?? 0) ||
      descendantAggregates.reduce((sum, aggregate) => sum + Number(aggregate?.claimCount ?? 0), 0),
    agendaScore,
    companyLabels: uniqueStrings(descendantAggregates.flatMap((aggregate) => toArray(aggregate?.companyLabels))).slice(0, 6),
    entityLabels: uniqueStrings(descendantAggregates.flatMap((aggregate) => toArray(aggregate?.entityLabels))).slice(0, 6),
    sectionKeys: uniqueStrings(descendantAggregates.flatMap((aggregate) => toArray(aggregate?.sectionKeys))).slice(0, 6),
    childLabels: collectHierarchyChildLabels(node?.child_items, 4),
    childItems: toArray(node?.child_items),
    userPolicy: descendantAggregates.every((aggregate) => aggregate?.userPolicy?.hidden === true)
      ? { hidden: true }
      : (descendantAggregates[0]?.userPolicy ?? {}),
  };
}

const GENERIC_HIERARCHY_WRAPPER_LABELS = new Set([
  "company updates",
  "market context",
  "category trends",
  "strategic patterns",
  "financial performance & market context",
  "innovation & technology",
  "strategic moves & partnerships",
  "category & consumer trends",
]);

function normalizeHierarchyLabel(value = "") {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function shouldUnwrapHierarchyDisplayNode(node = {}, parentLabel = "") {
  const childItems = toArray(node?.child_items);
  if (!childItems.length) return false;
  const directAggregateIds = collectDirectHierarchyNodeAggregateIds(node);
  if (directAggregateIds.length) return false;
  const label = normalizeHierarchyLabel(node?.label);
  const normalizedParentLabel = normalizeHierarchyLabel(parentLabel);
  if (childItems.length === 1) return true;
  if (label && normalizedParentLabel && label === normalizedParentLabel) return true;
  return GENERIC_HIERARCHY_WRAPPER_LABELS.has(label);
}

function buildHierarchyDisplayItems(nodes = [], aggregateById = new Map(), parentLabel = "") {
  return toArray(nodes).flatMap((node) => {
    if (shouldUnwrapHierarchyDisplayNode(node, parentLabel)) {
      return buildHierarchyDisplayItems(node?.child_items, aggregateById, String(node?.label ?? "").trim() || parentLabel);
    }
    return [buildHierarchyDisplayItem(node, aggregateById)];
  });
}

function buildParentDisplayChildThemes(parent = {}, aggregateById = new Map()) {
  const supportingSignalIds = uniqueStrings(toArray(parent?.supporting_aggregate_ids));
  const rawChildNodes = toArray(parent?.child_items).length
    ? toArray(parent?.child_items)
    : supportingSignalIds.map((aggregateId, childIndex) => ({
        node_id: `${parent?.parent_id ?? "parent"}_fallback_${childIndex + 1}`,
        label: aggregateById.get(aggregateId)?.label ?? aggregateId,
        supporting_aggregate_ids: [aggregateId],
        supporting_claim_ids: toArray(aggregateById.get(aggregateId)?.claimIds),
        child_items: [],
        aggregate_id: aggregateId,
        claim_count: Number(aggregateById.get(aggregateId)?.claimCount ?? 0),
        agenda_score: Number(aggregateById.get(aggregateId)?.agendaScore ?? 0),
      }));
  return uniqueObjectsBy(
    buildHierarchyDisplayItems(rawChildNodes, aggregateById, String(parent?.label ?? "").trim()),
    (item) => item?.displayId,
  );
}

function buildLocalResearchState(primitive = null) {
  const referenceParameters = primitive?.referenceParameters ?? {};
  const normalizeIdKeyedList = (value) => {
    if (Array.isArray(value)) {
      return value.filter((entry) => entry && typeof entry === "object").map((entry) => cloneJson(entry));
    }
    if (!value || typeof value !== "object") {
      return [];
    }
    return Object.entries(value)
      .filter(([key, entry]) => key && entry && typeof entry === "object" && !Array.isArray(entry))
      .sort((left, right) => String(left[0]).localeCompare(String(right[0])))
      .map(([, entry]) => cloneJson(entry));
  };
  const normalizeClaimList = (value) => {
    if (Array.isArray(value)) {
      return value.filter((claim) => claim && typeof claim === "object").map((claim) => cloneJson(claim));
    }
    if (!value || typeof value !== "object") {
      return [];
    }
    return Object.entries(value)
      .filter(([key, claim]) => /^\d+$/.test(key) && claim && typeof claim === "object")
      .sort((left, right) => Number(left[0]) - Number(right[0]))
      .map(([, claim]) => cloneJson(claim));
  };
  const researchState =
    referenceParameters.research_state && typeof referenceParameters.research_state === "object"
      ? cloneJson(referenceParameters.research_state)
      : {};
  const persistedClaims = normalizeClaimList(referenceParameters.research_claims);
  researchState.claims = normalizeClaimList(researchState.claims);
  if (researchState.claims.length === 0) {
    researchState.claims = persistedClaims;
  }
  researchState.research_map =
    referenceParameters.research_map && typeof referenceParameters.research_map === "object"
      ? cloneJson(referenceParameters.research_map)
      : (
          researchState.research_map && typeof researchState.research_map === "object"
            ? researchState.research_map
            : null
        );
  researchState.section_packets = Array.isArray(referenceParameters.section_packets)
    ? cloneJson(referenceParameters.section_packets)
    : (
        Array.isArray(researchState.section_packets)
          ? researchState.section_packets
          : []
      );
  researchState.section_packet_artifacts = Array.isArray(referenceParameters.section_packet_artifacts)
    ? cloneJson(referenceParameters.section_packet_artifacts)
    : (
        Array.isArray(researchState.section_packet_artifacts)
          ? researchState.section_packet_artifacts
          : []
      );
  researchState.planned_agent_units = Array.isArray(referenceParameters.research_units)
    ? cloneJson(referenceParameters.research_units)
    : (Array.isArray(researchState.planned_agent_units) ? researchState.planned_agent_units : []);
  researchState.agent_blueprints = Array.isArray(referenceParameters.agent_blueprints)
    ? cloneJson(referenceParameters.agent_blueprints)
    : (Array.isArray(researchState.agent_blueprints) ? researchState.agent_blueprints : []);
  researchState.agent_runs = Array.isArray(referenceParameters.agent_runs)
    ? cloneJson(referenceParameters.agent_runs)
    : (Array.isArray(researchState.agent_runs) ? researchState.agent_runs : []);
  researchState.unit_queue = Array.isArray(referenceParameters.unit_queue)
    ? cloneJson(referenceParameters.unit_queue)
    : (Array.isArray(researchState.unit_queue) ? researchState.unit_queue : []);
  researchState.unit_hierarchy = Array.isArray(referenceParameters.unit_hierarchy)
    ? cloneJson(referenceParameters.unit_hierarchy)
    : (Array.isArray(researchState.unit_hierarchy) ? researchState.unit_hierarchy : []);
  researchState.rendered_sections = Array.isArray(referenceParameters.rendered_sections)
    ? cloneJson(referenceParameters.rendered_sections)
    : (Array.isArray(researchState.rendered_sections) ? researchState.rendered_sections : []);
  researchState.rendered_section_artifacts = Array.isArray(referenceParameters.rendered_section_artifacts)
    ? cloneJson(referenceParameters.rendered_section_artifacts)
    : (Array.isArray(researchState.rendered_section_artifacts) ? researchState.rendered_section_artifacts : []);
  researchState.sources = normalizeIdKeyedList(researchState.sources_by_id ?? researchState.sources);
  researchState.entities = normalizeIdKeyedList(researchState.entities_by_id ?? researchState.entities);
  researchState.research_questions = normalizeIdKeyedList(researchState.research_questions_by_id ?? researchState.research_questions);
  researchState.research_objectives = normalizeIdKeyedList(researchState.research_objectives_by_id ?? researchState.research_objectives);
  researchState.research_tasks = Array.isArray(researchState.research_tasks) ? researchState.research_tasks : [];
  researchState.topic_hint_aggregates = Array.isArray(researchState.topic_hint_aggregates) ? researchState.topic_hint_aggregates : [];
  researchState.topic_hierarchy_snapshot =
    researchState.topic_hierarchy_snapshot && typeof researchState.topic_hierarchy_snapshot === "object"
      ? researchState.topic_hierarchy_snapshot
      : { generated_turn: Number(researchState?.turn_index ?? 0), parents: [] };
  researchState.context_graph =
    researchState.context_graph && typeof researchState.context_graph === "object"
      ? researchState.context_graph
      : {};
  delete researchState.sources_by_id;
  delete researchState.entities_by_id;
  delete researchState.research_questions_by_id;
  delete researchState.research_objectives_by_id;
  return researchState;
}

function extractDisplayDomain(value = "") {
  const text = String(value ?? "").trim();
  if (!text) return null;
  try {
    return new URL(text).hostname.replace(/^www\./i, "") || null;
  } catch {
    return text.replace(/^https?:\/\//i, "").replace(/^www\./i, "").split("/")[0] || null;
  }
}

function extractResearchSourceDomain(value = "") {
  return extractDisplayDomain(value) ?? null;
}

function humanizeKey(value = "", fallback = "Unassigned") {
  const text = String(value ?? "").trim();
  if (!text) {
    return fallback;
  }
  return text.replace(/[_-]+/g, " ");
}

function formatDurationCompact(value) {
  const durationMs = Number(value ?? 0);
  if (!Number.isFinite(durationMs) || durationMs <= 0) {
    return null;
  }
  const totalSeconds = Math.max(1, Math.round(durationMs / 1000));
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (!seconds) {
    return `${minutes}m`;
  }
  return `${minutes}m ${seconds}s`;
}

function formatDurationSecondsCompact(totalSeconds = 0) {
  const normalized = Math.max(0, Number(totalSeconds ?? 0) || 0);
  if (normalized < 60) {
    return `${normalized}s`;
  }
  const minutes = Math.floor(normalized / 60);
  const seconds = normalized % 60;
  return seconds ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

function formatScopeLabel(scope = null) {
  const normalizedScope = normalizeConversationScope(scope);
  if (normalizedScope.type !== "deep_dive") {
    return "Shared corpus";
  }
  if (normalizedScope.label) {
    return normalizedScope.label;
  }
  if (normalizedScope.anchorType) {
    return `${humanizeKey(normalizedScope.anchorType, "deep dive")} focus`;
  }
  return "Deep dive";
}

function buildScopedResearchState(state = {}, scope = null) {
  const normalizedScope = normalizeConversationScope(scope);
  if (normalizedScope.type !== "deep_dive" || normalizedScope.anchorIds.length === 0) {
    return state;
  }

  const next = cloneJson(state) ?? {};
  const anchorIds = new Set(normalizedScope.anchorIds);
  let filteredClaims = toArray(next.claims);

  if (normalizedScope.anchorType === "entity") {
    filteredClaims = filteredClaims.filter((claim) =>
      toArray(claim?.entity_ids).some((entityId) => anchorIds.has(entityId)),
    );
  } else if (normalizedScope.anchorType === "claim" || normalizedScope.anchorType === "claim_cluster") {
    filteredClaims = filteredClaims.filter((claim) => anchorIds.has(claim?.id));
  } else if (normalizedScope.anchorType === "aggregate") {
    filteredClaims = filteredClaims.filter((claim) =>
      toArray(claim?.metadata?.theme_topic_hints).some((hint) => anchorIds.has(hint?.aggregate_id)),
    );
  }

  const filteredClaimIds = new Set(filteredClaims.map((claim) => claim?.id).filter(Boolean));
  const filteredEntityIds = new Set(filteredClaims.flatMap((claim) => toArray(claim?.entity_ids)));
  const filteredSourceIds = new Set(filteredClaims.flatMap((claim) => toArray(claim?.source_ids)));
  const filteredAggregateIds = new Set(
    toArray(next?.topic_hint_aggregates)
      .filter((aggregate) => toArray(aggregate?.claimIds ?? aggregate?.claim_ids).some((claimId) => filteredClaimIds.has(claimId)))
      .map((aggregate) => aggregate?.aggregateId ?? aggregate?.aggregate_id)
      .filter(Boolean),
  );

  normalizedScope.anchorIds.forEach((anchorId) => {
    if (normalizedScope.anchorType === "entity") filteredEntityIds.add(anchorId);
    if (normalizedScope.anchorType === "aggregate") filteredAggregateIds.add(anchorId);
  });

  next.claims = filteredClaims;
  next.entities = toArray(next.entities).filter((entity) => filteredEntityIds.has(entity?.id));
  next.topic_hint_aggregates = toArray(next.topic_hint_aggregates).filter((aggregate) =>
    filteredAggregateIds.has(aggregate?.aggregateId ?? aggregate?.aggregate_id) ||
    toArray(aggregate?.claimIds ?? aggregate?.claim_ids).some((claimId) => filteredClaimIds.has(claimId)),
  );
  const visibleAggregateIds = new Set(
    toArray(next.topic_hint_aggregates).map((aggregate) => aggregate?.aggregateId ?? aggregate?.aggregate_id).filter(Boolean),
  );
  next.topic_hierarchy_snapshot = {
    ...(next.topic_hierarchy_snapshot ?? {}),
    parents: dedupeHierarchyParents(
      toArray(next?.topic_hierarchy_snapshot?.parents)
      .map((parent) => {
        const originalAggregateCount = uniqueStrings(collectHierarchyNodeAggregateIds(parent)).length;
        const filteredChildren = filterHierarchyChildItems(parent?.child_items, visibleAggregateIds);
        const descendantAggregateIds = uniqueStrings(filteredChildren.flatMap((child) => collectHierarchyNodeAggregateIds(child)));
        const directAggregateIds = uniqueStrings([
          ...toArray(parent?.supporting_aggregate_ids),
          String(parent?.aggregate_id ?? parent?.aggregateId ?? "").trim(),
        ]).filter((aggregateId) => visibleAggregateIds.has(aggregateId) && !descendantAggregateIds.includes(aggregateId));
        const filteredParent = {
          ...parent,
          supporting_aggregate_ids: directAggregateIds,
          supporting_claim_ids: uniqueStrings(toArray(parent?.supporting_claim_ids)).filter((claimId) => filteredClaimIds.has(claimId)),
          child_items: filteredChildren,
        };
        const filteredAggregateCount = uniqueStrings(collectHierarchyNodeAggregateIds(filteredParent)).length;
        if (filteredAggregateCount < 2 && originalAggregateCount > 1) {
          return null;
        }
        return {
          ...filteredParent,
          why_now: buildFilteredHierarchyWhyNow(filteredParent),
        };
      })
      .filter((parent) => parent && collectHierarchyNodeAggregateIds(parent).length > 0),
    ),
  };
  next.sources = toArray(next.sources).filter((source) => filteredSourceIds.has(source?.id));
  next.research_questions = toArray(next.research_questions).filter((question) => {
    if (normalizedScope.anchorType === "entity") {
      return toArray(question?.anchor_entity_ids).some((entityId) => filteredEntityIds.has(entityId));
    }
    if (normalizedScope.anchorType === "claim" || normalizedScope.anchorType === "claim_cluster") {
      return filteredClaimIds.has(question?.parent_claim_id);
    }
    if (normalizedScope.anchorType === "aggregate") {
      return anchorIds.has(question?.parent_aggregate_id) || filteredClaimIds.has(question?.parent_claim_id);
    }
    return true;
  });
  next.research_tasks = toArray(next.research_tasks).filter((task) => {
    if (normalizedScope.anchorType === "claim" || normalizedScope.anchorType === "claim_cluster") {
      return filteredClaimIds.has(task?.claim_id);
    }
    if (normalizedScope.anchorType === "entity") {
      return filteredClaims.some((claim) => claim?.id === task?.claim_id);
    }
    if (normalizedScope.anchorType === "aggregate") {
      return filteredClaimIds.has(task?.claim_id);
    }
    return true;
  });
  return next;
}

function buildLocalContextGraph(state = {}, options = {}) {
  const allDomains = uniqueObjectsBy(
    toArray(state.sources)
      .map((source) => {
        const domain = extractResearchSourceDomain(source?.url ?? source?.domain ?? "");
        if (!domain || domain === "unknown") {
          return null;
        }
        return {
          id: `domain:${domain}`,
          domain,
          label: domain,
        };
      })
      .filter(Boolean),
    (entry) => entry.id,
  );

  const domainIdsBySourceId = new Map(
    toArray(state.sources).map((source) => {
      const domain = extractResearchSourceDomain(source?.url ?? source?.domain ?? "");
      return [source?.id, domain && domain !== "unknown" ? `domain:${domain}` : null];
    }),
  );

  const entities = toArray(state.entities).map((entity) => ({
    id: entity?.id,
    name: entity?.name ?? entity?.title ?? entity?.id,
    type: entity?.type ?? null,
    related_entities: toArray(entity?.related_entities),
    claim_ids: uniqueStrings(toArray(entity?.claim_ids)),
  })).filter((entity) => entity.id);

  const claims = toArray(state.claims).map((claim) => ({
    id: claim?.id,
    type: claim?.type ?? null,
    text: claim?.text ?? "",
    primary_section: claim?.primary_section ?? toArray(claim?.section_keys)[0] ?? null,
    entity_ids: uniqueStrings(toArray(claim?.entity_ids)),
    source_ids: uniqueStrings(toArray(claim?.source_ids)),
  })).filter((claim) => claim.id);

  const claimEntityEdges = claims.flatMap((claim) =>
    claim.entity_ids.map((entityId) => ({
      claim_id: claim.id,
      entity_id: entityId,
      relation: "mentions",
    })),
  );

  const claimDomainEdges = uniqueObjectsBy(
    claims.flatMap((claim) =>
      claim.source_ids
        .map((sourceId) => domainIdsBySourceId.get(sourceId))
        .filter(Boolean)
        .map((domainId) => ({
          claim_id: claim.id,
          domain_id: domainId,
          relation: "sourced_from",
        })),
    ),
    (entry) => `${entry.claim_id}:${entry.domain_id}:${entry.relation}`,
  );
  const connectedDomainIds = new Set(toArray(claimDomainEdges).map((entry) => entry?.domain_id).filter(Boolean));
  const domains = allDomains.filter((domain) => connectedDomainIds.has(domain?.id));

  const hierarchySnapshot = state?.topic_hierarchy_snapshot ?? { generated_turn: Number(state?.turn_index ?? 0), parents: [] };
  const showSingleClaimThemes = options?.showSingleClaimThemes === true;
  const allAggregates = toArray(state?.topic_hint_aggregates)
    .map((aggregate) => ({
      id: aggregate?.aggregateId ?? aggregate?.aggregate_id,
      label: aggregate?.label ?? aggregate?.aggregateId ?? aggregate?.aggregate_id,
      scope: aggregate?.scope ?? null,
      claim_count: aggregate?.claimCount ?? aggregate?.claim_count ?? null,
      agenda_score: aggregate?.agendaScore ?? aggregate?.agenda_score ?? null,
      facet_family: aggregate?.facetFamily ?? aggregate?.facet_family ?? null,
      lens: aggregate?.lens ?? null,
      specificity: aggregate?.specificity ?? null,
      pattern_family_key: aggregate?.patternFamilyKey ?? aggregate?.pattern_family_key ?? null,
      claim_ids: uniqueStrings(toArray(aggregate?.claimIds ?? aggregate?.claim_ids)),
      entity_ids: uniqueStrings(toArray(aggregate?.entityIds ?? aggregate?.entity_ids)),
    }))
    .filter((aggregate) => aggregate.id);
  const aggregates = allAggregates.filter((aggregate) => {
    const claimCount = uniqueStrings(toArray(aggregate?.claim_ids)).length;
    return showSingleClaimThemes || claimCount >= 2;
  });
  const visibleAggregateIds = new Set(aggregates.map((aggregate) => aggregate.id));
  // Claims whose only fine-grained group was filtered out (singleton aggregates) attach
  // to their theme parent instead of dropping out of the hierarchy entirely.
  const fullAggregateClaimIds = new Map(allAggregates.map((aggregate) => [aggregate.id, aggregate.claim_ids]));
  const knownClaimIds = new Set(claims.map((claim) => claim.id));
  const claimIdsWithVisibleAggregate = new Set(aggregates.flatMap((aggregate) => aggregate.claim_ids));
  const claimParentEdges = uniqueObjectsBy(
    toArray(hierarchySnapshot?.parents).flatMap((parent) => {
      const parentId = String(parent?.parent_id ?? "").trim();
      if (!parentId) return [];
      return uniqueStrings(collectHierarchyNodeAggregateIds(parent))
        .filter((aggregateId) => !visibleAggregateIds.has(aggregateId))
        .flatMap((aggregateId) => fullAggregateClaimIds.get(aggregateId) ?? [])
        .filter((claimId) => knownClaimIds.has(claimId) && !claimIdsWithVisibleAggregate.has(claimId))
        .map((claimId) => ({ claim_id: claimId, parent_id: parentId, relation: "claim_supports_theme" }));
    }),
    (entry) => `${entry.claim_id}:${entry.parent_id}`,
  );
  const absorbedParentIds = new Set(claimParentEdges.map((edge) => edge.parent_id));
  const hierarchyGraph = buildHierarchyGraphStructures(hierarchySnapshot?.parents, visibleAggregateIds, absorbedParentIds);
  const claimAggregateEdges = uniqueObjectsBy(
    [
      ...aggregates.flatMap((aggregate) =>
        uniqueStrings(toArray(aggregate?.claim_ids)).map((claimId) => ({
          claim_id: claimId,
          aggregate_id: aggregate.id,
          relation: "claim_supports_aggregate",
        })),
      ),
      ...claims.flatMap((claim) =>
        uniqueStrings(toArray(claim?.aggregateIds))
          .filter((aggregateId) => visibleAggregateIds.has(aggregateId))
          .map((aggregateId) => ({
            claim_id: claim.id,
            aggregate_id: aggregateId,
            relation: "claim_supports_aggregate",
          })),
      ),
    ],
    (entry) => `${entry.claim_id}:${entry.aggregate_id}:${entry.relation}`,
  );
  const entityRelationEdges = uniqueObjectsBy(
    entities.flatMap((entity) =>
      toArray(entity.related_entities)
        .map((related) => {
          if (!related) {
            return null;
          }
          if (typeof related === "string") {
            return {
              from_entity_id: entity.id,
              to_entity_id: related,
              relation: "related_to",
            };
          }
          const targetId = related?.entity_id ?? related?.id ?? related?.target_entity_id ?? null;
          if (!targetId) {
            return null;
          }
          return {
            from_entity_id: entity.id,
            to_entity_id: targetId,
            relation: related?.relation ?? related?.type ?? "related_to",
          };
        })
        .filter(Boolean),
    ),
    (entry) => `${entry.from_entity_id}:${entry.to_entity_id}:${entry.relation}`,
  );

  return {
    domains,
    entities,
    aggregates,
    aggregate_parents: hierarchyGraph.aggregateParentNodes,
    aggregate_subthemes: hierarchyGraph.aggregateSubthemeNodes,
    claims: claims.map(({ entity_ids, source_ids, ...claim }) => claim),
    claim_domain_edges: claimDomainEdges,
    claim_entity_edges: claimEntityEdges,
    claim_aggregate_edges: claimAggregateEdges,
    claim_parent_edges: claimParentEdges,
    hierarchy_node_edges: hierarchyGraph.hierarchyNodeEdges,
    aggregate_hierarchy_edges: hierarchyGraph.aggregateHierarchyEdges,
    entity_relation_edges: entityRelationEdges,
  };
}

function buildEvidenceSnapshot(state = {}, scope = null) {
  const scopedState = buildScopedResearchState(state, scope);
  const claims = toArray(scopedState.claims);
  const sources = toArray(scopedState.sources);
  const aggregates = toArray(scopedState.topic_hint_aggregates);
  const hierarchy = cloneJson(scopedState.topic_hierarchy_snapshot) ?? { generated_turn: Number(scopedState?.turn_index ?? 0), parents: [] };
  hierarchy.parents = dedupeHierarchyParents(hierarchy.parents);
  const entityLabelMap = new Map(
    toArray(scopedState.entities)
      .filter((entity) => entity?.id)
      .map((entity) => [entity.id, entity.name ?? entity.id]),
  );
  const sourceMap = new Map(
    sources
      .filter((source) => source?.id)
      .map((source) => [source.id, source]),
  );
  const sectionCounts = {};
  const domainCounts = {};
  const allClaims = claims.map((claim) => {
    const sectionKey = String(claim?.primary_section ?? toArray(claim?.section_keys)[0] ?? "unassigned").trim();
    return {
      id: claim?.id ?? null,
      text: claim?.text ?? "",
      type: claim?.type ?? "claim",
      primarySection: sectionKey,
      date: claim?.date ?? null,
      relevance: claim?.relevance ?? null,
      confidence: claim?.confidence ?? null,
      sourceIds: uniqueStrings(toArray(claim?.source_ids)),
      entityIds: uniqueStrings(toArray(claim?.entity_ids)),
      entityLabels: uniqueStrings(toArray(claim?.entity_ids).map((entityId) => entityLabelMap.get(entityId) ?? entityId)),
      sourceDomains: uniqueStrings(
        toArray(claim?.source_ids)
          .map((sourceId) => sourceMap.get(sourceId))
          .map((source) => extractResearchSourceDomain(source?.url ?? source?.title ?? source?.id)),
      ),
      aggregateIds: uniqueStrings(toArray(claim?.metadata?.theme_topic_hints).map((hint) => hint?.aggregate_id)),
    };
  });

  const groups = Object.entries(
    claims.reduce((accumulator, claim, index) => {
      const key = String(claim?.type ?? "claim").trim() || "claim";
      const sectionKey = String(claim?.primary_section ?? toArray(claim?.section_keys)[0] ?? "unassigned").trim();
      sectionCounts[sectionKey] = (sectionCounts[sectionKey] ?? 0) + 1;
      uniqueStrings(
        toArray(claim?.source_ids)
          .map((sourceId) => sourceMap.get(sourceId))
          .map((source) => extractResearchSourceDomain(source?.url ?? source?.title ?? source?.id)),
      ).forEach((domain) => {
        domainCounts[domain] = (domainCounts[domain] ?? 0) + 1;
      });
      accumulator[key] ||= { key, count: 0, claims: [] };
      accumulator[key].count += 1;
      accumulator[key].claims.push(allClaims[index]);
      return accumulator;
    }, {}),
  )
    .map(([, group]) => ({
      ...group,
      claims: group.claims.slice(0, 25),
    }))
    .sort((left, right) => right.count - left.count || left.key.localeCompare(right.key));

  return {
    scope: normalizeConversationScope(scope),
    counts: {
      claims: claims.length,
      sources: sources.length,
      groups: groups.length,
      aggregates: aggregates.length,
    },
    aggregates: aggregates.map((aggregate) => ({
      aggregateId: aggregate?.aggregateId ?? aggregate?.aggregate_id ?? null,
      label: aggregate?.label ?? "",
      scope: aggregate?.scope ?? null,
      searchPhrase: aggregate?.searchPhrase ?? aggregate?.search_phrase ?? null,
      searchTerms: uniqueStrings(toArray(aggregate?.searchTerms ?? aggregate?.search_terms)),
      claimCount: Number(aggregate?.claimCount ?? aggregate?.claim_count ?? 0),
      turnsSeen: toArray(aggregate?.turnsSeen ?? aggregate?.turns_seen),
      firstSeenTurn: Number(aggregate?.firstSeenTurn ?? aggregate?.first_seen_turn ?? 0),
      lastSeenTurn: Number(aggregate?.lastSeenTurn ?? aggregate?.last_seen_turn ?? 0),
      sectionKeys: uniqueStrings(toArray(aggregate?.sectionKeys ?? aggregate?.section_keys)),
      entityIds: uniqueStrings(toArray(aggregate?.entityIds ?? aggregate?.entity_ids)),
      entityLabels: uniqueStrings(toArray(aggregate?.entityIds ?? aggregate?.entity_ids).map((entityId) => entityLabelMap.get(entityId) ?? entityId)),
      companyIds: uniqueStrings(toArray(aggregate?.companyIds ?? aggregate?.company_ids)),
      companyLabels: uniqueStrings(toArray(aggregate?.companyIds ?? aggregate?.company_ids).map((entityId) => entityLabelMap.get(entityId) ?? entityId)),
      recurrenceScore: Number(aggregate?.recurrenceScore ?? aggregate?.recurrence_score ?? 0),
      supportBreadthScore: Number(aggregate?.supportBreadthScore ?? aggregate?.support_breadth_score ?? 0),
      recentGrowthScore: Number(aggregate?.recentGrowthScore ?? aggregate?.recent_growth_score ?? 0),
      agendaScore: Number(aggregate?.agendaScore ?? aggregate?.agenda_score ?? 0),
      userPolicy: cloneJson(aggregate?.userPolicy ?? aggregate?.user_policy) ?? {},
      claimIds: uniqueStrings(toArray(aggregate?.claimIds ?? aggregate?.claim_ids)),
    })),
    hierarchy,
    sections: Object.entries(sectionCounts)
      .map(([key, count]) => ({ key, count }))
      .sort((left, right) => right.count - left.count || left.key.localeCompare(right.key))
      .slice(0, 12),
    domains: Object.entries(domainCounts)
      .map(([domain, count]) => ({ domain, count }))
      .sort((left, right) => right.count - left.count || left.domain.localeCompare(right.domain))
      .slice(0, 16),
    allClaims,
    groups,
    sources: sources.slice(0, 100).map((source) => ({
      id: source?.id ?? null,
      kind: source?.kind ?? null,
      title: source?.title ?? source?.url ?? source?.id ?? "Source",
      url: source?.url ?? null,
    })),
  };
}

function getHierarchyParentCount(snapshot = null) {
  return toArray(snapshot?.parents).length;
}

function areScopesEquivalent(leftScope = null, rightScope = null) {
  const left = normalizeConversationScope(leftScope);
  const right = normalizeConversationScope(rightScope);
  return (
    left.type === right.type &&
    left.anchorType === right.anchorType &&
    left.anchorIds.join("|") === right.anchorIds.join("|")
  );
}

function shouldRetainPreviousHierarchyDuringLiveRun(previousHierarchy = null, nextHierarchy = null, { isRunning = false } = {}) {
  if (!isRunning) return false;
  const previousParentCount = getHierarchyParentCount(previousHierarchy);
  const nextParentCount = getHierarchyParentCount(nextHierarchy);
  if (previousParentCount < 3) return false;
  if (nextParentCount === 0) return true;
  if (nextParentCount < previousParentCount) {
    if (nextParentCount <= 1) return true;
    // Keep the leaderboard stable during live updates instead of briefly shrinking.
    if (previousParentCount >= 4) return true;
  }
  return false;
}

function mergeStableEvidenceSnapshot(previousEvidence = null, nextEvidence = null, { isRunning = false } = {}) {
  if (!nextEvidence) return previousEvidence;
  if (!previousEvidence) return nextEvidence;
  if (!areScopesEquivalent(previousEvidence?.scope, nextEvidence?.scope)) {
    return nextEvidence;
  }
  if (!shouldRetainPreviousHierarchyDuringLiveRun(previousEvidence?.hierarchy, nextEvidence?.hierarchy, { isRunning })) {
    return nextEvidence;
  }
  return {
    ...nextEvidence,
    hierarchy: cloneJson(previousEvidence?.hierarchy) ?? previousEvidence?.hierarchy ?? nextEvidence?.hierarchy,
  };
}

function buildGraphSnapshot(state = {}, scope = null, options = {}) {
  const scopedState = buildScopedResearchState(state, scope);
  const localContextGraph = buildLocalContextGraph(scopedState, options);
  const contextGraph = localContextGraph;
  const entityNodes = toArray(contextGraph.entities).map((entity) => ({
    id: entity.id,
    kind: "entity",
    label: entity.name ?? entity.id,
    entityType: entity.type ?? null,
    claimCount: uniqueStrings(toArray(entity.claim_ids)).length,
  }));
  const claimNodes = toArray(contextGraph.claims).map((claim) => ({
    id: claim.id,
    kind: "claim",
    label: truncateText(claim.text ?? claim.id, 90),
    claimType: claim.type ?? null,
    primarySection: claim.primary_section ?? null,
  }));
  const domainNodes = toArray(contextGraph.domains).map((domain) => ({
    id: domain.id,
    kind: "domain",
    label: domain.label ?? domain.domain ?? domain.id,
  }));
  const aggregateNodes = toArray(contextGraph.aggregates).map((aggregate) => ({
    id: aggregate.id,
    kind: "aggregate",
    label: aggregate.label ?? aggregate.id,
    agendaScore: aggregate.agenda_score ?? aggregate.agendaScore ?? null,
    claimCount: aggregate.claim_count ?? aggregate.claimCount ?? null,
  }));
  const aggregateParentNodes = toArray(contextGraph.aggregate_parents).map((parent) => ({
    id: parent.id,
    kind: "aggregate_parent",
    label: parent.label ?? parent.id,
    rank: parent.rank ?? null,
  }));
  const aggregateSubthemeNodes = toArray(contextGraph.aggregate_subthemes).map((node) => ({
    id: node.id,
    kind: "aggregate_subtheme",
    label: node.label ?? node.id,
    whyNow: node.why_now ?? null,
    depth: node.depth ?? null,
    claimCount: node.claim_count ?? null,
    agendaScore: node.agenda_score ?? null,
  }));

  return {
    scope: normalizeConversationScope(scope),
    nodes: [...entityNodes, ...domainNodes, ...claimNodes, ...aggregateNodes, ...aggregateParentNodes, ...aggregateSubthemeNodes],
    edges: [
      ...toArray(contextGraph.claim_entity_edges).map((edge) => ({
        id: `claim_entity:${edge.claim_id}:${edge.entity_id}`,
        source: edge.claim_id,
        target: edge.entity_id,
        relation: edge.relation ?? "mentions",
        kind: "claim_entity",
      })),
      ...toArray(contextGraph.entity_relation_edges).map((edge) => ({
        id: `entity_relation:${edge.from_entity_id}:${edge.to_entity_id}:${edge.relation ?? "related_to"}`,
        source: edge.from_entity_id,
        target: edge.to_entity_id,
        relation: edge.relation ?? "related_to",
        kind: "entity_relation",
      })),
      ...toArray(contextGraph.claim_domain_edges).map((edge) => ({
        id: `claim_domain:${edge.claim_id}:${edge.domain_id}`,
        source: edge.claim_id,
        target: edge.domain_id,
        relation: edge.relation ?? "sourced_from",
        kind: "claim_domain",
      })),
      ...toArray(contextGraph.claim_aggregate_edges).map((edge) => ({
        id: `claim_aggregate:${edge.claim_id}:${edge.aggregate_id}`,
        source: edge.claim_id,
        target: edge.aggregate_id,
        relation: edge.relation ?? "claim_supports_aggregate",
        kind: "claim_aggregate",
      })),
      ...toArray(contextGraph.claim_parent_edges).map((edge) => ({
        id: `claim_parent:${edge.claim_id}:${edge.parent_id}`,
        source: edge.claim_id,
        target: edge.parent_id,
        relation: edge.relation ?? "claim_supports_theme",
        kind: "claim_parent",
      })),
      ...toArray(contextGraph.hierarchy_node_edges).map((edge) => ({
        id: `hierarchy_node:${edge.parent_node_id}:${edge.child_node_id}`,
        source: edge.child_node_id,
        target: edge.parent_node_id,
        relation: edge.relation ?? "subtheme_belongs_to_theme",
        kind: "hierarchy_node",
      })),
      ...toArray(contextGraph.aggregate_hierarchy_edges).map((edge) => ({
        id: `aggregate_hierarchy:${edge.hierarchy_node_id}:${edge.aggregate_id}`,
        source: edge.aggregate_id,
        target: edge.hierarchy_node_id,
        relation: edge.relation ?? "aggregate_belongs_to_parent_theme",
        kind: "aggregate_hierarchy",
      })),
    ].filter((edge) => edge.source && edge.target),
  };
}

function hasGraphSnapshotData(graph = null) {
  if (!graph || typeof graph !== "object") {
    return false;
  }
  if (Array.isArray(graph.nodes) && graph.nodes.length > 0) {
    return true;
  }
  if (graph.context_graph && typeof graph.context_graph === "object") {
    const contextGraph = graph.context_graph;
    return (
      toArray(contextGraph.entities).length > 0 ||
      toArray(contextGraph.domains).length > 0 ||
      toArray(contextGraph.claims).length > 0 ||
      toArray(contextGraph.aggregates).length > 0 ||
      toArray(contextGraph.aggregate_parents).length > 0 ||
      toArray(contextGraph.aggregate_subthemes).length > 0
    );
  }
  return false;
}

function filterGraphSnapshotByEntityClaimCount(graph = null, minimumClaimCount = 1) {
  if (!graph || !Array.isArray(graph.nodes) || !Array.isArray(graph.edges) || minimumClaimCount <= 1) {
    return graph;
  }

  const allowedNodeIds = new Set(
    graph.nodes
      .filter((node) => node?.kind !== "entity" || Number(node?.claimCount ?? 0) >= minimumClaimCount)
      .map((node) => node?.id)
      .filter(Boolean),
  );

  const edges = graph.edges.filter(
    (edge) => allowedNodeIds.has(edge?.source) && allowedNodeIds.has(edge?.target),
  );
  const connectedNodeIds = new Set(edges.flatMap((edge) => [edge.source, edge.target]).filter(Boolean));
  const nodes = graph.nodes.filter((node) => {
    if (!allowedNodeIds.has(node?.id)) {
      return false;
    }
    if (node?.kind === "entity") {
      return true;
    }
    return connectedNodeIds.has(node?.id);
  });

  return {
    ...graph,
    nodes,
    edges,
  };
}

function normalizeFacetToken(value = "") {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function sentenceLabel(value = "") {
  const text = String(value ?? "").replace(/_/g, " ").replace(/\s+/g, " ").trim();
  return text ? text.slice(0, 1).toUpperCase() + text.slice(1) : "Unknown";
}

function isFacetEntityAnalyticallyStrong(entity = null, options = {}) {
  if (!entity || typeof entity !== "object") return false;
  if (options?.showWeakEntities === true) return true;
  const type = String(entity?.type ?? "").trim().toLowerCase();
  const significanceScore = Number(entity?.significance_score ?? 0);
  const significanceReasons = new Set(toArray(entity?.significance_reasons).map((value) => String(value ?? "").trim().toLowerCase()));
  if (significanceReasons.has("subject") || significanceReasons.has("domain") || significanceReasons.has("type_locked")) {
    return true;
  }
  if (significanceScore >= 6) {
    return true;
  }
  if (["company", "organization", "brand", "product", "initiative", "program", "person"].includes(type)) {
    return true;
  }
  return false;
}

function inferFacetFamilyFromHintOrAggregate(hint = {}, aggregate = null) {
  return (
    normalizeFacetToken(hint?.facet_family) ||
    normalizeFacetToken(aggregate?.facet_family) ||
    normalizeFacetToken(aggregate?.pattern_family_key) ||
    normalizeFacetToken(aggregate?.scope ?? hint?.scope) ||
    "theme"
  );
}

function getFacetPresetOrder(preset = "overview", sectionKey = "") {
  if (preset === "hierarchy") return ["hierarchy_parent", "aggregate", "entity"];
  if (preset === "entity") return ["entity", "hierarchy_parent", "aggregate"];
  if (preset === "aggregate" || preset === "theme") return ["aggregate", "hierarchy_parent", "entity"];
  if (preset === "source") return ["source_domain", "hierarchy_parent", "aggregate"];
  const key = String(sectionKey ?? "").trim().toLowerCase();
  if (key === "competitor_landscape") return ["entity", "hierarchy_parent", "aggregate"];
  if (key === "financial_performance") return ["hierarchy_parent", "aggregate", "entity"];
  if (key === "recent_announcements") return ["hierarchy_parent", "aggregate", "entity"];
  return ["hierarchy_parent", "aggregate", "entity"];
}

function getDefaultPrimaryAxisForPreset(preset = "overview") {
  if (preset === "hierarchy") return "hierarchy_parent";
  if (preset === "entity") return "entity";
  if (preset === "aggregate" || preset === "theme") return "aggregate";
  if (preset === "source") return "source_domain";
  return "hierarchy_parent";
}

function getDefaultSecondaryAxisForPreset(preset = "overview", primaryAxis = "hierarchy_parent") {
  if (preset === "hierarchy") return primaryAxis === "hierarchy_parent" ? "aggregate" : "hierarchy_parent";
  if (preset === "entity") return primaryAxis === "entity" ? "hierarchy_parent" : "entity";
  if (preset === "aggregate" || preset === "theme") return primaryAxis === "aggregate" ? "hierarchy_parent" : "aggregate";
  if (preset === "source") return primaryAxis === "source_domain" ? "hierarchy_parent" : "source_domain";
  return primaryAxis === "hierarchy_parent" ? "aggregate" : "hierarchy_parent";
}

function sanitizeFacetOrder(order = [], preset = "overview") {
  const validAxisKeys = new Set(FACET_AXIS_OPTIONS.map((axis) => axis.key));
  return uniqueStrings(toArray(order)).filter((axis) => axis && validAxisKeys.has(axis));
}

function buildDateBucket(value = "") {
  const match = String(value ?? "").trim().match(/^(\d{4})(?:-(\d{2}))?/);
  if (!match) return { id: "undated", label: "Undated" };
  const id = match[2] ? `${match[1]}-${match[2]}` : match[1];
  return { id, label: id };
}

function buildFacetBucketKey(dimension, value = {}) {
  const rawId = String(value?.id ?? "unknown");
  const rawLabel = String(value?.label ?? rawId).trim();
  if (dimension !== "aggregate") {
    return rawId || "unknown";
  }
  return rawId || normalizeHierarchyLabel(rawLabel) || "unknown";
}

function buildFacetTreeSnapshot(state = {}, options = {}) {
  const preset = options?.preset ?? "overview";
  const showSingleClaimThemes = options?.showSingleClaimThemes === true;
  const showWeakEntities = options?.showWeakEntities === true;
  const scopedState = buildScopedResearchState(state, options?.scope ?? null);
  const claims = toArray(scopedState?.claims);
  const entityById = new Map(toArray(scopedState?.entities).filter((entity) => entity?.id).map((entity) => [entity.id, entity]));
  const sourceById = new Map(toArray(scopedState?.sources).filter((source) => source?.id).map((source) => [source.id, source]));
  const aggregateById = new Map(toArray(scopedState?.topic_hint_aggregates).filter((aggregate) => aggregate?.aggregate_id ?? aggregate?.aggregateId).map((aggregate) => [aggregate.aggregate_id ?? aggregate.aggregateId, aggregate]));
  const aggregateIdsByClaimId = new Map();
  aggregateById.forEach((aggregate, aggregateId) => {
    uniqueStrings(toArray(aggregate?.claim_ids ?? aggregate?.claimIds)).forEach((claimId) => {
      if (!aggregateIdsByClaimId.has(claimId)) {
        aggregateIdsByClaimId.set(claimId, []);
      }
      aggregateIdsByClaimId.get(claimId).push(aggregateId);
    });
  });
  const hierarchyFacetIndex = buildHierarchyFacetIndex(scopedState?.topic_hierarchy_snapshot?.parents);
  const anchorEntityIds = new Set(uniqueStrings([
    state?.subject?.entity_id,
    state?.target_entity_id,
    state?.anchor_entity_id,
    ...toArray(state?.anchor_entity_ids),
    ...toArray(state?.domain_frame?.anchor_entity_ids),
  ]));
  const sectionKey = String(options?.sectionKey ?? claims[0]?.primary_section ?? toArray(claims[0]?.section_keys)[0] ?? "").trim();
  const explicitOrder = uniqueStrings(toArray(options?.order)).length > 0;
  const order = sanitizeFacetOrder(
    explicitOrder ? options?.order : getFacetPresetOrder(preset, sectionKey),
    preset,
  );
  const rows = claims.flatMap((claim) => {
    const hints = toArray(claim?.metadata?.theme_topic_hints);
    const aggregateRefs = uniqueStrings([
      ...hints.map((hint) => hint?.aggregate_id),
      ...toArray(aggregateIdsByClaimId.get(claim?.id)),
    ]);
    const sourceId = uniqueStrings(toArray(claim?.source_ids))[0] ?? null;
    const source = sourceById.get(sourceId);
    const domain = extractResearchSourceDomain(source?.url ?? source?.title ?? sourceId ?? "");
    const claimEntityIds = uniqueStrings(toArray(claim?.entity_ids));
    const analyticalEntityIds = claimEntityIds.filter((entityId) =>
      isFacetEntityAnalyticallyStrong(entityById.get(entityId), { showWeakEntities }),
    );
    const rowEntityIds =
      order[0] === "anchor_entity"
        ? analyticalEntityIds.filter((entityId) => anchorEntityIds.has(entityId))
        : analyticalEntityIds;
    const fallbackEntityIds =
      rowEntityIds.length > 0
        ? rowEntityIds
        : claimEntityIds.filter((entityId) => anchorEntityIds.has(entityId));
    const visibleAggregateRefs = aggregateRefs.filter((aggregateId) => {
      const aggregate = aggregateById.get(aggregateId);
      const count = uniqueStrings(toArray(aggregate?.claim_ids ?? aggregate?.claimIds)).length;
      return showSingleClaimThemes || !aggregate || count >= 2 || aggregate?.user_policy?.followed === true || aggregate?.userPolicy?.followed === true || aggregate?.user_policy?.upvoted === true || aggregate?.userPolicy?.upvoted === true;
    });
    const aggregateRows = visibleAggregateRefs.length > 0 ? visibleAggregateRefs : [`claim_theme:${claim?.id}`];
    return aggregateRows.flatMap((aggregateId) => {
      const hint = hints.find((entry) => entry?.aggregate_id === aggregateId) ?? {};
      const aggregate = aggregateById.get(aggregateId) ?? null;
      const hierarchyPath = hierarchyFacetIndex.get(aggregateId) ?? null;
      const facetFamily = inferFacetFamilyFromHintOrAggregate(hint, aggregate);
      const lens = normalizeFacetToken(hint?.lens) || normalizeFacetToken(aggregate?.lens) || facetFamily;
      return (fallbackEntityIds.length ? fallbackEntityIds : [claimEntityIds[0] ?? "unknown_entity"]).map((entityId) => {
        const entity = entityById.get(entityId);
        return {
          claim,
          aggregate,
          values: {
            entity: { id: entityId, label: entity?.name ?? entityId },
            anchor_entity: { id: entityId, label: entity?.name ?? entityId },
            facet_family: { id: facetFamily, label: sentenceLabel(facetFamily) },
            lens: { id: lens, label: sentenceLabel(lens) },
            hierarchy_parent: hierarchyPath?.parent ?? { id: "_unassigned_", label: "Unassigned" },
            hierarchy_subtheme: hierarchyPath?.subtheme ?? { id: "_unassigned_", label: "Unassigned" },
            aggregate: { id: aggregateId, label: aggregate?.label ?? hint?.label ?? "Unassigned" },
            date_bucket: buildDateBucket(claim?.date),
            source_domain: { id: domain || "_unassigned_", label: domain || "Unassigned" },
          },
        };
      });
    });
  });
  const buildLevel = (levelRows = [], depth = 0, path = "facet") => {
    const dimension = order[depth];
    if (!dimension) return [];
    const buckets = new Map();
    levelRows.forEach((row) => {
      const value = row.values[dimension] ?? { id: "unknown", label: "Unknown" };
      const key = buildFacetBucketKey(dimension, value);
      const bucket = buckets.get(key) ?? { value, rows: [] };
      bucket.rows.push(row);
      buckets.set(key, bucket);
    });
    return [...buckets.entries()].map(([key, bucket]) => {
      const claimIds = uniqueStrings(bucket.rows.map((row) => row.claim?.id));
      const aggregateIds = uniqueStrings(
        bucket.rows
          .map((row) => row.aggregate?.aggregate_id ?? row.aggregate?.aggregateId ?? row.values?.aggregate?.id)
          .filter(Boolean),
      );
      const entityIds = uniqueStrings(
        bucket.rows.flatMap((row) =>
          toArray(row.claim?.entity_ids).filter((entityId) =>
            isFacetEntityAnalyticallyStrong(entityById.get(entityId), { showWeakEntities }),
          ),
        ),
      );
      return {
        id: `${path}:${dimension}:${key}`,
        label: bucket.value.label ?? key,
        dimension,
        value: key,
        claimIds,
        aggregateIds,
        entityIds,
        children: buildLevel(bucket.rows, depth + 1, `${path}:${dimension}:${key}`),
      };
    }).sort((left, right) => right.claimIds.length - left.claimIds.length || String(left.label).localeCompare(String(right.label)));
  };
  const root = {
    id: "facet:root",
    label: preset === "overview" ? "Section facets" : GRAPH_FACET_PRESETS.find((entry) => entry.key === preset)?.label ?? "Facets",
    dimension: "root",
    value: "root",
    claimIds: uniqueStrings(rows.map((row) => row.claim?.id)),
    aggregateIds: uniqueStrings(
      rows
        .map((row) => row.aggregate?.aggregate_id ?? row.aggregate?.aggregateId ?? row.values?.aggregate?.id)
        .filter(Boolean),
    ),
    entityIds: uniqueStrings(
      rows.flatMap((row) =>
        toArray(row.claim?.entity_ids).filter((entityId) =>
          isFacetEntityAnalyticallyStrong(entityById.get(entityId), { showWeakEntities }),
        ),
      ),
    ),
    children: buildLevel(rows),
  };
  return { preset, groupBy: order, root };
}

function buildFacetGraphSnapshot(state = {}, options = {}) {
  const scopedState = buildScopedResearchState(state, options?.scope ?? null);
  const aggregateById = new Map(
    toArray(scopedState?.topic_hint_aggregates)
      .filter((aggregate) => aggregate?.aggregate_id ?? aggregate?.aggregateId)
      .map((aggregate) => [aggregate.aggregate_id ?? aggregate.aggregateId, aggregate]),
  );
  const claimById = new Map(
    toArray(scopedState?.claims)
      .filter((claim) => claim?.id)
      .map((claim) => [claim.id, claim]),
  );
  const entityById = new Map(
    toArray(scopedState?.entities)
      .filter((entity) => entity?.id)
      .map((entity) => [entity.id, entity]),
  );

  if (options?.preset === "hierarchy") {
    const showSingleClaimThemes = options?.showSingleClaimThemes === true;
    const aggregateNodes = toArray(scopedState?.topic_hint_aggregates)
      .map((aggregate) => {
        const aggregateId = aggregate?.aggregate_id ?? aggregate?.aggregateId;
        const claimIds = uniqueStrings(toArray(aggregate?.claim_ids ?? aggregate?.claimIds));
        if (!aggregateId || (!showSingleClaimThemes && claimIds.length < 2)) return null;
        return {
          id: aggregateId,
          kind: "aggregate",
          label: aggregate?.label ?? aggregateId,
          agendaScore: aggregate?.agenda_score ?? aggregate?.agendaScore ?? null,
          claimCount: aggregate?.claim_count ?? aggregate?.claimCount ?? claimIds.length,
          claimIds,
        };
      })
      .filter(Boolean);
    const visibleAggregateIds = new Set(aggregateNodes.map((aggregate) => aggregate.id));
    const hierarchyGraph = buildHierarchyGraphStructures(
      scopedState?.topic_hierarchy_snapshot?.parents,
      visibleAggregateIds,
    );
    const toHierarchyGraphNodeId = (nodeId) => `hierarchy:${String(nodeId ?? "").trim()}`;
    const parentNodes = toArray(hierarchyGraph.aggregateParentNodes).map((node) => ({
      ...node,
      id: toHierarchyGraphNodeId(node?.id),
      rawId: node?.id,
      kind: "aggregate_parent",
      label: node?.label ?? node?.id,
    }));
    const subthemeNodes = toArray(hierarchyGraph.aggregateSubthemeNodes).map((node) => ({
      ...node,
      id: toHierarchyGraphNodeId(node?.id),
      rawId: node?.id,
      kind: "aggregate_subtheme",
      label: node?.label ?? node?.id,
    }));
    const hierarchyEdges = toArray(hierarchyGraph.hierarchyNodeEdges).map((edge) => ({
      id: `hierarchy_node:${edge.parent_node_id}:${edge.child_node_id}`,
      source: toHierarchyGraphNodeId(edge.child_node_id),
      target: toHierarchyGraphNodeId(edge.parent_node_id),
      relation: edge.relation,
      kind: "hierarchy_node",
    }));
    const aggregateHierarchyEdges = toArray(hierarchyGraph.aggregateHierarchyEdges).map((edge) => ({
      id: `aggregate_hierarchy:${edge.hierarchy_node_id}:${edge.aggregate_id}`,
      source: edge.aggregate_id,
      target: toHierarchyGraphNodeId(edge.hierarchy_node_id),
      relation: edge.relation,
      kind: "aggregate_hierarchy",
    }));
    const claimIdsToAttach = new Set();
    aggregateNodes.forEach((aggregate) => {
      uniqueStrings(toArray(aggregate?.claimIds)).forEach((claimId) => claimIdsToAttach.add(claimId));
    });
    const claimNodes = [...claimIdsToAttach]
      .map((claimId) => {
        const claim = claimById.get(claimId);
        return claim
          ? {
              id: claimId,
              kind: "claim",
              label: truncateText(claim?.text ?? claimId, 110),
              claimType: claim?.type ?? null,
              primarySection: claim?.primary_section ?? null,
            }
          : null;
      })
      .filter(Boolean);
    const aggregateClaimEdges = aggregateNodes.flatMap((aggregate) =>
      uniqueStrings(toArray(aggregate?.claimIds))
        .filter((claimId) => claimIdsToAttach.has(claimId))
        .map((claimId) => ({
          id: `claim_aggregate:${claimId}:${aggregate.id}`,
          source: claimId,
          target: aggregate.id,
          relation: "claim_supports_aggregate",
          kind: "claim_aggregate",
        })),
    );
    return {
      scope: normalizeConversationScope(options?.scope ?? null),
      nodes: [...parentNodes, ...subthemeNodes, ...aggregateNodes, ...claimNodes],
      edges: [...hierarchyEdges, ...aggregateHierarchyEdges, ...aggregateClaimEdges],
    };
  }

  const snapshot = buildFacetTreeSnapshot(state, options);

  const facetNodes = [];
  const facetEdges = [];
  const aggregateIdsToAttach = new Set();
  const claimIdsToAttach = new Set();
  const entityIdsToAttach = new Set();

  const toFacetGraphNodeId = (nodeId) => `facet:${String(nodeId ?? "").trim()}`;
  const visit = (node, depth = 0, parentId = null) => {
    const nodeId = String(node?.id ?? "").trim();
    if (!nodeId) return;
    const graphNodeId = toFacetGraphNodeId(nodeId);
    if (depth > 0) {
      facetNodes.push({
        id: graphNodeId,
        kind: depth === 1 ? "aggregate_parent" : "aggregate_subtheme",
        label: node?.label ?? nodeId,
        facetId: nodeId,
        depth: depth - 1,
        claimCount: Number(toArray(node?.claimIds).length ?? toArray(node?.claim_ids).length ?? 0),
      });
      if (parentId) {
        facetEdges.push({
          id: `facet_hierarchy:${parentId}:${graphNodeId}`,
          source: graphNodeId,
          target: parentId,
          relation: "facet_child_of",
          kind: "hierarchy_node",
        });
      }
    }
    const children = toArray(node?.children);
    if (children.length > 0) {
      children.forEach((child) => visit(child, depth + 1, depth > 0 ? graphNodeId : parentId));
      return;
    }
    const leafParentId = depth > 0 ? graphNodeId : parentId;
    toArray(node?.aggregateIds ?? node?.aggregate_ids).forEach((aggregateId) => {
      if (!aggregateId) return;
      aggregateIdsToAttach.add(aggregateId);
      if (leafParentId) {
        facetEdges.push({
          id: `facet_aggregate:${leafParentId}:${aggregateId}`,
          source: aggregateId,
          target: leafParentId,
          relation: "aggregate_belongs_to_parent_theme",
          kind: "aggregate_hierarchy",
        });
      }
    });
    toArray(node?.claimIds ?? node?.claim_ids).forEach((claimId) => claimIdsToAttach.add(claimId));
    toArray(node?.entityIds ?? node?.entity_ids).forEach((entityId) => entityIdsToAttach.add(entityId));
  };

  visit(snapshot?.root, 0, null);

  const aggregateNodes = [...aggregateIdsToAttach]
    .map((aggregateId) => {
      const aggregate = aggregateById.get(aggregateId);
      return aggregate
        ? {
            id: aggregateId,
            kind: "aggregate",
            label: aggregate?.label ?? aggregateId,
            agendaScore: aggregate?.agenda_score ?? aggregate?.agendaScore ?? null,
            claimCount: aggregate?.claim_count ?? aggregate?.claimCount ?? uniqueStrings(toArray(aggregate?.claim_ids ?? aggregate?.claimIds)).length,
          }
        : null;
    })
    .filter(Boolean);

  aggregateNodes.forEach((aggregate) => {
    toArray(aggregateById.get(aggregate.id)?.claim_ids ?? aggregateById.get(aggregate.id)?.claimIds).forEach((claimId) => {
      claimIdsToAttach.add(claimId);
    });
    toArray(aggregateById.get(aggregate.id)?.entity_ids ?? aggregateById.get(aggregate.id)?.entityIds).forEach((entityId) => {
      entityIdsToAttach.add(entityId);
    });
  });

  const claimNodes = [...claimIdsToAttach]
    .map((claimId) => {
      const claim = claimById.get(claimId);
      return claim
        ? {
            id: claimId,
            kind: "claim",
            label: truncateText(claim?.text ?? claimId, 90),
            claimType: claim?.type ?? null,
            primarySection: claim?.primary_section ?? null,
          }
        : null;
    })
    .filter(Boolean);

  claimNodes.forEach((claim) => {
    toArray(claimById.get(claim.id)?.entity_ids).forEach((entityId) => entityIdsToAttach.add(entityId));
  });

  const entityNodes = [...entityIdsToAttach]
    .map((entityId) => {
      const entity = entityById.get(entityId);
      return entity
        ? {
            id: entityId,
            kind: "entity",
            label: entity?.name ?? entityId,
            entityType: entity?.type ?? null,
            claimCount: uniqueStrings(toArray(entity?.claim_ids)).length,
          }
        : null;
    })
    .filter(Boolean);

  const aggregateClaimEdges = aggregateNodes.flatMap((aggregate) =>
    uniqueStrings(toArray(aggregateById.get(aggregate.id)?.claim_ids ?? aggregateById.get(aggregate.id)?.claimIds))
      .filter((claimId) => claimIdsToAttach.has(claimId))
      .map((claimId) => ({
        id: `claim_aggregate:${claimId}:${aggregate.id}`,
        source: claimId,
        target: aggregate.id,
        relation: "claim_supports_aggregate",
        kind: "claim_aggregate",
      })),
  );

  const claimEntityEdges = claimNodes.flatMap((claim) =>
    uniqueStrings(toArray(claimById.get(claim.id)?.entity_ids))
      .filter((entityId) => entityIdsToAttach.has(entityId))
      .map((entityId) => ({
        id: `claim_entity:${claim.id}:${entityId}`,
        source: claim.id,
        target: entityId,
        relation: "mentions",
        kind: "claim_entity",
      })),
  );

  return {
    scope: normalizeConversationScope(options?.scope ?? null),
    nodes: [...facetNodes, ...aggregateNodes, ...claimNodes, ...entityNodes],
    edges: [...facetEdges, ...aggregateClaimEdges, ...claimEntityEdges],
  };
}

function buildFacetGridSnapshot(state = {}, options = {}) {
  const requestedOrder = sanitizeFacetOrder(options?.order, options?.preset);
  const primaryAxis = String(options?.primaryAxis ?? requestedOrder[0] ?? getDefaultPrimaryAxisForPreset(options?.preset)).trim() || "lens";
  const secondaryAxis = String(options?.secondaryAxis ?? requestedOrder.find((axis) => axis !== primaryAxis) ?? getDefaultSecondaryAxisForPreset(options?.preset, primaryAxis)).trim() || "aggregate";
  const order = sanitizeFacetOrder([primaryAxis, secondaryAxis, ...requestedOrder], options?.preset);
  const treeSnapshot = buildFacetTreeSnapshot(state, {
    ...options,
    order,
  });
  const graphSnapshot = buildFacetGraphSnapshot(state, {
    ...options,
    order,
  });
  return {
    primaryAxis,
    secondaryAxis,
    treeSnapshot,
    graphSnapshot,
    cells: toArray(treeSnapshot?.root?.children),
  };
}

function findFacetNode(root = null, nodeId = null) {
  if (!root || !nodeId) return null;
  if (root.id === nodeId) return root;
  for (const child of toArray(root.children)) {
    const match = findFacetNode(child, nodeId);
    if (match) return match;
  }
  return null;
}

function filterGraphSnapshotByFacetNode(graph = null, facetNode = null) {
  if (!graph || !facetNode) return graph;
  const allowedClaims = new Set(toArray(facetNode.claimIds));
  const allowedAggregates = new Set(toArray(facetNode.aggregateIds));
  const allowedEntities = new Set(toArray(facetNode.entityIds));
  const allowedNodeIds = new Set([...allowedClaims, ...allowedAggregates, ...allowedEntities]);
  const edges = toArray(graph.edges).filter((edge) => allowedNodeIds.has(edge?.source) || allowedNodeIds.has(edge?.target));
  edges.forEach((edge) => {
    if (edge?.source) allowedNodeIds.add(edge.source);
    if (edge?.target) allowedNodeIds.add(edge.target);
  });
  return {
    ...graph,
    nodes: toArray(graph.nodes).filter((node) => allowedNodeIds.has(node?.id)),
    edges: edges.filter((edge) => allowedNodeIds.has(edge?.source) && allowedNodeIds.has(edge?.target)),
  };
}

function getNodeTypeVisibilityForFacetPreset(preset = "overview") {
  if (preset === "overview") {
    return {
      domains: false,
      claims: true,
      entities: true,
      topics: false,
      aggregates: true,
    };
  }
  if (preset === "hierarchy") {
    return {
      domains: false,
      claims: true,
      entities: false,
      topics: false,
      aggregates: true,
    };
  }
  return {
    domains: false,
    claims: false,
    entities: true,
    topics: false,
    aggregates: true,
  };
}

function buildQuestionsSnapshot(state = {}, scope = null) {
  const scopedState = buildScopedResearchState(state, scope);
  const scopedObjectives = toArray(scopedState.research_objectives);
  const scopedTasks = toArray(scopedState.research_tasks);
  const runtime = getResearchRuntimeSnapshot(scopedState);
  const runtimeV3 = getResearchRuntimeV3Snapshot(state);
  const globalQuestionsById = new Map(
    toArray(state?.research_questions)
      .filter((question) => question?.id)
      .map((question) => [question.id, question]),
  );
  const scopedQuestionIds = new Set(
    toArray(scopedState.research_questions).map((question) => question?.id).filter(Boolean),
  );
  const supplementalRuntimeQuestionIds = uniqueStrings(
    runtimeV3.recentRuntimeEvents
      .map((event) => event?.payload?.research_question_id ?? event?.research_question_id)
      .filter(Boolean),
  );
  const supplementalQuestions = supplementalRuntimeQuestionIds
    .filter((questionId) => !scopedQuestionIds.has(questionId))
    .map((questionId) => globalQuestionsById.get(questionId))
    .filter(Boolean);
  const displayQuestions = uniqueObjectsBy(
    [
      ...cloneJson(toArray(scopedState.research_questions)),
      ...cloneJson(supplementalQuestions),
    ],
    (question) => question?.id ?? JSON.stringify(question),
  );
  const tasksByQuestionId = new Map();
  const objectivesByQuestionId = new Map();

  scopedTasks.forEach((task) => {
    const questionId = String(task?.research_question_id ?? task?.question_id ?? "").trim();
    if (!questionId) return;
    const tasks = tasksByQuestionId.get(questionId) ?? [];
    tasks.push(task);
    tasksByQuestionId.set(questionId, tasks);
  });

  scopedObjectives.forEach((objective) => {
    const questionId = String(objective?.research_question_id ?? "").trim();
    if (!questionId) return;
    const objectives = objectivesByQuestionId.get(questionId) ?? [];
    objectives.push(objective);
    objectivesByQuestionId.set(questionId, objectives);
  });

  return {
    scope: normalizeConversationScope(scope),
    questions: displayQuestions.map((question) => {
      const questionId = String(question?.id ?? "").trim();
      const rawStatus = normalizeStatus(question?.status);
      const linkedTasks = tasksByQuestionId.get(questionId) ?? [];
      const linkedObjectives = objectivesByQuestionId.get(questionId) ?? [];
      let lifecycle = summarizeQuestionLifecycle(question, linkedTasks, linkedObjectives);
      const activeRuntimeTasks = linkedTasks.filter((task) => runtime.activeTaskIds.has(String(task?.id ?? "").trim()));
      const queuedRuntimeTasks = linkedTasks.filter((task) => runtime.queuedTaskIds.has(String(task?.id ?? "").trim()));
      const activeRuntimeObjectives = linkedObjectives.filter((objective) =>
        runtime.activeObjectiveIds.has(String(objective?.id ?? "").trim()),
      );
      if (activeRuntimeTasks.length > 0) {
        lifecycle = {
          ...lifecycle,
          runningTaskCount: activeRuntimeTasks.length,
          queuedTaskCount: Math.max(lifecycle.queuedTaskCount ?? 0, queuedRuntimeTasks.length),
          activeObjectiveCount: Math.max(lifecycle.activeObjectiveCount ?? 0, activeRuntimeObjectives.length),
          latestActivityMs: runtime.updatedAtMs ?? lifecycle.latestActivityMs ?? null,
          latestActivityAge: runtime.updatedAtMs ? formatRelativeAge(runtime.updatedAtMs) : lifecycle.latestActivityAge,
          synthesizingNow: false,
          engineStateLabel:
            activeRuntimeTasks.length === 1
              ? `Running ${titleizeKey(activeRuntimeTasks[0]?.type ?? "task")}`
              : `${activeRuntimeTasks.length} tasks running`,
          engineStateTone: "blue",
        };
      } else if (activeRuntimeObjectives.length > 0 || runtime.activeQuestionIds.has(questionId)) {
        lifecycle = {
          ...lifecycle,
          activeObjectiveCount: Math.max(lifecycle.activeObjectiveCount ?? 0, activeRuntimeObjectives.length || 1),
          latestActivityMs: runtime.updatedAtMs ?? lifecycle.latestActivityMs ?? null,
          latestActivityAge: runtime.updatedAtMs ? formatRelativeAge(runtime.updatedAtMs) : lifecycle.latestActivityAge,
          synthesizingNow: true,
          engineStateLabel:
            activeRuntimeObjectives.length === 1
              ? `Synthesizing ${titleizeKey(activeRuntimeObjectives[0]?.kind ?? "objective")}`
              : "Objective active, no task running",
          engineStateTone: "violet",
        };
      } else if (queuedRuntimeTasks.length > 0 && rawStatus === "open") {
        lifecycle = {
          ...lifecycle,
          queuedTaskCount: Math.max(lifecycle.queuedTaskCount ?? 0, queuedRuntimeTasks.length),
          latestActivityMs: runtime.updatedAtMs ?? lifecycle.latestActivityMs ?? null,
          latestActivityAge: runtime.updatedAtMs ? formatRelativeAge(runtime.updatedAtMs) : lifecycle.latestActivityAge,
          engineStateLabel: queuedRuntimeTasks.length === 1 ? "1 queued task" : `${queuedRuntimeTasks.length} queued tasks`,
          engineStateTone: "amber",
        };
      }
      return {
        ...question,
        kanbanStatus:
          rawStatus === "open" && lifecycle.runningTaskCount > 0
            ? "active"
            : rawStatus === "open" && lifecycle.synthesizingNow
              ? "synthesizing"
            : rawStatus,
        lifecycle,
      };
    }) ?? [],
  };
}

function buildTasksSnapshot(state = {}, scope = null) {
  const scopedState = buildScopedResearchState(state, scope);
  const runtime = getResearchRuntimeSnapshot(scopedState);
  const objectivesById = new Map(toArray(scopedState.research_objectives).map((objective) => [objective?.id, objective]));
  const questionsById = new Map(toArray(scopedState.research_questions).map((question) => [question?.id, question]));
  return {
    scope: normalizeConversationScope(scope),
    tasks: cloneJson(toArray(scopedState.research_tasks)).map((task) => {
      const rawStatus = normalizeStatus(task?.status);
      const linkedObjective = objectivesById.get(task?.objective_id) ?? null;
      const linkedQuestion = questionsById.get(task?.research_question_id ?? task?.question_id) ?? null;
      const taskId = String(task?.id ?? "").trim();
      let kanbanStatus = rawStatus;
      let lifecycle = summarizeTaskLifecycle(task, linkedObjective, linkedQuestion);
      if (taskId && runtime.activeTaskIds.has(taskId)) {
        kanbanStatus = "active";
        lifecycle = {
          ...lifecycle,
          latestActivityMs: runtime.updatedAtMs ?? lifecycle.latestActivityMs ?? null,
          latestActivityAge: runtime.updatedAtMs ? formatRelativeAge(runtime.updatedAtMs) : lifecycle.latestActivityAge,
          engineStateLabel: "Running now",
          engineStateTone: "blue",
        };
      } else if (taskId && runtime.queuedTaskIds.has(taskId) && rawStatus === "open") {
        lifecycle = {
          ...lifecycle,
          latestActivityMs: runtime.updatedAtMs ?? lifecycle.latestActivityMs ?? null,
          latestActivityAge: runtime.updatedAtMs ? formatRelativeAge(runtime.updatedAtMs) : lifecycle.latestActivityAge,
          engineStateLabel: "Queued in current run",
          engineStateTone: "amber",
        };
      }
      return {
        ...task,
        kanbanStatus,
        lifecycle,
      };
    }) ?? [],
  };
}

// Toggle chip component for filtering graph node types
function formatCount(value, singular, plural = `${singular}s`) {
  const count = Number(value ?? 0);
  return `${count} ${count === 1 ? singular : plural}`;
}

export function normalizeStatus(value = "") {
  const status = String(value ?? "").trim().toLowerCase();
  if (!status) return "unknown";
  if (status === "awaiting_review") return "awaiting_review";
  if (status === "synthesizing") return "synthesizing";
  if (status === "planning_followup") return "planning";
  if (["running", "active", "in_progress"].includes(status)) return "active";
  if (["open", "queued", "ready", "funded", "partial", "planned"].includes(status)) return "open";
  if (["answered", "satisfied", "done", "completed", "closed", "verified", "resolved"].includes(status)) return "closed";
  if (["skipped", "exhausted", "cancelled", "canceled"].includes(status)) return "skipped";
  if (status === "deferred_budget") return "deferred";
  if (["blocked", "failed", "error"].includes(status)) return "blocked";
  return "blocked";
}

function normalizeQuestionPriority(question = null) {
  const statusBucket = normalizeStatus(question?.kanbanStatus ?? question?.status);
  const rawScore = Number(question?.priority_score);
  if (statusBucket === "blocked" || statusBucket === "deferred" || statusBucket === "skipped") {
    return { label: "low", tone: "slate" };
  }
  if (Number.isFinite(rawScore)) {
    if (rawScore >= 1.25) return { label: "high", tone: "rose" };
    if (rawScore >= 0.85) return { label: "medium", tone: "amber" };
    return { label: "low", tone: "slate" };
  }
  const rawPriority = String(question?.priority ?? "").trim().toLowerCase();
  if (["high", "medium", "low"].includes(rawPriority)) {
    return {
      label: rawPriority,
      tone: rawPriority === "high" ? "rose" : rawPriority === "medium" ? "amber" : "slate",
    };
  }
  return statusBucket === "open" || statusBucket === "active" || statusBucket === "synthesizing"
    ? { label: "medium", tone: "amber" }
    : { label: "low", tone: "slate" };
}

function humanizeQuestionStatusReason(value = "") {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) return null;
  const mapped = {
    deferred_budget: "Deferred because the execution budget was exhausted or reserved for higher-priority work.",
    question_deferred_at_finalize: "Deferred at finalization because the run ended before this question could be funded.",
    objective_deferred_at_finalize: "Deferred because the linked objective was deprioritized at finalization.",
    queued_tasks_blocked: "Blocked because the remaining queued tasks could not run.",
    no_remaining_tasks_with_unresolved_questions: "Blocked because there were unresolved questions but no runnable tasks left.",
    policy_deferred: "Deferred by the current verification or research policy.",
    verification_deferred: "Deferred because verification work did not meet the promotion threshold.",
  }[normalized];
  return mapped ?? titleizeKey(normalized);
}

function humanizeObjectiveStatusReason(value = "") {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (!normalized) return null;
  const mapped = {
    deferred_budget: "Deferred because the execution budget was exhausted or reserved for higher-priority work.",
    pending_tasks: "Waiting for queued or running tasks to finish.",
    bootstrap_search_pending: "Waiting for the initial search step to be queued or completed.",
    baseline_search_pending: "Waiting for baseline discovery follow-up work.",
    awaiting_upstream_evidence: "Waiting for upstream evidence before this objective can progress.",
    recovery_required: "Blocked until secondary source recovery completes.",
    claim_binding_required: "Blocked until claim binding is completed.",
    verification_followup_required: "Waiting for claim verification follow-up.",
    needs_substantive_evidence: "Blocked because only shallow evidence was found; substantive sources are still needed.",
    soft_evidence_only: "Only shallow evidence was found, so the objective cannot progress further yet.",
    substantive_source_coverage: "This objective has enough substantive source coverage.",
    sufficient_coverage: "This objective has enough coverage to be considered satisfied.",
    additional_steps_available: "More follow-up work is available for this objective.",
    planning_saturated: "No additional high-value follow-up steps remain.",
    no_successful_steps: "The objective did not complete any successful steps.",
    all_steps_failed: "All execution steps for this objective failed.",
  }[normalized];
  return mapped ?? titleizeKey(normalized);
}

function getObjectiveStatusReason(objective = null) {
  const explicitReason = [
    objective?.status_reason,
    objective?.reason,
    objective?.blocked_reason,
    objective?.deferred_reason,
    objective?.failure_reason,
    objective?.error,
    objective?.error_message,
    objective?.metadata?.status_reason,
    objective?.metadata?.reason,
    objective?.metadata?.blocked_reason,
    objective?.metadata?.deferred_reason,
    objective?.metadata?.error,
    objective?.metadata?.error_message,
  ]
    .map((value) => (typeof value === "string" ? value.trim() : null))
    .find(Boolean);
  if (explicitReason) {
    return humanizeObjectiveStatusReason(explicitReason) ?? explicitReason;
  }
  const remainingWork = uniqueStrings(toArray(objective?.remaining_work));
  if (remainingWork.length) {
    return `Remaining work: ${remainingWork.map((entry) => humanizeKey(entry)).join(", ")}.`;
  }
  const statusBucket = normalizeStatus(objective?.status);
  if (statusBucket === "deferred") {
    return "Deferred because the engine ran out of budget or higher-priority work was chosen first.";
  }
  if (statusBucket === "blocked") {
    return "Blocked because the engine could not progress this objective with the current evidence, policy, or runnable tasks.";
  }
  return null;
}

function getQuestionStatusReason(question = null) {
  const blockedDomains = uniqueStrings(toArray(question?.metadata?.blocked_domains ?? question?.blocked_domains));
  if (blockedDomains.length) {
    return `Blocked by domain policy: ${blockedDomains.join(", ")}`;
  }
  const explicitReason = [
    question?.status_reason,
    question?.reason,
    question?.blocked_reason,
    question?.deferred_reason,
    question?.failure_reason,
    question?.error,
    question?.error_message,
    question?.metadata?.status_reason,
    question?.metadata?.reason,
    question?.metadata?.blocked_reason,
    question?.metadata?.deferred_reason,
    question?.metadata?.release_reason,
    question?.metadata?.error,
    question?.metadata?.error_message,
  ]
    .map((value) => (typeof value === "string" ? value.trim() : null))
    .find(Boolean);
  if (explicitReason) {
    return humanizeQuestionStatusReason(explicitReason) ?? explicitReason;
  }
  const statusBucket = normalizeStatus(question?.kanbanStatus ?? question?.status);
  if (statusBucket === "deferred") {
    return "Deferred because the engine ran out of budget or higher-priority work was chosen first.";
  }
  if (statusBucket === "blocked") {
    return "Blocked because the engine could not progress this question with the current sources, policy, or runnable tasks.";
  }
  return null;
}

export function summarizeStatuses(items = []) {
  const counts = { synthesizing: 0, active: 0, open: 0, closed: 0, skipped: 0, deferred: 0, blocked: 0 };
  items.forEach((item) => {
    const normalized = normalizeStatus(item?.kanbanStatus ?? item?.status);
    if (Object.prototype.hasOwnProperty.call(counts, normalized)) {
      counts[normalized] += 1;
    }
  });
  const parts = [];
  if (counts.synthesizing) parts.push(`${counts.synthesizing} synthesizing`);
  if (counts.active) parts.push(`${counts.active} active`);
  if (counts.open) parts.push(`${counts.open} open`);
  if (counts.blocked) parts.push(`${counts.blocked} failed`);
  if (counts.deferred) parts.push(`${counts.deferred} deferred`);
  const settledCount = counts.closed + counts.skipped;
  if (settledCount) parts.push(`${settledCount} settled`);
  return parts.join(" · ") || "All settled";
}

export function countStatuses(items = []) {
  const counts = { synthesizing: 0, active: 0, open: 0, closed: 0, skipped: 0, deferred: 0, blocked: 0 };
  items.forEach((item) => {
    const normalized = normalizeStatus(item?.kanbanStatus ?? item?.status);
    if (Object.prototype.hasOwnProperty.call(counts, normalized)) {
      counts[normalized] += 1;
    }
  });
  return counts;
}

const KANBAN_STATUS_COLUMNS = [
  { key: "active", label: "Active" },
  { key: "planning", label: "Thinking…" },
  { key: "synthesizing", label: "Synthesizing" },
  { key: "open", label: "Open" },
  { key: "blocked", label: "Blocked" },
  { key: "deferred", label: "Deferred" },
  { key: "closed", label: "Closed" },
  { key: "skipped", label: "Skipped" },
];

const KANBAN_STATUS_THEMES = {
  active: {
    dotClass: "bg-blue-500",
    barClass: "bg-blue-500",
    badgeClass: "bg-blue-50 text-blue-700",
    columnClass: "border-blue-200 bg-blue-50/60",
  },
  awaiting_review: {
    dotClass: "bg-orange-500",
    barClass: "bg-orange-500",
    badgeClass: "bg-orange-50 text-orange-700",
    columnClass: "border-orange-200 bg-orange-50/60",
  },
  synthesizing: {
    dotClass: "bg-violet-500",
    barClass: "bg-violet-500",
    badgeClass: "bg-violet-50 text-violet-700",
    columnClass: "border-violet-200 bg-violet-50/60",
  },
  open: {
    dotClass: "bg-amber-500",
    barClass: "bg-amber-500",
    badgeClass: "bg-amber-50 text-amber-700",
    columnClass: "border-amber-200 bg-amber-50/60",
  },
  blocked: {
    dotClass: "bg-rose-500",
    barClass: "bg-rose-500",
    badgeClass: "bg-rose-50 text-rose-700",
    columnClass: "border-rose-200 bg-rose-50/60",
  },
  deferred: {
    dotClass: "bg-slate-400",
    barClass: "bg-slate-400",
    badgeClass: "bg-slate-100 text-slate-700",
    columnClass: "border-slate-200 bg-slate-100/70",
  },
  closed: {
    dotClass: "bg-emerald-500",
    barClass: "bg-emerald-500",
    badgeClass: "bg-emerald-50 text-emerald-700",
    columnClass: "border-emerald-200 bg-emerald-50/60",
  },
  skipped: {
    dotClass: "bg-stone-400",
    barClass: "bg-stone-400",
    badgeClass: "bg-stone-100 text-stone-700",
    columnClass: "border-stone-200 bg-stone-100/70",
  },
};

function getKanbanStatusTheme(status = "") {
  return KANBAN_STATUS_THEMES[normalizeStatus(status)] ?? KANBAN_STATUS_THEMES.blocked;
}

function titleizeKey(value = "", fallback = "Unassigned") {
  return humanizeKey(value, fallback).replace(/\b\w/g, (char) => char.toUpperCase());
}

export function formatTaskStatusLabel(value = "") {
  const status = String(value ?? "").trim().toLowerCase();
  if (!status) return "Unknown";
  if (status === "awaiting_review") return "Awaiting Review";
  if (status === "synthesizing") return "Synthesizing";
  if (["running", "active", "in_progress"].includes(status)) return "Active";
  if (["open", "queued", "ready", "funded", "partial", "planned"].includes(status)) return "Open";
  if (["answered", "satisfied", "done", "completed", "closed", "verified", "resolved"].includes(status)) return "Closed";
  if (["skipped", "exhausted", "cancelled", "canceled"].includes(status)) return "Skipped";
  if (status === "deferred_budget") return "Deferred";
  if (["failed", "error"].includes(status)) return "Failed";
  if (status === "blocked") return "Blocked";
  return titleizeKey(status);
}

function normalizeAgentLifecycleStatus(value = "") {
  const status = String(value ?? "").trim().toLowerCase();
  if (!status) return "blocked";
  if (["awaiting_review", "awaiting_children", "planning_followup", "running", "ready", "ready_to_reason", "blocked", "completed", "answered", "deferred"].includes(status)) return status;
  if (["active", "in_progress", "partial"].includes(status)) return "running";
  if (["queued", "planned", "open", "funded"].includes(status)) return "ready";
  if (["closed", "answered", "satisfied", "done", "resolved", "verified"].includes(status)) return "completed";
  if (["failed", "error", "exhausted", "canceled", "cancelled"].includes(status)) return "blocked";
  return "blocked";
}

function isActionableAgentThread(agentThread = {}) {
  const agentId = String(agentThread?.id ?? "").trim();
  return agentId.startsWith("agent:") || agentId.startsWith("writer:");
}

function formatAgentLifecycleLabel(value = "") {
  const status = normalizeAgentLifecycleStatus(value);
  if (status === "awaiting_review") return "Awaiting Review";
  if (status === "awaiting_children") return "Awaiting Children";
  if (status === "planning_followup") return "Thinking…";
  if (status === "running") return "Active";
  if (status === "ready") return "Ready";
  if (status === "ready_to_reason") return "Ready To Reason";
  if (status === "completed") return "Completed";
  if (status === "answered") return "Answered";
  if (status === "deferred") return "Deferred";
  return "Blocked";
}

function formatFetchStatusMessage(task = {}) {
  const fetchStatus = String(task?.metadata?.fetch_status ?? "").trim().toLowerCase();
  if (fetchStatus === "no_text") return "Page could not be read";
  if (fetchStatus === "ok") return null;
  if (fetchStatus) return `Fetch: ${titleizeKey(fetchStatus)}`;
  if (String(task?.status ?? "").trim().toLowerCase() === "failed") {
    return task?.metadata?.error ? truncateText(task.metadata.error, 100) : "Fetch failed";
  }
  return null;
}

function summarizeNestedFetches(fetches = []) {
  const counts = { synthesizing: 0, active: 0, open: 0, blocked: 0, deferred: 0, closed: 0, skipped: 0 };
  toArray(fetches).forEach((entry) => {
    const normalized = normalizeStatus(entry?.status);
    if (Object.prototype.hasOwnProperty.call(counts, normalized)) {
      counts[normalized] += 1;
    }
  });
  const parts = [];
  if (counts.synthesizing) parts.push(`${counts.synthesizing} synthesizing`);
  if (counts.active) parts.push(`${counts.active} active`);
  if (counts.open) parts.push(`${counts.open} open`);
  if (counts.blocked) parts.push(`${counts.blocked} failed`);
  if (counts.deferred) parts.push(`${counts.deferred} deferred`);
  const settledCount = counts.closed + counts.skipped;
  if (settledCount) parts.push(`${settledCount} settled`);
  return parts.join(" · ") || "All settled";
}

function aggregateNestedTaskStatus(task = {}) {
  const nestedStatuses = (Array.isArray(task?.nestedFetches) ? task.nestedFetches : [])
    .map((entry) => normalizeStatus(entry?.status));
  const ownStatus = normalizeStatus(task?.kanbanStatus ?? task?.status);
  if (!nestedStatuses.length) {
    return ownStatus;
  }
  if (ownStatus === "active" || nestedStatuses.includes("active")) return "active";
  if (nestedStatuses.includes("open")) return "open";
  if (ownStatus === "blocked" || nestedStatuses.includes("blocked")) return "blocked";
  if (ownStatus === "deferred" || nestedStatuses.includes("deferred")) return "deferred";
  if (ownStatus === "skipped") return "skipped";
  const statuses = [ownStatus, ...nestedStatuses];
  if (statuses.includes("closed")) return "closed";
  if (statuses.includes("skipped")) return "skipped";
  return ownStatus;
}

function KanbanBoard({ items = [], renderCard, emptyMessage }) {
  const columns = useMemo(
    () =>
      KANBAN_STATUS_COLUMNS.map((column) => ({
        ...column,
        items: items.filter((item) => normalizeStatus(item?.kanbanStatus ?? item?.status) === column.key),
      })),
    [items],
  );
  const [activeColumn, setActiveColumn] = useState(columns[0]?.key ?? "open");

  useEffect(() => {
    if (!columns.some((column) => column.key === activeColumn)) {
      setActiveColumn(columns[0]?.key ?? "open");
    }
  }, [activeColumn, columns]);

  if (!items.length) {
    return <p className="text-sm text-slate-500">{emptyMessage}</p>;
  }

  const selectedColumn = columns.find((column) => column.key === activeColumn) ?? columns[0];

  return (
    <div className="space-y-4">
      <div className="sm:hidden">
        <select
          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm"
          value={selectedColumn?.key ?? "open"}
          onChange={(event) => setActiveColumn(event.target.value)}
        >
          {columns.map((column) => (
            <option key={column.key} value={column.key}>
              {`${column.label} (${column.items.length})`}
            </option>
          ))}
        </select>
        {selectedColumn ? (
          <div className={clsx("mt-3 rounded-2xl border p-3", getKanbanStatusTheme(selectedColumn.key).columnClass)}>
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={clsx("h-2.5 w-2.5 rounded-full", getKanbanStatusTheme(selectedColumn.key).dotClass)} aria-hidden="true" />
                <h3 className="text-sm font-semibold text-slate-900">{selectedColumn.label}</h3>
              </div>
              <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
                {selectedColumn.items.length}
              </span>
            </div>
            <div className="space-y-3">
              {selectedColumn.items.length ? selectedColumn.items.map(renderCard) : (
                <p className="rounded-2xl border border-dashed border-slate-200 bg-white/80 px-4 py-6 text-center text-sm text-slate-500">
                  Nothing in this column.
                </p>
              )}
            </div>
          </div>
        ) : null}
      </div>

      <div className="hidden gap-4 overflow-x-auto pb-1 sm:flex">
        {columns.map((column) => (
          <section
            key={column.key}
            className={clsx("min-w-[17rem] flex-1 rounded-2xl border p-3", getKanbanStatusTheme(column.key).columnClass)}
          >
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={clsx("h-2.5 w-2.5 rounded-full", getKanbanStatusTheme(column.key).dotClass)} aria-hidden="true" />
                <h3 className="text-sm font-semibold text-slate-900">{column.label}</h3>
              </div>
              <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
                {column.items.length}
              </span>
            </div>
            <div className="space-y-3">
              {column.items.length ? column.items.map(renderCard) : (
                <p className="rounded-2xl border border-dashed border-slate-200 bg-white/80 px-4 py-6 text-center text-sm text-slate-500">
                  Nothing in this column.
                </p>
              )}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function ResetChatIcon(props) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...props}>
      <path d="M6.25 6.25h7.5a2.5 2.5 0 0 1 2.5 2.5v4a2.5 2.5 0 0 1-2.5 2.5h-4l-3.5 2v-2H6.25a2.5 2.5 0 0 1-2.5-2.5v-4a2.5 2.5 0 0 1 2.5-2.5Z" />
      <path d="M8.2 10h3.6" />
      <path d="M10 8.2V5.75" />
      <path d="M7.5 3.75H10v2.5" />
    </svg>
  );
}

function RestartRunIcon(props) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...props}>
      <path d="M16.25 10a6.25 6.25 0 1 1-2.12-4.69" />
      <path d="M16.25 4.75v4h-4" />
      <path d="M10 7.5v3l2 1.25" />
    </svg>
  );
}

function ChevronRightIcon(props) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...props}>
      <path d="m7.5 4.75 5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ChevronDownIcon(props) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...props}>
      <path d="m4.75 7.5 5 5 5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ActivityRowIcon(props) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...props}>
      <circle cx="10" cy="10" r="2.2" fill="currentColor" stroke="none" />
      <path d="M10 4.5v2.1M10 13.4v2.1M4.5 10h2.1M13.4 10h2.1" strokeLinecap="round" />
    </svg>
  );
}

function ClaimsIcon(props) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...props}>
      <path d="M4.75 5.75h10.5M4.75 10h10.5M4.75 14.25h6.5" strokeLinecap="round" />
    </svg>
  );
}

function QuestionsIcon(props) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...props}>
      <path d="M8.8 7.1a1.9 1.9 0 1 1 2.72 1.72c-.94.45-1.52 1.03-1.52 2.08" strokeLinecap="round" />
      <circle cx="10" cy="14.1" r="0.85" fill="currentColor" stroke="none" />
      <path d="M10 17a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z" />
    </svg>
  );
}

function TasksIcon(props) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...props}>
      <path d="M6.25 5.75h8M6.25 10h8M6.25 14.25h8" strokeLinecap="round" />
      <path d="m3.9 5.9.45.45.85-.95M3.9 10.15l.45.45.85-.95M3.9 14.4l.45.45.85-.95" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AgentsIcon(props) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true" {...props}>
      <circle cx="7" cy="8" r="2.2" />
      <circle cx="13.2" cy="7" r="1.8" />
      <path d="M3.8 15.5c.7-2.3 2.4-3.5 5-3.5s4.3 1.2 5 3.5" strokeLinecap="round" />
      <path d="M12.5 12.2c1.7.2 2.9 1 3.7 2.4" strokeLinecap="round" />
    </svg>
  );
}

function parseTurnSummaryMetrics(entryOrContent = "") {
  const entry =
    entryOrContent && typeof entryOrContent === "object" && !Array.isArray(entryOrContent)
      ? entryOrContent
      : null;
  const text = String(entry?.content ?? entryOrContent ?? "");
  const claims = text.match(/(\d+)\s+claims?/i);
  const questions = text.match(/(\d+)\s+open questions?/i);
  const tasks = text.match(/(\d+)\s+queued tasks?/i);
  const claimCount = Number(entry?.metadata?.claimCount ?? entry?.metadata?.claim_count);
  const questionCount = Number(
    entry?.metadata?.openQuestionCount ?? entry?.metadata?.open_question_count,
  );
  const taskCount = Number(
    entry?.metadata?.queuedTaskCount ?? entry?.metadata?.queued_task_count,
  );
  return {
    claims: Number.isFinite(claimCount) ? claimCount : claims ? Number(claims[1]) : null,
    questions: Number.isFinite(questionCount) ? questionCount : questions ? Number(questions[1]) : null,
    tasks: Number.isFinite(taskCount) ? taskCount : tasks ? Number(tasks[1]) : null,
  };
}

function pickActivityIcon(entry = {}) {
  const text = String(entry?.content ?? "").toLowerCase();
  if (text.includes("objective")) {
    return ActivityRowIcon;
  }
  if (text.includes("question")) {
    return QuestionsIcon;
  }
  if (text.includes("task") || text.includes("fetch")) {
    return TasksIcon;
  }
  if (text.includes("claim")) {
    return ClaimsIcon;
  }
  return ActivityRowIcon;
}

function countTurnActivityKinds(activities = []) {
  return activities.reduce(
    (counts, entry) => {
      const kind = String(entry?.kind ?? "").trim();
      if (kind === "objective_opened") counts.objectivesOpened += 1;
      else if (kind === "objective_closed") counts.objectivesClosed += 1;
      else if (kind === "question_opened") counts.questionsOpened += 1;
      else if (kind === "question_closed") counts.questionsClosed += 1;
      else if (kind === "task_enqueued") counts.tasksOpened += 1;
      else if (kind === "task_completed") counts.tasksClosed += 1;
      return counts;
    },
    {
      objectivesOpened: 0,
      objectivesClosed: 0,
      questionsOpened: 0,
      questionsClosed: 0,
      tasksOpened: 0,
      tasksClosed: 0,
    },
  );
}

function buildTurnChangeSummaryRows({
  objectivesOpened = 0,
  objectivesClosed = 0,
  questionsOpened = 0,
  questionsClosed = 0,
  tasksOpened = 0,
  tasksClosed = 0,
}) {
  const rows = [];
  const addRow = (key, icon, label, opened, closed) => {
    if (!opened && !closed) {
      return;
    }
    rows.push({
      key,
      icon,
      label,
      opened,
      closed,
    });
  };
  addRow("objectives", ActivityRowIcon, "Objectives", objectivesOpened, objectivesClosed);
  addRow("questions", QuestionsIcon, "Questions", questionsOpened, questionsClosed);
  addRow("tasks", TasksIcon, "Tasks", tasksOpened, tasksClosed);
  return rows;
}

function isWorkTraceActivity(entry = {}) {
  const kind = String(entry?.kind ?? "").trim();
  return ![
    "objective_opened",
    "objective_closed",
    "question_opened",
    "question_closed",
    "task_enqueued",
  ].includes(kind);
}

function mergeActivityEntries(...lists) {
  const merged = [];
  lists.flat().forEach((entry) => {
    const normalized = normalizeActivityItem(entry, entry?.kind ?? "update");
    const key = getActivitySemanticKey(normalized);
    if (!key) return;
    const existingIndex = merged.findIndex((item) => getActivitySemanticKey(item) === key);
    if (existingIndex >= 0) {
      const existing = merged[existingIndex];
      merged[existingIndex] = {
        ...existing,
        ...normalized,
        id: existing.id || normalized.id,
        createdAt: existing.createdAt ?? normalized.createdAt,
        updatedAt:
          new Date(existing.updatedAt ?? existing.createdAt ?? 0).getTime() >
          new Date(normalized.updatedAt ?? normalized.createdAt ?? 0).getTime()
            ? existing.updatedAt
            : normalized.updatedAt ?? normalized.createdAt,
        repeatCount: Math.max(
          Number(existing.repeatCount ?? 1),
          Number(normalized.repeatCount ?? 1),
        ),
      };
      return;
    }
    merged.push(normalized);
  });
  return merged.sort(
    (left, right) =>
      new Date(left.createdAt ?? left.updatedAt ?? 0).getTime() -
      new Date(right.createdAt ?? right.updatedAt ?? 0).getTime(),
  );
}

function buildRunGroups(items = []) {
  const groups = [];
  let turnBuffer = [];

  const flushTurnBuffer = () => {
    if (!turnBuffer.length) {
      return;
    }
    const latestTurn = turnBuffer[turnBuffer.length - 1];
    groups.push({
      id: `run_group_${turnBuffer[0].id}`,
      timelineType: "run_group",
      timelineTime: turnBuffer[0].timelineTime,
      turns: turnBuffer,
      latestTurn,
      totalUpdates: turnBuffer.reduce((sum, entry) => sum + (entry.updates?.length ?? 0), 0),
    });
    turnBuffer = [];
  };

  items.forEach((item) => {
    if (item.timelineType === "turn_block") {
      turnBuffer.push(item);
      return;
    }
    flushTurnBuffer();
    groups.push(item);
  });

  flushTurnBuffer();
  return groups;
}

function summarizeQuestionLabel(question = null) {
  const raw = String(question?.question ?? question?.id ?? "Question").trim();
  if (!raw) return "Question";
  const replacements = [
    [/^scan peers on topic:\s*/i, "Peer scan: "],
    [/^trace topic:\s*/i, "Topic trace: "],
    [/^expand topic coverage:\s*/i, "Coverage expand: "],
    [/^explore market context for:\s*/i, "Market context: "],
    [/^deepen claim:\s*/i, "Verify claim: "],
    [/^corroborate social signal:\s*/i, "Corroborate signal: "],
  ];
  const normalized = replacements.reduce(
    (value, [pattern, replacement]) => value.replace(pattern, replacement),
    raw,
  );
  return truncateText(normalized, 104);
}

function summarizeObjectiveLabel(objective = null) {
  return summarizeQuestionLabel({
    question: objective?.question ?? objective?.id ?? "Objective",
  });
}

function buildResearchReferenceMaps(state = {}) {
  return {
    objectivesById: new Map(toArray(state?.research_objectives).map((objective) => [objective?.id, objective])),
    questionsById: new Map(toArray(state?.research_questions).map((question) => [question?.id, question])),
  };
}

function resolveObjectiveReferenceLabel(objective = null, references = {}) {
  if (!objective) return null;
  const linkedQuestion = references?.questionsById?.get?.(objective?.research_question_id) ?? null;
  const sectionLabel = objective?.section_key ? titleizeKey(objective.section_key) : null;
  return summarizeQuestionLabel({
    question:
      objective?.question ??
      linkedQuestion?.question ??
      linkedQuestion?.title ??
      sectionLabel ??
      objective?.kind ??
      objective?.id ??
      "Objective",
  });
}

function resolveQuestionReferenceLabel(question = null) {
  if (!question) return null;
  return summarizeQuestionLabel({
    question:
      question?.question ??
      question?.title ??
      question?.id ??
      "Question",
  });
}

function buildTaskReferenceLabel(task = null, references = {}) {
  const linkedQuestion =
    references?.questionsById?.get?.(task?.research_question_id ?? task?.question_id) ?? null;
  const linkedObjective =
    references?.objectivesById?.get?.(task?.objective_id) ?? null;
  return (
    resolveQuestionReferenceLabel(linkedQuestion) ??
    resolveObjectiveReferenceLabel(linkedObjective, references) ??
    (task?.section_key ? titleizeKey(task.section_key) : null)
  );
}

function summarizeTaskLabel(task = null, references = null) {
  const taskType = String(task?.type ?? "").trim().toLowerCase();
  const sectionKey = String(task?.section_key ?? "").trim();
  const sectionLabel = sectionKey ? humanizeKey(sectionKey).replace(/\b\w/g, (char) => char.toUpperCase()) : null;
  const linkedLabel = buildTaskReferenceLabel(task, references ?? {});
  const query = String(task?.query ?? "").trim();
  if (taskType === "fetch_page") {
    const domain = extractDisplayDomain(task?.metadata?.url ?? query);
    if (domain && sectionLabel) return `Fetch ${domain} for ${sectionLabel}`;
    if (domain) return `Fetch ${domain}`;
  }
  if (taskType === "search_google") {
    const compactQuery = summarizeQuestionLabel({ question: query });
    return compactQuery.startsWith("Peer scan:")
      || compactQuery.startsWith("Topic trace:")
      || compactQuery.startsWith("Coverage expand:")
      || compactQuery.startsWith("Market context:")
      || compactQuery.startsWith("Verify claim:")
      || compactQuery.startsWith("Corroborate signal:")
      ? compactQuery
      : truncateText(`Search: ${query || linkedLabel || sectionLabel || "web"}`, 104);
  }
  if (taskType === "grounded_query") {
    return linkedLabel ? `Grounded scan: ${linkedLabel}` : sectionLabel ? `Grounded scan: ${sectionLabel}` : "Grounded scan";
  }
  if (taskType === "local_rag") {
    return linkedLabel ? `Internal evidence: ${linkedLabel}` : sectionLabel ? `Internal evidence: ${sectionLabel}` : "Internal evidence";
  }
  if (taskType === "resolve_company_domain") {
    return linkedLabel ? `Resolve company domain: ${linkedLabel}` : "Resolve company domain";
  }
  if (query) {
    return truncateText(query, 104);
  }
  if (linkedLabel) {
    return truncateText(`${humanizeKey(taskType, "Task")}: ${linkedLabel}`, 104);
  }
  if (sectionLabel) {
    return truncateText(`${humanizeKey(taskType, "Task")}: ${sectionLabel}`, 104);
  }
  return humanizeKey(taskType, "Task");
}

function normalizeTaskActivityStatus(status = "") {
  const normalized = normalizeStatus(status);
  if (["running", "active", "in_progress"].includes(normalized)) return "running";
  if (normalized === "open") return "queued";
  if (normalized === "closed") return "completed";
  if (normalized === "blocked") return "blocked";
  if (normalized === "skipped") return "skipped";
  return normalized || "unknown";
}

function formatTaskActivityStatusLabel(status = "") {
  switch (normalizeTaskActivityStatus(status)) {
    case "running":
      return "Running";
    case "queued":
      return "Queued";
    case "completed":
      return "Done";
    case "blocked":
      return "Blocked";
    case "skipped":
      return "Skipped";
    default:
      return formatTaskStatusLabel(status);
  }
}

function getTaskActivityStatusTheme(status = "") {
  switch (normalizeTaskActivityStatus(status)) {
    case "running":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "queued":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "completed":
      return "bg-slate-100 text-slate-700 border-slate-200";
    case "blocked":
      return "bg-rose-50 text-rose-700 border-rose-200";
    case "skipped":
      return "bg-zinc-100 text-zinc-600 border-zinc-200";
    default:
      return "bg-slate-100 text-slate-600 border-slate-200";
  }
}

function hasLiveTaskExecutionSignal(currentAction = null) {
  if (!currentAction || typeof currentAction !== "object") return false;
  const actionName = String(currentAction?.action ?? "").trim().toLowerCase();
  if (actionName === "run_tasks") {
    return toArray(currentAction?.task_ids).length > 0;
  }
  return false;
}

function isVerificationAgent(agent = {}) {
  const objectiveKind = String(agent?.work_package?.recipe_contract?.objective_kind ?? "").trim().toLowerCase();
  if (objectiveKind === "verify") return true;
  const missionText = String(
    agent?.work_package?.mission ??
    agent?.work_package?.question ??
    agent?.work_package?.objective ??
    agent?.label ??
    "",
  )
    .trim()
    .toLowerCase();
  return missionText.startsWith("verify claims:") || missionText.startsWith("verify claim:");
}

function buildTaskReferenceDetail(task = null, references = {}) {
  const linkedQuestion =
    references?.questionsById?.get?.(task?.research_question_id ?? task?.question_id) ?? null;
  const linkedObjective =
    references?.objectivesById?.get?.(task?.objective_id ?? linkedQuestion?.parent_objective_id) ?? null;
  if (linkedQuestion) {
    return `${String(linkedObjective?.kind ?? "").trim().toLowerCase() === "verify" ? "Claim to verify" : "Question"}: ${resolveQuestionReferenceLabel(linkedQuestion)}`;
  }
  if (linkedObjective) {
    return `Objective: ${resolveObjectiveReferenceLabel(linkedObjective, references)}`;
  }
  return task?.section_key ? `Section: ${titleizeKey(task.section_key)}` : null;
}

function resolveActivityContent(entry = {}, state = {}) {
  const normalized = normalizeActivityItem(entry, entry?.kind ?? "status");
  const references = buildResearchReferenceMaps(state);
  const objectiveLabel = normalized.objectiveId
    ? resolveObjectiveReferenceLabel(references.objectivesById.get(normalized.objectiveId), references)
    : null;
  const questionLabel = normalized.researchQuestionId
    ? resolveQuestionReferenceLabel(references.questionsById.get(normalized.researchQuestionId))
    : null;
  let content = String(normalized.content ?? "").trim();
  if (!content) return content;
  if (objectiveLabel && normalized.objectiveId) {
    content = content.replace(new RegExp(`\\b${normalized.objectiveId}\\b`, "g"), objectiveLabel);
  }
  if (questionLabel && normalized.researchQuestionId) {
    content = content.replace(new RegExp(`\\b${normalized.researchQuestionId}\\b`, "g"), questionLabel);
  }
  if (/^Queued\s+/i.test(content) && normalized.taskType && objectiveLabel && !content.includes(objectiveLabel)) {
    return `Queued ${humanizeKey(normalized.taskType)} for ${objectiveLabel}.`;
  }
  return content;
}

function buildScopedActiveWorkItems(state = {}, scope = null) {
  const questions = toArray(state?.research_questions);
  const tasks = toArray(state?.research_tasks);
  const objectives = toArray(state?.research_objectives);
  const references = buildResearchReferenceMaps(state);
  const anchorIds = new Set(toArray(scope?.anchorIds));
  const anchorType = String(scope?.anchorType ?? "").trim().toLowerCase();
  const isRootScope = !scope || scope.type !== "deep_dive" || anchorIds.size === 0;

  const questionMatchesScope = (question) => {
    if (isRootScope) return true;
    if (anchorType === "entity") {
      return toArray(question?.anchor_entity_ids).some((entityId) => anchorIds.has(entityId));
    }
    if (anchorType === "claim" || anchorType === "claim_cluster") {
      return anchorIds.has(question?.parent_claim_id);
    }
    if (anchorType === "topic") {
      return anchorIds.has(question?.parent_topic_id);
    }
    if (anchorType === "aggregate") {
      return anchorIds.has(question?.parent_aggregate_id);
    }
    return true;
  };

  const objectiveMatchesScope = (objective) => {
    if (isRootScope) return true;
    if (anchorType === "entity") {
      return toArray(objective?.anchor_entity_ids).some((entityId) => anchorIds.has(entityId));
    }
    if (anchorType === "claim" || anchorType === "claim_cluster") {
      return [
        objective?.parent_claim_id,
        ...toArray(objective?.frontier_claim_ids),
        ...toArray(objective?.direct_claim_ids),
        ...toArray(objective?.supporting_claim_ids),
        ...toArray(objective?.incidental_claim_ids),
        ...toArray(objective?.claim_ids),
      ].some((claimId) => anchorIds.has(claimId));
    }
    if (anchorType === "topic") {
      return [
        objective?.parent_topic_id,
        ...toArray(objective?.anchor_topic_ids),
        ...toArray(objective?.frontier_topic_ids),
      ].some((topicId) => anchorIds.has(topicId));
    }
    if (anchorType === "aggregate") {
      return anchorIds.has(objective?.parent_aggregate_id);
    }
    return true;
  };

  const scopedQuestions = questions.filter(questionMatchesScope);
  const scopedObjectives = objectives.filter(objectiveMatchesScope);
  const scopedObjectiveIds = new Set(scopedObjectives.map((objective) => objective?.id).filter(Boolean));
  const scopedAgentIds = new Set(
    [
      ...scopedQuestions.map((question) => question?.agent_id).filter(Boolean),
      ...scopedObjectives.map((objective) => objective?.agent_id).filter(Boolean),
    ],
  );
  const scopedTasks = tasks.filter((task) => {
    if (isRootScope) return true;
    if (scopedObjectiveIds.has(task?.objective_id)) return true;
    if (scopedAgentIds.has(task?.agent_id)) return true;
    if (anchorType === "claim" || anchorType === "claim_cluster") {
      return anchorIds.has(task?.claim_id);
    }
    return false;
  });

  const activeTasks = scopedTasks.filter((task) => normalizeStatus(task?.status) === "active");
  const taskObjectiveIds = new Set(activeTasks.map((task) => task?.objective_id).filter(Boolean));
  const taskQuestionIds = new Set(
    activeTasks.map((task) => task?.research_question_id ?? task?.question_id).filter(Boolean),
  );
  const activeObjectives = scopedObjectives.filter((objective) => {
    if (normalizeStatus(objective?.status) !== "active") {
      return false;
    }
    const statusReason = String(objective?.status_reason ?? "").trim().toLowerCase();
    if (statusReason === "awaiting_upstream_evidence") {
      return false;
    }
    const hasRunningTask = scopedTasks.some(
      (task) =>
        String(task?.objective_id ?? "").trim() === String(objective?.id ?? "").trim() &&
        normalizeStatus(task?.status) === "active",
    );
    if (hasRunningTask) {
      return true;
    }
    return String(objective?.kind ?? "").trim().toLowerCase() === "synthesis";
  });
  const objectiveQuestionIds = new Set(
    activeObjectives.map((objective) => objective?.research_question_id).filter(Boolean),
  );
  const activeQuestions = scopedQuestions.filter((question) => {
    const linkedTasks = scopedTasks.filter((task) =>
      String(task?.research_question_id ?? task?.question_id ?? "").trim() === String(question?.id ?? "").trim(),
    );
    const linkedObjectives = scopedObjectives.filter((objective) =>
      String(objective?.research_question_id ?? "").trim() === String(question?.id ?? "").trim(),
    );
    const lifecycle = summarizeQuestionLifecycle(question, linkedTasks, linkedObjectives);
    return lifecycle.runningTaskCount > 0 || lifecycle.synthesizingNow;
  });
  const statusPriority = {
    running: 0,
    queued: 1,
    ready: 2,
    active: 2,
    open: 3,
    partial: 4,
  };
  const getStatusPriority = (value = "") => {
    const key = String(value ?? "").trim().toLowerCase();
    return Number.isFinite(statusPriority[key]) ? statusPriority[key] : 9;
  };

  return [
    ...activeTasks.map((task) => ({
      id: `task:${task.id}`,
      itemType: "task",
      status: task?.status ?? null,
      statusBucket: normalizeStatus(task?.status),
      label: summarizeTaskLabel(task, references),
      content: summarizeTaskLabel(task, references),
      detail: buildTaskReferenceDetail(task, references),
      groupKey: task?.type ?? "task",
      taskType: task?.type ?? null,
    })),
    ...activeObjectives
      .filter((objective) => !taskObjectiveIds.has(objective?.id))
      .map((objective) => ({
        id: `objective:${objective.id}`,
        itemType: "objective",
        status: objective?.status ?? null,
        statusBucket: normalizeStatus(objective?.status),
        label: summarizeObjectiveLabel(objective),
        content: summarizeObjectiveLabel(objective),
        detail: objective?.kind ? humanizeKey(objective.kind).replace(/\b\w/g, (char) => char.toUpperCase()) : null,
        groupKey: objective?.kind ?? "objective",
        objectiveKind: objective?.kind ?? null,
      })),
    ...activeQuestions
      .filter((question) => !taskQuestionIds.has(question?.id) && !objectiveQuestionIds.has(question?.id))
      .map((question) => ({
        id: `question:${question.id}`,
        itemType: "question",
        status: question?.status ?? null,
        statusBucket: normalizeStatus(question?.status),
        label: summarizeQuestionLabel(question),
        content: summarizeQuestionLabel(question),
        detail: question?.kind ? humanizeKey(question.kind).replace(/\b\w/g, (char) => char.toUpperCase()) : null,
        groupKey: question?.kind ?? "question",
        questionKind: question?.kind ?? null,
      })),
  ].sort((left, right) => {
    const typePriority = { task: 0, objective: 1, question: 2 };
    const leftType = Number.isFinite(typePriority[left.itemType]) ? typePriority[left.itemType] : 9;
    const rightType = Number.isFinite(typePriority[right.itemType]) ? typePriority[right.itemType] : 9;
    if (leftType !== rightType) return leftType - rightType;
    const leftStatus = getStatusPriority(left.status);
    const rightStatus = getStatusPriority(right.status);
    if (leftStatus !== rightStatus) return leftStatus - rightStatus;
    return String(left.label ?? "").localeCompare(String(right.label ?? ""));
  });
}

function getLifecycleTimestampMs(entry = {}) {
  const numericCandidates = [
    entry?.started_at_ms,
    entry?.completed_at_ms,
    entry?.updated_at_ms,
  ]
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0);
  if (numericCandidates.length) {
    return Math.max(...numericCandidates);
  }
  const dateCandidates = [
    entry?.completed_at,
    entry?.started_at,
    entry?.updated_at,
    entry?.updatedAt,
    entry?.created_at,
    entry?.createdAt,
  ]
    .map((value) => (value ? new Date(value).getTime() : NaN))
    .filter((value) => Number.isFinite(value) && value > 0);
  return dateCandidates.length ? Math.max(...dateCandidates) : null;
}

function formatRelativeAge(timestampMs = null) {
  if (!Number.isFinite(Number(timestampMs)) || Number(timestampMs) <= 0) {
    return null;
  }
  const deltaMs = Math.max(0, Date.now() - Number(timestampMs));
  if (deltaMs < 15000) return "just now";
  if (deltaMs < 60000) return `${Math.round(deltaMs / 1000)}s ago`;
  if (deltaMs < 3600000) return `${Math.round(deltaMs / 60000)}m ago`;
  return `${Math.round(deltaMs / 3600000)}h ago`;
}

function summarizeQuestionLifecycle(question = {}, linkedTasks = [], linkedObjectives = []) {
  const normalizedTaskStatuses = linkedTasks.map((task) => normalizeStatus(task?.status));
  const runningTasks = linkedTasks.filter((task) => normalizeStatus(task?.status) === "active");
  const queuedTasks = linkedTasks.filter((task) => normalizeStatus(task?.status) === "open");
  const activeObjectives = linkedObjectives.filter((objective) => {
    if (normalizeStatus(objective?.status) !== "active") {
      return false;
    }
    return String(objective?.status_reason ?? "").trim().toLowerCase() !== "awaiting_upstream_evidence";
  });
  const openObjectives = linkedObjectives.filter((objective) => normalizeStatus(objective?.status) === "open");
  const latestActivityMs = [
    ...linkedTasks.map((task) => getLifecycleTimestampMs(task)),
    ...linkedObjectives.map((objective) => getLifecycleTimestampMs(objective)),
    getLifecycleTimestampMs(question),
  ]
    .filter((value) => Number.isFinite(Number(value)) && Number(value) > 0)
    .sort((left, right) => Number(right) - Number(left))[0] ?? null;
  const latestActivityAge = formatRelativeAge(latestActivityMs);
  const recentActivityMs = latestActivityMs ? Math.max(0, Date.now() - Number(latestActivityMs)) : Number.POSITIVE_INFINITY;
  const synthesizingNow =
    runningTasks.length === 0 &&
    queuedTasks.length === 0 &&
    activeObjectives.length > 0 &&
    recentActivityMs <= 3 * 60 * 1000;

  let engineStateLabel = "No linked work";
  let engineStateTone = "slate";
  if (runningTasks.length > 0) {
    engineStateLabel = runningTasks.length === 1
      ? `Running ${titleizeKey(runningTasks[0]?.type ?? "task")}`
      : `${runningTasks.length} tasks running`;
    engineStateTone = "blue";
  } else if (queuedTasks.length > 0) {
    engineStateLabel = queuedTasks.length === 1 ? "1 queued task" : `${queuedTasks.length} queued tasks`;
    engineStateTone = "amber";
  } else if (synthesizingNow) {
    engineStateLabel = activeObjectives.length === 1
      ? `Synthesizing ${titleizeKey(activeObjectives[0]?.kind ?? "objective")}`
      : `${activeObjectives.length} objectives synthesizing`;
    engineStateTone = "violet";
  } else if (activeObjectives.length > 0) {
    engineStateLabel = activeObjectives.length === 1
      ? `Objective active, no task running`
      : `${activeObjectives.length} active objectives, no task running`;
    engineStateTone = "slate";
  } else if (openObjectives.length > 0 || normalizedTaskStatuses.includes("open")) {
    engineStateLabel = "Waiting on queued work";
    engineStateTone = "amber";
  }

  return {
    runningTaskCount: runningTasks.length,
    queuedTaskCount: queuedTasks.length,
    activeObjectiveCount: activeObjectives.length,
    openObjectiveCount: openObjectives.length,
    linkedTaskCount: linkedTasks.length,
    linkedObjectiveCount: linkedObjectives.length,
    latestActivityMs,
    latestActivityAge,
    synthesizingNow,
    engineStateLabel,
    engineStateTone,
  };
}

function summarizeTaskLifecycle(task = {}, linkedObjective = null, linkedQuestion = null) {
  const normalizedStatus = normalizeStatus(task?.status);
  const objectiveStatus = normalizeStatus(linkedObjective?.status);
  const latestActivityMs = getLifecycleTimestampMs(task) ?? getLifecycleTimestampMs(linkedObjective) ?? getLifecycleTimestampMs(linkedQuestion);
  const latestActivityAge = formatRelativeAge(latestActivityMs);
  let engineStateLabel = "Idle";
  let engineStateTone = "slate";
  if (normalizedStatus === "active") {
    engineStateLabel = "Running now";
    engineStateTone = "blue";
  } else if (normalizedStatus === "open") {
    engineStateLabel = objectiveStatus === "active" ? "Queued under active objective" : "Queued";
    engineStateTone = "amber";
  } else if (normalizedStatus === "deferred") {
    engineStateLabel = "Deferred";
    engineStateTone = "slate";
  } else if (normalizedStatus === "blocked") {
    engineStateLabel = "Blocked";
    engineStateTone = "rose";
  } else if (normalizedStatus === "closed" || normalizedStatus === "skipped") {
    engineStateLabel = "Settled";
    engineStateTone = "emerald";
  }
  return {
    latestActivityMs,
    latestActivityAge,
    objectiveStatus,
    engineStateLabel,
    engineStateTone,
  };
}

function applyLiveExecutionStateToQuestions(questions = [], liveExecutionState = null) {
  const activeQuestionIds = liveExecutionState?.activeQuestionIds ?? new Set();
  return toArray(questions).map((question) => {
    const questionId = String(question?.id ?? "").trim();
    if (!questionId || !activeQuestionIds.has(questionId)) {
      return question;
    }
    const lifecycle = question?.lifecycle ?? {};
    const hasRunningTask = Number(lifecycle.runningTaskCount ?? 0) > 0;
    return {
      ...question,
      kanbanStatus: hasRunningTask ? "active" : "synthesizing",
      lifecycle: {
        ...lifecycle,
        engineStateLabel: hasRunningTask ? (lifecycle.engineStateLabel ?? "Running now") : "Live engine activity",
        engineStateTone: hasRunningTask ? (lifecycle.engineStateTone ?? "blue") : "violet",
      },
    };
  });
}

function applyLiveExecutionStateToTasks(tasks = [], liveExecutionState = null) {
  const activeTaskIds = liveExecutionState?.activeTaskIds ?? new Set();
  const queuedTaskIds = liveExecutionState?.queuedTaskIds ?? new Set();
  return toArray(tasks).map((task) => {
    const taskId = String(task?.id ?? "").trim();
    if (!taskId) {
      return task;
    }
    const lifecycle = task?.lifecycle ?? {};
    if (activeTaskIds.has(taskId)) {
      return {
        ...task,
        kanbanStatus: "active",
        lifecycle: {
          ...lifecycle,
          engineStateLabel: "Running now",
          engineStateTone: "blue",
        },
      };
    }
    if (queuedTaskIds.has(taskId) && normalizeStatus(task?.kanbanStatus ?? task?.status) === "open") {
      return {
        ...task,
        lifecycle: {
          ...lifecycle,
          engineStateLabel: "Queued in current run",
          engineStateTone: "amber",
        },
      };
    }
    return task;
  });
}

function buildTopicSelectionSummary(entry = null) {
  const candidates = toArray(entry?.metadata?.candidates);
  const resolution = entry?.metadata?.resolution ?? null;
  const selectedIds = toArray(resolution?.selectedTopicIds);
  const selectedAggregateIds = toArray(resolution?.selectedAggregateIds);
  const selected = selectedIds
    .map((topicId) => candidates.find((candidate) => candidate?.topic_id === topicId))
    .filter(Boolean);
  const selectedAggregates = selectedAggregateIds
    .map((aggregateId) => candidates.find((candidate) => candidate?.aggregate_id === aggregateId))
    .filter(Boolean);
  return {
    action: resolution?.action ?? null,
    selected,
    selectedIds,
    selectedAggregates,
    selectedAggregateIds,
  };
}

function normalizeActiveWorkDisplayItem(entry = {}) {
  if (entry?.itemType) {
    return {
      id: entry.id ?? `active_${Math.random().toString(36).slice(2)}`,
      itemType: entry.itemType,
      status: entry.status ?? null,
      statusBucket: entry.statusBucket ?? null,
      label: entry.label ?? "",
      content: entry.label ?? entry.content ?? "",
      detail: entry.detail ?? null,
      groupKey: entry.groupKey ?? null,
      taskType: entry.taskType ?? null,
      objectiveKind: entry.objectiveKind ?? null,
      questionKind: entry.questionKind ?? null,
    };
  }
  const normalized = normalizeActivityItem(entry, entry?.kind ?? "update");
  return {
    ...normalized,
    itemType: "activity",
    label: normalized.content,
    detail: null,
    groupKey: normalized.kind ?? null,
  };
}

function deriveLiveExecutionState(activities = [], questions = [], tasks = [], objectives = []) {
  const recentActivities = toArray(activities)
    .map((entry) => normalizeActivityItem(entry, entry?.kind ?? "update"))
    .filter((entry) => {
      const timestamp = new Date(entry.createdAt ?? entry.updatedAt ?? 0).getTime();
      return Number.isFinite(timestamp) && Date.now() - timestamp <= 10 * 60 * 1000;
    });
  const taskToQuestionId = new Map(
    toArray(tasks)
      .filter((task) => task?.id)
      .map((task) => [task.id, task?.research_question_id ?? task?.question_id ?? null]),
  );
  const objectiveToQuestionId = new Map(
    toArray(objectives)
      .filter((objective) => objective?.id)
      .map((objective) => [objective.id, objective?.research_question_id ?? null]),
  );
  const activeTaskIds = new Set();
  const activeObjectiveIds = new Set();
  const activeQuestionIds = new Set();
  const queuedTaskIds = new Set();

  recentActivities.forEach((activity) => {
    if (activity.kind === "task_started" && activity.taskId) {
      activeTaskIds.add(activity.taskId);
      if (activity.objectiveId) activeObjectiveIds.add(activity.objectiveId);
      const questionId =
        activity.researchQuestionId ??
        taskToQuestionId.get(activity.taskId) ??
        objectiveToQuestionId.get(activity.objectiveId);
      if (questionId) activeQuestionIds.add(questionId);
    } else if (activity.kind === "task_completed" && activity.taskId) {
      activeTaskIds.delete(activity.taskId);
    } else if (activity.kind === "task_enqueued" && activity.taskId) {
      queuedTaskIds.add(activity.taskId);
      if (activity.objectiveId) activeObjectiveIds.add(activity.objectiveId);
      const questionId =
        activity.researchQuestionId ??
        taskToQuestionId.get(activity.taskId) ??
        objectiveToQuestionId.get(activity.objectiveId);
      if (questionId) activeQuestionIds.add(questionId);
    } else if (activity.kind === "objective_opened" && activity.objectiveId) {
      activeObjectiveIds.add(activity.objectiveId);
      const questionId = activity.researchQuestionId ?? objectiveToQuestionId.get(activity.objectiveId);
      if (questionId) activeQuestionIds.add(questionId);
    } else if (activity.kind === "objective_closed" && activity.objectiveId) {
      activeObjectiveIds.delete(activity.objectiveId);
    }
  });

  return {
    activeTaskIds,
    queuedTaskIds,
    activeObjectiveIds,
    activeQuestionIds,
  };
}

function buildLastMessage(history = []) {
  const lastEntry = Array.isArray(history) ? history[history.length - 1] : null;
  return lastEntry?.content ? truncateText(lastEntry.content, 80) : null;
}

function hydrateFinalResultEntry(entry = {}, primitive = null) {
  if (entry?.kind !== "final_result") {
    return entry;
  }
  const primitiveStructuredSummary = cloneJson(primitive?.referenceParameters?.structured_summary) ?? null;
  const primitiveSummary = primitive?.referenceParameters?.summary ?? null;
  const structuredSummary = cloneJson(entry?.metadata?.structuredSummary) ?? primitiveStructuredSummary;
  const plainSummary = entry?.metadata?.plainSummary ?? primitiveSummary ?? entry?.content ?? "";
  return {
    ...entry,
    content: plainSummary,
    metadata: {
      ...(entry?.metadata ?? {}),
      plainSummary,
      structuredSummary,
    },
  };
}

function findPendingEntry(history = []) {
  return [...(Array.isArray(history) ? history : [])]
    .reverse()
    .find((entry) => entry?.metadata?.pending);
}

function normalizeResearchChat(researchChat = null) {
  const conversations = Object.fromEntries(
    Object.entries(researchChat?.conversations ?? {}).filter(([, conversation]) => conversation?.id),
  );
  return {
    activeConversationId: researchChat?.activeConversationId ?? Object.keys(conversations)[0] ?? null,
    conversations,
  };
}

function getConversation(researchChat = null, conversationId = null) {
  const normalized = normalizeResearchChat(researchChat);
  const resolvedId = conversationId ?? normalized.activeConversationId ?? null;
  return resolvedId ? normalized.conversations[resolvedId] ?? null : null;
}

function updateConversationSnapshot(researchChat = null, conversation = null) {
  const normalized = normalizeResearchChat(researchChat);
  if (!conversation?.id) {
    return normalized;
  }
  normalized.conversations[conversation.id] = cloneJson(conversation) ?? conversation;
  if (!normalized.activeConversationId) {
    normalized.activeConversationId = conversation.id;
  }
  return normalized;
}

function markPendingConversationEntryResolved(conversation = null, pendingId = null, resolution = {}) {
  if (!conversation?.id) {
    return conversation;
  }
  const nextConversation = cloneJson(conversation) ?? {};
  nextConversation.history = toArray(nextConversation.history).map((entry) => {
    if (!entry?.metadata?.pending) return entry;
    if (pendingId && entry.id !== pendingId) return entry;
    return {
      ...entry,
      metadata: {
        ...(entry.metadata ?? {}),
        pending: false,
        resolvedAt: new Date().toISOString(),
        resolution: cloneJson(resolution) ?? {},
      },
    };
  });
  nextConversation.updatedAt = new Date().toISOString();
  return nextConversation;
}

const AGGREGATE_POLICY_KEYS = new Set(["upvoted", "downvoted", "followed", "hidden"]);

function normalizeAggregatePolicyKey(value = "") {
  const normalized = String(value ?? "").trim().toLowerCase();
  return AGGREGATE_POLICY_KEYS.has(normalized) ? normalized : null;
}

function setPrimitiveResearchState(primitive = null, nextState = null) {
  if (!primitive || !nextState) {
    return false;
  }
  const clonedState = cloneJson(nextState) ?? {};
  if (typeof primitive.setField === "function") {
    primitive.setField("referenceParameters.research_state", clonedState);
  } else {
    primitive.referenceParameters ||= {};
    primitive.referenceParameters.research_state = clonedState;
  }
  return true;
}

function setPrimitiveResearchClaims(primitive = null, nextClaims = null) {
  if (!primitive || !Array.isArray(nextClaims)) {
    return false;
  }
  const clonedClaims = cloneJson(nextClaims) ?? [];
  if (typeof primitive.setField === "function") {
    primitive.setField("referenceParameters.research_claims", clonedClaims);
  } else {
    primitive.referenceParameters ||= {};
    primitive.referenceParameters.research_claims = clonedClaims;
  }
  return true;
}

function setPrimitiveResearchChat(primitive = null, nextResearchChat = null) {
  if (!primitive || !nextResearchChat) {
    return false;
  }
  const clonedResearchChat = cloneJson(nextResearchChat) ?? {};
  if (typeof primitive.setField === "function") {
    primitive.setField("referenceParameters.research_chat", clonedResearchChat);
  } else {
    primitive.referenceParameters ||= {};
    primitive.referenceParameters.research_chat = clonedResearchChat;
  }
  return true;
}

function applyTopicSelectionToPrimitive(primitive = null, conversationId = null, aggregateIds = []) {
  if (!primitive) {
    return false;
  }
  const researchState = buildLocalResearchState(primitive);
  const researchChat = normalizeResearchChat(primitive?.referenceParameters?.research_chat);
  const conversation = getConversation(researchChat, conversationId ?? researchChat.activeConversationId ?? null);
  if (!conversation) {
    return false;
  }
  const pendingEntry = toArray(conversation.history)
    .slice()
    .reverse()
    .find((entry) => entry?.metadata?.pending === true && entry?.metadata?.pendingType === "topic_selection");
  if (!pendingEntry) {
    return false;
  }
  const selectedAggregateIds = uniqueStrings(aggregateIds);
  const selectedTopicIds = uniqueStrings(
    selectedAggregateIds.flatMap((aggregateId) => {
      const aggregate = toArray(researchState?.topic_hint_aggregates).find(
        (entry) => (entry?.aggregateId ?? entry?.aggregate_id) === aggregateId,
      );
      return toArray(aggregate?.topicIds ?? aggregate?.topic_ids);
    }),
  );
  researchState.user_topic_policies ||= {};
  selectedAggregateIds.forEach((aggregateId) => {
    researchState.user_topic_policies[aggregateId] = {
      ...(researchState.user_topic_policies?.[aggregateId] ?? {}),
      followed: true,
      updated_at: new Date().toISOString(),
    };
  });
  const resolvedConversation = markPendingConversationEntryResolved(conversation, pendingEntry.id, {
    action: "selected_topics",
    selectedAggregateIds,
    selectedTopicIds,
  });
  const nextResearchChat = updateConversationSnapshot(researchChat, resolvedConversation);
  setPrimitiveResearchState(primitive, researchState);
  setPrimitiveResearchChat(primitive, nextResearchChat);
  return true;
}

function applyTopicPolicyToPrimitive(primitive = null, aggregateId = null, policy = null, enabled = true) {
  if (!primitive) {
    return false;
  }
  const normalizedAggregateId = String(aggregateId ?? "").trim();
  const normalizedPolicy = normalizeAggregatePolicyKey(policy);
  if (!normalizedAggregateId || !normalizedPolicy) {
    return false;
  }
  const researchState = buildLocalResearchState(primitive);
  researchState.user_topic_policies ||= {};
  const current = {
    ...(researchState.user_topic_policies?.[normalizedAggregateId] ?? {}),
  };
  if (enabled) {
    current[normalizedPolicy] = true;
    if (normalizedPolicy === "upvoted") current.downvoted = false;
    if (normalizedPolicy === "downvoted") current.upvoted = false;
  } else {
    delete current[normalizedPolicy];
  }
  current.updated_at = new Date().toISOString();
  researchState.user_topic_policies[normalizedAggregateId] = Object.fromEntries(
    Object.entries(current).filter(([, value]) => value !== undefined),
  );
  setPrimitiveResearchState(primitive, researchState);
  return true;
}

function summarizeConversationList(researchChat = null) {
  const normalized = normalizeResearchChat(researchChat);
  return Object.values(normalized.conversations)
    .map((conversation) => {
      const pendingEntry = findPendingEntry(conversation?.history);
      const lastEntry = toArray(conversation?.history).slice(-1)[0] ?? null;
      return {
        id: conversation.id,
        title: conversation.title,
        kind: conversation.kind,
        scope: conversation.scope,
        createdAt: conversation.createdAt,
        updatedAt: conversation.updatedAt,
        messageCount: toArray(conversation.history).length,
        lastMessage: lastEntry ? truncateText(lastEntry.content, 120) : null,
        hasPending: Boolean(pendingEntry),
      };
    })
    .sort((left, right) => String(right.updatedAt ?? "").localeCompare(String(left.updatedAt ?? "")));
}

function getResearchRuntimeSnapshot(state = {}) {
  const runtime =
    state?.runtime && typeof state.runtime === "object" && !Array.isArray(state.runtime)
      ? state.runtime
      : {};
  const updatedAtMs = runtime?.updated_at ? new Date(runtime.updated_at).getTime() : null;
  return {
    phase: runtime?.phase ?? null,
    updatedAtMs: Number.isFinite(updatedAtMs) ? updatedAtMs : null,
    activeTaskIds: new Set(uniqueStrings(toArray(runtime?.active_task_ids))),
    queuedTaskIds: new Set(uniqueStrings(toArray(runtime?.queued_task_ids))),
    activeObjectiveIds: new Set(uniqueStrings(toArray(runtime?.active_objective_ids))),
    activeQuestionIds: new Set(uniqueStrings(toArray(runtime?.active_question_ids))),
  };
}

function getResearchRuntimeV3Snapshot(state = {}) {
  const runtime =
    state?.research_runtime_v3 && typeof state.research_runtime_v3 === "object" && !Array.isArray(state.research_runtime_v3)
      ? state.research_runtime_v3
      : null;
  if (!runtime) {
    const agentRuns = toArray(state?.agent_runs);
    const activeUnits = agentRuns.filter((agent) => normalizeAgentLifecycleStatus(agent?.status) === "running");
    const queuedUnits = agentRuns.filter((agent) => {
      const lifecycle = normalizeAgentLifecycleStatus(agent?.status);
      return lifecycle === "ready" || lifecycle === "awaiting_review";
    });
    const phase = state?.research_phase ?? null;
    const inferredStatus =
      phase === "completed"
        ? "completed"
        : activeUnits.length > 0 || queuedUnits.length > 0
          ? "running"
          : phase ?? null;
    return {
      status: inferredStatus,
      phase,
      updatedAtMs: null,
      activeUnits,
      queuedUnits,
      activeUnitCount: activeUnits.length,
      queuedUnitCount: queuedUnits.length,
      progressCounters: {},
      budgetUsage: {},
      currentUnitId: state?.current_unit_id ?? state?.current_agent_id ?? null,
      currentFocusArea: state?.current_focus_area ?? null,
      currentSection: state?.current_section ?? null,
      recentRuntimeEvents: [],
    };
  }
  return {
    status: runtime?.status ?? null,
    phase: runtime?.active_phase ?? null,
    updatedAtMs: parseTimestampMs(runtime?.updated_at),
    activeUnits: toArray(runtime?.active_units),
    queuedUnits: toArray(runtime?.queued_units),
    activeUnitCount: toArray(runtime?.active_units).length,
    queuedUnitCount: toArray(runtime?.queued_units).length,
    progressCounters:
      runtime?.progress_counters && typeof runtime.progress_counters === "object"
        ? runtime.progress_counters
        : {},
    budgetUsage:
      runtime?.budget_usage && typeof runtime.budget_usage === "object"
        ? runtime.budget_usage
        : {},
    currentUnitId: runtime?.current_unit_id ?? null,
    currentFocusArea: runtime?.current_focus_area ?? null,
    currentSection: runtime?.current_section ?? null,
    recentRuntimeEvents: toArray(runtime?.recent_runtime_events),
  };
}

function getAgentRuntimeSnapshot(state = {}) {
  const runtime =
    state?.agent_runtime && typeof state.agent_runtime === "object" && !Array.isArray(state.agent_runtime)
      ? state.agent_runtime
      : null;
  if (runtime) {
    return runtime;
  }
  const agentRuns = toArray(state?.agent_runs);
  const activeAgents = agentRuns.filter((agent) => normalizeAgentLifecycleStatus(agent?.status) === "running");
  const awaitingReviewAgents = agentRuns.filter((agent) => normalizeAgentLifecycleStatus(agent?.status) === "awaiting_review");
  const readyAgents = agentRuns.filter((agent) => normalizeAgentLifecycleStatus(agent?.status) === "ready");
  const blockedAgents = agentRuns.filter((agent) => normalizeAgentLifecycleStatus(agent?.status) === "blocked");
  const completedAgents = agentRuns.filter((agent) => normalizeAgentLifecycleStatus(agent?.status) === "completed");
  return {
    status:
      state?.research_phase === "completed"
        ? "completed"
        : activeAgents.length > 0 || awaitingReviewAgents.length > 0 || readyAgents.length > 0
          ? "running"
          : state?.research_phase ?? null,
    current_agent_id: state?.current_agent_id ?? null,
    active_agents: activeAgents,
    awaiting_review_agents: awaitingReviewAgents,
    ready_agents: readyAgents,
    blocked_agents: blockedAgents,
    completed_agents: completedAgents,
  };
}

function normalizeAgentDisplayText(value = "") {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function isRedundantQuestionWrapperAgent(agent = {}, allAgents = []) {
  return false;
}

function isRedundantQuestionWrapperNode(node = {}, parentNode = null) {
  return false;
}

function getVisibleAgentRuns(agentRuns = [], { includeRoot = false } = {}) {
  const canonicalRuns = toArray(agentRuns).filter((agent) => {
    if (!agent || typeof agent !== "object") {
      return false;
    }
    const agentId = String(agent?.id ?? "").trim();
    if (!agentId) {
      return false;
    }
    if (agentId === "agent:root") {
      return includeRoot;
    }
    if (agentId.startsWith("display-group:")) {
      return false;
    }
    return Boolean(
      agent?.kind ||
      agent?.scope_type ||
      agent?.status ||
      agent?.work_package ||
      agent?.execution,
    );
  });
  return canonicalRuns.filter((agent) => {
    if (!includeRoot && agent?.id === "agent:root") {
      return false;
    }
    return true;
  });
}

function shouldIncludeCoordinatorAgent(runtimeV3 = {}, agentRuntime = {}, state = {}) {
  const currentAgentId = String(
    agentRuntime?.current_agent_id ??
    runtimeV3?.currentAgentId ??
    runtimeV3?.current_agent_id ??
    state?.current_agent_id ??
    "",
  ).trim();
  if (currentAgentId === "agent:root") {
    return true;
  }
  const runtimeAgents = [
    ...toArray(agentRuntime?.active_agents),
    ...toArray(agentRuntime?.awaiting_review_agents),
    ...toArray(state?.agent_runs),
  ];
  const rootAgent = runtimeAgents.find((agent) => agent?.id === "agent:root");
  if (!rootAgent) {
    return false;
  }
  const rootChildAgentIds = uniqueStrings(toArray(rootAgent?.child_agent_ids));
  const hasDirectRootChildren =
    rootChildAgentIds.length > 0 ||
    toArray(state?.agent_runs).some((agent) => String(agent?.parent_agent_id ?? "").trim() === "agent:root");
  if (hasDirectRootChildren) {
    return true;
  }
  const rootStatus = String(rootAgent?.status ?? "").trim().toLowerCase();
  return ["running", "awaiting_review", "awaiting_children", "ready", "ready_to_reason", "blocked", "deferred"].includes(rootStatus);
}

function isFreshActiveSubRun(agentThread = {}, runtime = {}, nowMs = Date.now()) {
  if (!agentThread || typeof agentThread !== "object") {
    return false;
  }
  const activeTaskCount = Number(agentThread?.coverage?.active_task_count ?? 0);
  if (activeTaskCount > 0 || hasLiveTaskExecutionSignal(agentThread?.current_action)) {
    return true;
  }
  const statusText = String(agentThread?.status_text ?? agentThread?.display_label ?? "").trim().toLowerCase();
  const looksLikeSchedulingPlaceholder = statusText.includes("scheduling next step");
  const agentThreadFreshnessMs = Math.max(
    parseTimestampMs(agentThread?.updated_at) ?? 0,
    parseTimestampMs(agentThread?.started_at) ?? 0,
  );
  const freshnessMs = agentThreadFreshnessMs || (Number(runtime?.updatedAtMs ?? 0) || 0);
  if (!freshnessMs) {
    return !looksLikeSchedulingPlaceholder && normalizeStatus(agentThread?.status) === "active";
  }
  const isFresh = nowMs - freshnessMs <= ACTIVE_WORK_SCHEDULING_FRESH_MS;
  if (looksLikeSchedulingPlaceholder) {
    return isFresh;
  }
  return normalizeStatus(agentThread?.status) === "active" && isFresh;
}

function summarizeWorkspaceStatusFromState(state = {}, claims = []) {
  const questions = toArray(state?.research_questions);
  const tasks = toArray(state?.research_tasks);
  const runtime = getResearchRuntimeSnapshot(state);
  const runtimeV3 = getResearchRuntimeV3Snapshot(state);
  return {
    hasResearchState: Boolean(state && typeof state === "object" && Object.keys(state).length > 0),
    phase: runtimeV3.phase ?? runtime.phase ?? state?.research_phase ?? null,
    turnIndex: Number(state?.turn_index ?? 0),
    claimCount: claims.length,
    questionCount: questions.length,
    openQuestionCount: questions.filter((entry) => !["answered", "satisfied", "exhausted", "failed"].includes(String(entry?.status ?? "").toLowerCase())).length,
    taskCount: tasks.length,
    queuedTaskCount:
      runtime.queuedTaskIds.size ||
      tasks.filter((entry) => String(entry?.status ?? "").toLowerCase() === "queued").length,
    runningTaskCount:
      runtimeV3.activeUnitCount ||
      runtime.activeTaskIds.size ||
      tasks.filter((entry) => String(entry?.status ?? "").toLowerCase() === "running").length,
    queuedUnitCount: runtimeV3.queuedUnitCount,
    runtimeStatus: runtimeV3.status ?? null,
  };
}

function summarizeTabCountsFromState(state = {}, claims = []) {
  const sources = toArray(state?.sources);
  const runtimeV3 = getResearchRuntimeV3Snapshot(state);
  return {
    evidence: {
      claims: claims.length,
      sources: sources.length,
    },
    graph: {
      claims: claims.length,
      entities: toArray(state?.entities).length,
    },
    questions: {
      total: toArray(state?.research_questions).length,
    },
    tasks: {
      total: toArray(state?.research_tasks).length,
    },
    runtime: {
      totalUnits: toArray(state?.planned_agent_units).length,
      totalBlueprints: toArray(state?.agent_blueprints).length,
      activeUnits: runtimeV3.activeUnitCount,
      queuedUnits: runtimeV3.queuedUnitCount,
    },
  };
}

function buildSubAgentsSnapshot(state = {}) {
  const runtimeV3 = getResearchRuntimeV3Snapshot(state);
  const agentRuntime = getAgentRuntimeSnapshot(state);
  const includeCoordinatorAgent = shouldIncludeCoordinatorAgent(runtimeV3, agentRuntime, state);
  const questionSnapshot = buildQuestionsSnapshot(state, null);
  const taskSnapshot = buildTasksSnapshot(state, null);
  const canonicalAgentRuns = getVisibleAgentRuns(toArray(state?.agent_runs), { includeRoot: includeCoordinatorAgent });
  const canonicalAgentRunById = new Map(canonicalAgentRuns.map((agent) => [String(agent?.id ?? ""), agent]));
  const pruneHierarchyToVisibleRuns = (nodes = []) =>
    toArray(nodes).flatMap((node) => {
      if (!node || typeof node !== "object") return [];
      const nodeId = String(node?.id ?? "");
      const childItems = pruneHierarchyToVisibleRuns(node?.child_items);
      if (canonicalAgentRunById.has(nodeId)) {
        return [{ ...node, child_items: childItems }];
      }
      return childItems;
    });
  const visibleAgentHierarchy = pruneHierarchyToVisibleRuns(state?.agent_hierarchy);
  const plannedAgentUnits = canonicalAgentRuns
    .map((agent) => ({
      ...agent,
      status: normalizeAgentLifecycleStatus(agent?.status),
    }))
    .sort((left, right) =>
      String(left?.display_label ?? left?.scope_key ?? left?.id ?? "").localeCompare(
        String(right?.display_label ?? right?.scope_key ?? right?.id ?? ""),
      ),
    );
  const activeAgentThreads = plannedAgentUnits.filter((unit) => normalizeAgentLifecycleStatus(unit?.status) === "running");
  const queuedAgentThreads = plannedAgentUnits.filter((unit) => {
    const lifecycle = normalizeAgentLifecycleStatus(unit?.status);
    return lifecycle === "ready" || lifecycle === "awaiting_review";
  });
  const completedAgentThreads = plannedAgentUnits.filter((unit) => normalizeAgentLifecycleStatus(unit?.status) === "completed");
  const blockedAgentThreads = plannedAgentUnits.filter((unit) => normalizeAgentLifecycleStatus(unit?.status) === "blocked");
  // Build a simple agent flow graph for the Flow Diagram tab
  const agentFlowGraph = (() => {
    const graphNodes = [];
    const graphEdges = [];
    const lineageEdges = [];
    const seenNodeIds = new Set();
    const seenEdgeIds = new Set();

    const awaitingReviewIds = new Set(
      canonicalAgentRuns
        .filter((a) => normalizeAgentLifecycleStatus(a?.status) === "awaiting_review" && String(a?.node_role ?? "").trim() === "objective" && a?.reviewable === true)
        .map((a) => String(a?.id ?? "").trim())
        .filter(Boolean),
    );

    const visibleAgents = canonicalAgentRuns.filter((agent) => {
      const agentId = String(agent?.id ?? "").trim();
      if (!agentId) return false;
      const parentId = String(agent?.parent_agent_id ?? "").trim();
      if (parentId && awaitingReviewIds.has(parentId)) return false;
      return true;
    });

    const visibleAgentIds = new Set(visibleAgents.map((a) => String(a?.id ?? "").trim()).filter(Boolean));
    const agentById = new Map(visibleAgents.map((a) => [String(a?.id ?? "").trim(), a]).filter(([id]) => id));

    // Build claim lookup
    const claimsById = new Map(toArray(state?.claims).filter((c) => c?.id).map((c) => [c.id, c]));

    // Helper: find tasks linked to an agent
    const tasksByAgentId = new Map();
    toArray(taskSnapshot?.tasks).forEach((task) => {
      const agentId = String(task?.unit_id ?? task?.agent_thread_id ?? task?.agent_id ?? "").trim();
      if (!agentId) return;
      if (!tasksByAgentId.has(agentId)) tasksByAgentId.set(agentId, []);
      tasksByAgentId.get(agentId).push(task);
    });

    // Helper: count claims per agent
    const claimCounts = new Map();
    visibleAgents.forEach((agent) => {
      const agentId = String(agent?.id ?? "").trim();
      let count = (agent?.produced_claim_ids ?? []).length;
      toArray(state?.claims).forEach((claim) => {
        if (
          toArray(claim?.originating_agent_ids).includes(agentId) ||
          toArray(claim?.originating_unit_ids).includes(agentId) ||
          toArray(claim?.originating_agent_thread_ids).includes(agentId)
        ) {
          count += 1;
        }
      });
      claimCounts.set(agentId, count);
    });

    const humanizeLabel = (value = "", fallback = "Untitled") => {
      const text = String(value ?? "").trim();
      if (!text) return fallback;
      return text.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    };

    const getRoleLabel = (agent) => {
      if (agent?.node_role === "root") return "Coordinator";
      if (agent?.node_role === "objective") return "Objective Agent";
      if (agent?.node_role === "question") return "Question Agent";
      if (agent?.kind === "section_writer") return "Writer";
      if (agent?.kind === "frame_setting") return "Frame";
      if (agent?.kind === "selection_setting") return "Selection";
      if (agent?.kind === "synthesis_producing") return "Synthesis";
      if (agent?.kind === "evidence_producing") return "Evidence";
      if (agent?.scope_type === "section") return "Section Agent";
      if (agent?.scope_type === "focus_area") return "Focus Area";
      return "";
    };

    const getStatusLabel = (status) => {
      const s = normalizeAgentLifecycleStatus(status);
      if (s === "awaiting_review") return "Awaiting Review";
      if (s === "awaiting_children") return "Awaiting Children";
      if (s === "running") return "Active";
      if (s === "ready") return "Ready";
      if (s === "ready_to_reason") return "Ready To Reason";
      if (s === "completed") return "Completed";
      if (s === "answered") return "Answered";
      if (s === "deferred") return "Deferred";
      return "Blocked";
    };

    const getPrimaryText = (agent) => {
      const wp = agent?.work_package ?? {};
      const candidates = [
        wp?.objective,
        wp?.question,
        wp?.mission,
        agent?.label,
        agent?.display_label,
        agent?.scope_key,
        agent?.id,
      ];
      for (const c of candidates) {
        const t = String(c ?? "").trim();
        if (t) return humanizeLabel(t, t);
      }
      return "Untitled agent";
    };

    const getSecondaryText = (agent) => {
      const wp = agent?.work_package ?? {};
      const candidates = [wp?.mission, agent?.status_text];
      for (const c of candidates) {
        const t = String(c ?? "").trim();
        if (t && t !== getPrimaryText(agent)) return t;
      }
      return "";
    };

    visibleAgents.forEach((agent) => {
      const nodeId = String(agent?.id ?? "").trim();
      if (!nodeId || seenNodeIds.has(nodeId)) return;
      seenNodeIds.add(nodeId);

      const lifecycleStatus = normalizeAgentLifecycleStatus(agent?.status);
      const roleLabel = getRoleLabel(agent);
      const primaryText = getPrimaryText(agent);
      const secondaryText = getSecondaryText(agent);

      const agentTasks = tasksByAgentId.get(nodeId) ?? [];
      const activeTaskCount = agentTasks.filter((t) =>
        ["running", "active", "in_progress"].includes(String(t?.status ?? "").trim().toLowerCase()),
      ).length;
      const openTaskCount = agentTasks.filter((t) =>
        ["open", "queued", "ready", "planned"].includes(String(t?.status ?? "").trim().toLowerCase()),
      ).length;
      const claimCount = claimCounts.get(nodeId) ?? 0;

      const metaParts = [
        activeTaskCount > 0 ? `${activeTaskCount} active tasks` : null,
        openTaskCount > 0 ? `${openTaskCount} queued tasks` : null,
        claimCount > 0 ? `${claimCount} claims` : null,
      ].filter(Boolean);

      graphNodes.push({
        id: nodeId,
        title: primaryText,
        scopeLabel: roleLabel,
        lifecycleStatus,
        lifecycleLabel: getStatusLabel(agent?.status),
        summary: secondaryText,
        questionPreview: "",
        nextStep: agent?.status_text ?? "",
        meta: metaParts.join(" · "),
      });

      // Parent edge
      const parentId = String(agent?.parent_agent_id ?? "").trim();
      if (parentId && visibleAgentIds.has(parentId) && parentId !== nodeId) {
        const edgeId = `${parentId}:${nodeId}`;
        if (!seenEdgeIds.has(edgeId)) {
          seenEdgeIds.add(edgeId);
          graphEdges.push({ from: parentId, to: nodeId });
        }
      }

      // Claim edges
      const producedClaimIds = toArray(agent?.produced_claim_ids);
      producedClaimIds.forEach((claimId) => {
        const claim = claimsById.get(claimId);
        if (!claim) return;
        const claimNodeId = `claim:${claimId}`;
        if (!seenNodeIds.has(claimNodeId)) {
          seenNodeIds.add(claimNodeId);
          const confidenceValue = Number(claim?.confidence);
          const claimMetaParts = [
            claim?.type ? humanizeLabel(claim.type) : null,
            Number.isFinite(confidenceValue) ? `Confidence ${confidenceValue.toFixed(2)}` : null,
          ].filter(Boolean);
          graphNodes.push({
            id: claimNodeId,
            selectId: nodeId,
            title: String(claim?.text ?? "").trim() || claimId,
            scopeLabel: "Claim",
            lifecycleStatus: "claim",
            lifecycleLabel: "Claim",
            summary: "",
            questionPreview: "",
            nextStep: "",
            meta: claimMetaParts.join(" · "),
            width: 240,
            height: 132,
          });
        }
        const claimEdgeId = `${nodeId}:${claimNodeId}`;
        if (!seenEdgeIds.has(claimEdgeId)) {
          seenEdgeIds.add(claimEdgeId);
          graphEdges.push({ from: nodeId, to: claimNodeId });
        }
      });
    });

    return { nodes: graphNodes, edges: graphEdges, lineageEdges, reviewGateEdges: [] };
  })();

  return {
    researchRuntimeV3: cloneJson(runtimeV3) ?? null,
    runtimeV3,
    agentRuntime: cloneJson(agentRuntime) ?? null,
    plannedAgentUnits,
    agentBlueprints: cloneJson(toArray(state?.agent_blueprints)) ?? [],
    agentRuns: cloneJson(canonicalAgentRuns) ?? [],
    agentHierarchy: cloneJson(visibleAgentHierarchy) ?? [],
    sharedGraph: {
      topic_aggregates: cloneJson(toArray(state?.topic_hint_aggregates)) ?? [],
    },
    sectionPacketArtifacts: cloneJson(toArray(state?.section_packet_artifacts)) ?? [],
    renderedSectionArtifacts: cloneJson(toArray(state?.rendered_section_artifacts)) ?? [],
    renderedSections: cloneJson(toArray(state?.rendered_sections)) ?? [],
    activeAgentThreads,
    queuedAgentThreads,
    completedAgentThreads,
    blockedAgentThreads,
    claims: cloneJson(toArray(state?.claims)),
    objectives: cloneJson(toArray(state?.research_objectives)),
    questions: cloneJson(toArray(questionSnapshot?.questions)),
    tasks: cloneJson(toArray(taskSnapshot?.tasks)),
    agentFlowGraph,
  };
}

function buildLocalWorkspaceSnapshot(primitive = null, conversationId = null) {
  if (!primitive) {
    return null;
  }
  const researchChat = normalizeResearchChat(primitive?.referenceParameters?.research_chat);
  const rawActiveConversation = getConversation(researchChat, conversationId);
  const activeConversation = rawActiveConversation
    ? {
        ...rawActiveConversation,
        history: toArray(rawActiveConversation.history).map((entry) => hydrateFinalResultEntry(entry, primitive)),
      }
    : null;
  const researchState = buildLocalResearchState(primitive);
  const researchClaims = toArray(researchState.claims);
  const includeCoordinatorAgent = shouldIncludeCoordinatorAgent(
    getResearchRuntimeV3Snapshot(researchState),
    getAgentRuntimeSnapshot(researchState),
    researchState,
  );
  const canonicalAgentRuns = getVisibleAgentRuns(toArray(researchState?.agent_runs), { includeRoot: includeCoordinatorAgent });
  const canonicalAgentRunById = new Map(canonicalAgentRuns.map((agent) => [String(agent?.id ?? ""), agent]));
  const pruneHierarchyToVisibleRuns = (nodes = []) =>
    toArray(nodes).flatMap((node) => {
      if (!node || typeof node !== "object") return [];
      const nodeId = String(node?.id ?? "");
      const childItems = pruneHierarchyToVisibleRuns(node?.child_items);
      if (canonicalAgentRunById.has(nodeId)) {
        return [{ ...node, child_items: childItems }];
      }
      return childItems;
    });
  const visibleAgentHierarchy = pruneHierarchyToVisibleRuns(researchState?.agent_hierarchy);
  return {
    primitive: {
      id: primitive.id,
      title: primitive.title ?? primitive.referenceParameters?.title ?? "Research workspace",
      type: primitive.type,
      workspaceId: primitive.workspaceId,
      referenceParameters: {
        summary: primitive.referenceParameters?.summary ?? null,
        structured_summary: cloneJson(primitive.referenceParameters?.structured_summary) ?? null,
      },
    },
    researchChat: {
      activeConversationId: activeConversation?.id ?? researchChat.activeConversationId ?? null,
      conversations: summarizeConversationList(researchChat),
    },
    activeConversation: activeConversation
      ? {
          id: activeConversation.id,
          title: activeConversation.title,
          kind: activeConversation.kind,
          scope: activeConversation.scope,
          history: cloneJson(activeConversation.history) ?? [],
        }
      : null,
    liveRun: null,
    researchRuntimeV3: cloneJson(getResearchRuntimeV3Snapshot(researchState)) ?? null,
    agentRuntime: cloneJson(getAgentRuntimeSnapshot(researchState)) ?? null,
    agentBlueprints: cloneJson(toArray(researchState?.agent_blueprints)) ?? [],
    agentRuns: cloneJson(canonicalAgentRuns) ?? [],
    agentHierarchy: cloneJson(visibleAgentHierarchy) ?? [],
    sharedGraph: {
      topic_aggregates: cloneJson(toArray(researchState?.topic_hint_aggregates)) ?? [],
    },
    sectionPacketArtifacts: cloneJson(toArray(researchState?.section_packet_artifacts)) ?? [],
    renderedSectionArtifacts: cloneJson(toArray(researchState?.rendered_section_artifacts)) ?? [],
    researchMap: cloneJson(researchState?.research_map) ?? null,
    plannedAgentUnits: cloneJson(toArray(researchState?.planned_agent_units)) ?? [],
    unitQueue: cloneJson(toArray(researchState?.unit_queue)) ?? [],
    unitHierarchy: cloneJson(toArray(researchState?.unit_hierarchy)) ?? [],
    status: summarizeWorkspaceStatusFromState(researchState, researchClaims),
    tabCounts: summarizeTabCountsFromState(researchState, researchClaims),
    topicHintAggregates: cloneJson(toArray(researchState?.topic_hint_aggregates)) ?? [],
    topicHierarchySnapshot: cloneJson(researchState?.topic_hierarchy_snapshot) ?? { generated_turn: Number(researchState?.turn_index ?? 0), parents: [] },
  };
}

function getMessageTone(entry = {}) {
  if (entry.kind === "question") {
    return entry?.metadata?.pending
      ? "border-violet-200 bg-violet-50 text-violet-900"
      : "border-violet-100 bg-violet-50/60 text-violet-900";
  }
  if (entry.kind === "final_result") {
    return "border-emerald-200 bg-emerald-50 text-emerald-900";
  }
  if (entry.kind === "turn_summary") {
    return "border-sky-200 bg-sky-50 text-sky-900";
  }
  if (entry.role === "user") {
    return "border-blue-200 bg-blue-50 text-blue-900";
  }
  return "border-slate-200 bg-white text-slate-800";
}

function normalizeActivityItem(entry = {}, fallbackKind = "status") {
  return {
    id: entry.id ?? `activity_${Math.random().toString(36).slice(2)}`,
    kind: entry.kind ?? fallbackKind,
    content: entry.content ?? entry.message ?? "",
    createdAt: entry.createdAt ?? new Date().toISOString(),
    updatedAt: entry.updatedAt ?? Date.now(),
    key: entry.key ?? null,
    status: entry.status ?? null,
    turnIndex:
      entry.turnIndex !== undefined && entry.turnIndex !== null
        ? Number(entry.turnIndex)
        : entry.turn_index !== undefined && entry.turn_index !== null
          ? Number(entry.turn_index)
          : null,
    taskId: entry.taskId ?? entry.task_id ?? null,
    objectiveId: entry.objectiveId ?? entry.objective_id ?? null,
    researchQuestionId: entry.researchQuestionId ?? entry.research_question_id ?? null,
    taskType: entry.taskType ?? entry.task_type ?? null,
    sectionKey: entry.sectionKey ?? entry.section_key ?? null,
    repeatCount: Math.max(1, Number(entry.repeatCount ?? entry.repeat_count ?? 1) || 1),
  };
}

function getActivitySemanticKey(entry = {}) {
  const normalized = normalizeActivityItem(entry, entry?.kind ?? "update");
  return [
    normalized.turnIndex ?? "",
    normalized.kind ?? "",
    normalized.content ?? "",
  ].join("|");
}

function getSummaryHeadingClasses(depth = 0) {
  const level = Math.max(0, Number(depth) || 0);
  if (level <= 0) {
    return "text-2xl";
  }
  if (level === 1) {
    return "text-xl";
  }
  if (level === 2) {
    return "text-lg";
  }
  if (level === 3) {
    return "text-base";
  }
  if (level === 4) {
    return "text-sm";
  }
  return "text-xs";
}

function normalizeStructuredSummaryRoot(source = {}) {
  const structuredSummary =
    source?.referenceParameters?.structured_summary ??
    source?.metadata?.structuredSummary ??
    source?.structuredSummary ??
    source?.structured_summary;
  if (Array.isArray(structuredSummary)) {
    return structuredSummary;
  }
  if (Array.isArray(structuredSummary?.sections)) {
    return structuredSummary.sections;
  }
  return [];
}

function getPreparedSummarySections(source = null) {
  const structuredSummary = normalizeStructuredSummaryRoot(source);
  if (!Array.isArray(structuredSummary) || structuredSummary.length === 0) {
    return [];
  }
  const prepared = prepareStructuredSummary({ structuredSummary });
  return Array.isArray(prepared?.sections) ? prepared.sections : [];
}

function StructuredSummaryNode({ node, depth = 0 }) {
  if (!node || typeof node !== "object") {
    return null;
  }
  const heading = String(node.heading ?? "").trim();
  const content = String(node.content ?? "").trim();
  const subsections = Array.isArray(node.subsections) ? node.subsections : [];
  if (!heading && !content && !subsections.length) {
    return null;
  }
  const HeadingTag = `h${Math.min(depth + 1, 6)}`;
  return (
    <section className={clsx(depth > 0 ? "mt-5" : "", "space-y-2")}>
      {heading ? (
        <HeadingTag className={clsx(getSummaryHeadingClasses(depth), "font-semibold text-slate-900")}>
          {heading}
        </HeadingTag>
      ) : null}
      {content ? (
        <div className="text-sm leading-6 text-slate-700">
          <MarkdownContent content={content} compactMode={false} />
        </div>
      ) : null}
      {subsections.length ? (
        <div className="space-y-1 border-l border-slate-100 pl-4">
          {subsections.map((child, index) => (
            <StructuredSummaryNode
              key={`${heading || "section"}_${index}_${child?.heading ?? "untitled"}`}
              node={child}
              depth={depth + 1}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function StructuredSummaryView({ entry }) {
  const sections = getPreparedSummarySections(entry);
  if (!sections?.length) {
    return null;
  }
  return (
    <div className="space-y-4">
      {sections.map((section, index) => (
        <StructuredSummaryNode
          key={`${section?.heading ?? "summary"}_${index}`}
          node={section}
          depth={0}
        />
      ))}
    </div>
  );
}

function buildFinalResultPreview(entry = {}) {
  return truncateText(
    String(entry?.metadata?.plainSummary ?? entry?.content ?? "")
      .replace(/\s+/g, " ")
      .trim(),
    160,
  );
}

function SummaryPanel({
  summaryEntries = [],
  selectedEntryId = null,
  primitiveSummarySource = null,
  onSelectEntry,
  onRerender,
  rerendering = false,
}) {
  const selectedEntry =
    summaryEntries.find((entry) => entry.id === selectedEntryId) ??
    summaryEntries[summaryEntries.length - 1] ??
    null;
  const selectedEntrySections = getPreparedSummarySections(selectedEntry);
  const primitiveSections = getPreparedSummarySections(primitiveSummarySource);
  const activeSummarySource = selectedEntrySections.length ? selectedEntry : primitiveSummarySource;
  const sections = selectedEntrySections.length ? selectedEntrySections : primitiveSections;

  if (!activeSummarySource || sections.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-6 text-sm text-slate-500">
        <p>No structured summary is currently stored on this primitive.</p>
        <Button
          className="mt-4"
          color="primary"
          onPress={() => onRerender?.()}
          isLoading={rerendering}
        >
          Re-render summary
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-slate-900">Rendered summary</p>
          <p className="text-sm text-slate-500">
            Rendering only the current primitive referenceParameters.structured_summary.
          </p>
        </div>
        <Button color="primary" variant="flat" onPress={() => onRerender?.()} isLoading={rerendering}>
          Re-render summary
        </Button>
      </div>
      {summaryEntries.length > 1 ? (
        <div className="flex flex-wrap gap-2">
          {summaryEntries.map((entry, index) => {
            const isActive = entry.id === selectedEntry?.id;
            const label = entry?.metadata?.renderMode === "rerender" ? `Updated ${index + 1}` : `Summary ${index + 1}`;
            return (
              <button
                key={entry.id}
                type="button"
                onClick={() => onSelectEntry?.(entry.id)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                  isActive
                    ? "border-emerald-700 bg-emerald-700 text-white"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      ) : null}
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <StructuredSummaryView entry={activeSummarySource} />
      </div>
    </div>
  );
}

function EvidencePanel({ data, onOpenDeepDive, onUpdateTopicPolicy }) {
  const [expandedParentIds, setExpandedParentIds] = useState({});
  const [expandedAggregateIds, setExpandedAggregateIds] = useState({});

  if (!data) {
    return <p className="text-sm text-slate-500">Claims are not available yet for this conversation.</p>;
  }
  const aggregateById = new Map(
    toArray(data.aggregates)
      .filter((aggregate) => aggregate?.aggregateId)
      .map((aggregate) => [aggregate.aggregateId, aggregate]),
  );
  const allClaims = toArray(data.allClaims);
  const claimById = new Map(
    allClaims
      .filter((claim) => claim?.id)
      .map((claim) => [claim.id, claim]),
  );
  const topParents = toArray(data.hierarchy?.parents).slice(0, 5);
  const claimGroupKeyById = new Map(
    toArray(data.groups).flatMap((group) =>
      toArray(group?.claims).map((claim) => [claim?.id, group?.key]),
    ),
  );
  const topClaims = allClaims
    .map((claim) => ({ ...claim, groupKey: claimGroupKeyById.get(claim.id) ?? claim.type ?? "claim" }))
    .sort((left, right) => {
      const leftConfidence = Number(left.confidence ?? 0);
      const rightConfidence = Number(right.confidence ?? 0);
      if (leftConfidence !== rightConfidence) return rightConfidence - leftConfidence;
      return String(left.text ?? "").localeCompare(String(right.text ?? ""));
    })
    .slice(0, 48);
  const allDisplayChildThemes = topParents.flatMap((parent) => buildParentDisplayChildThemes(parent, aggregateById));
  const maxParentClaimCount = Math.max(
    1,
    ...topParents.map((parent) => uniqueStrings(toArray(parent?.supporting_claim_ids)).length),
  );
  const maxParentSignalCount = Math.max(
    1,
    ...topParents.map((parent) => buildParentDisplayChildThemes(parent, aggregateById).length),
  );
  return (
    <div className="flex h-full flex-col gap-4 overflow-auto">
      <div className="grid gap-3 md:grid-cols-5">
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Claims</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{data.counts?.claims ?? 0}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Domains</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{(data.domains ?? []).length}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Sections</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{(data.sections ?? []).length}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Themes</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{toArray(data.hierarchy?.parents).length}</p>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Sub-Themes</p>
          <p className="mt-2 text-2xl font-semibold text-slate-900">{allDisplayChildThemes.length}</p>
        </div>
      </div>
      <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Emerging themes</p>
            <h3 className="mt-1 text-lg font-semibold text-slate-900">What is shaping up now</h3>
            <p className="mt-1 text-sm text-slate-500">
              Parent themes synthesized from aggregate topic hints. Expand a theme to browse its sub-themes and the evidence behind them.
            </p>
          </div>
          <Chip size="sm" variant="flat" color="primary">
            {`${toArray(data.hierarchy?.parents).length} parent themes`}
          </Chip>
        </div>
        <div className="mt-4 divide-y divide-slate-100">
          {topParents.length ? (
            topParents.map((parent, index) => {
              const expanded = expandedParentIds[parent.parent_id] === true;
              const supportingClaimIds = uniqueStrings(toArray(parent?.supporting_claim_ids));
              const childThemes = buildParentDisplayChildThemes(parent, aggregateById);
              const supportingSignalCount = childThemes.length;
              const visibleChildAggregates = childThemes
                .filter((aggregate) => aggregate?.userPolicy?.hidden !== true)
                .sort((left, right) => Number(right?.agendaScore ?? 0) - Number(left?.agendaScore ?? 0))
                .slice(0, expanded ? 8 : 4);
              const parentClaims = topClaims
                .filter((claim) => supportingClaimIds.includes(claim.id))
                .slice(0, expanded ? 8 : 4);
              const subtitle = truncateText(
                parent.why_now ?? "Synthesized from recurring theme signals.",
                140,
              );
              const claimFillWidth = Math.max(
                12,
                Math.round((supportingClaimIds.length / maxParentClaimCount) * 100),
              );
              const signalFillWidth = Math.max(
                10,
                Math.round((supportingSignalCount / maxParentSignalCount) * 100),
              );
              return (
                <article key={parent.parent_id} className="py-3 first:pt-0 last:pb-0">
                  <button
                    type="button"
                    onClick={() => setExpandedParentIds((prev) => ({ ...prev, [parent.parent_id]: !expanded }))}
                    className="group block w-full text-left"
                  >
                    <div className="md:grid md:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] md:items-start md:gap-6">
                      <div className="min-w-0 flex items-start gap-4">
                        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[11px] font-bold text-slate-500 group-hover:bg-primary/10 group-hover:text-primary">
                          {index + 1}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="block truncate text-sm font-semibold text-slate-900">
                              {parent.label}
                            </span>
                            <Chip size="sm" variant="flat" color="secondary">
                              {expanded ? "Expanded" : "Expand"}
                            </Chip>
                          </div>
                          <p className="mt-1 text-xs leading-5 text-slate-500">
                            {subtitle}
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 w-full min-w-0 md:mt-0">
                        <div className="flex items-center justify-between gap-3 text-[11px] font-medium text-slate-500">
                          <span>Coverage</span>
                          <span>{`${supportingClaimIds.length} evidence / ${supportingSignalCount} sub-themes`}</span>
                        </div>
                        <div className="mt-1.5 relative h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
                          <div
                            className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-violet-600 via-fuchsia-500 to-sky-400 opacity-90"
                            style={{ width: `${claimFillWidth}%` }}
                          />
                          <div
                            className="absolute inset-y-[2px] left-0 rounded-full bg-white/70"
                            style={{ width: `${signalFillWidth}%` }}
                          />
                        </div>
                        <div className="mt-2 flex items-center gap-3 text-xs font-medium text-slate-500 md:justify-end">
                          <span>{supportingClaimIds.length} evidence</span>
                          <span className="h-1 w-1 rounded-full bg-slate-300" />
                          <span>{supportingSignalCount} sub-themes</span>
                        </div>
                      </div>
                    </div>
                  </button>
                  {expanded ? (
                    <div className="mt-4 border-t border-slate-200 pt-4">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sub-Themes</p>
                        <span className="text-xs text-slate-500">{`${supportingSignalCount} sub-themes / ${supportingClaimIds.length} evidence items`}</span>
                      </div>
                      <div className="mt-3 space-y-3">
                        {visibleChildAggregates.length ? (
                          visibleChildAggregates.map((aggregate) => {
                            const aggregateExpanded = expandedAggregateIds[aggregate.displayId] === true;
                            const aggregateClaimIds = uniqueStrings(toArray(aggregate.claimIds));
                            const matchedAggregateClaims = uniqueObjectsBy(
                              [
                                ...aggregateClaimIds.map((claimId) => claimById.get(claimId)).filter(Boolean),
                                ...allClaims.filter((claim) =>
                                  toArray(claim?.aggregateIds).some((aggregateId) => aggregate.aggregateIds.includes(aggregateId)),
                                ),
                              ],
                              (claim) => claim?.id,
                            )
                              .map((claim) => ({ ...claim, groupKey: claimGroupKeyById.get(claim.id) ?? claim.type ?? "claim" }))
                              .sort((left, right) => {
                                const leftConfidence = Number(left.confidence ?? 0);
                                const rightConfidence = Number(right.confidence ?? 0);
                                if (leftConfidence !== rightConfidence) return rightConfidence - leftConfidence;
                                return String(left.text ?? "").localeCompare(String(right.text ?? ""));
                              });
                            const resolvedAggregateEvidenceCount =
                              matchedAggregateClaims.length ||
                              aggregateClaimIds.length ||
                              Number(aggregate.claimCount ?? 0);
                            const aggregateClaims = aggregateExpanded
                              ? matchedAggregateClaims
                              : matchedAggregateClaims.slice(0, 3);
                            return (
                              <div key={aggregate.displayId} className="rounded-2xl border border-slate-200 bg-white">
                                <div className="border-l-4 border-emerald-400 px-4 py-3">
                                  <div className="flex items-start justify-between gap-3">
                                    <button
                                      type="button"
                                      onClick={() => setExpandedAggregateIds((prev) => ({ ...prev, [aggregate.displayId]: !aggregateExpanded }))}
                                      className="min-w-0 flex-1 text-left"
                                    >
                                        <div className="flex flex-wrap items-center gap-2">
                                          <p className="text-sm font-medium text-slate-900">{aggregate.label}</p>
                                          <Chip size="sm" variant="flat" color="secondary">
                                            {aggregateExpanded ? "Expanded" : "Expand"}
                                          </Chip>
                                        </div>
                                        <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                                          <span>{`${resolvedAggregateEvidenceCount} evidence items`}</span>
                                          <span>{`agenda ${Number(aggregate.agendaScore ?? 0).toFixed(1)}`}</span>
                                        </div>
                                        {(aggregate.companyLabels?.length || aggregate.entityLabels?.length) ? (
                                          <div className="mt-3 flex flex-wrap gap-2">
                                            {toArray(aggregate.companyLabels).slice(0, 4).map((label) => (
                                              <Chip key={`${aggregate.displayId}_${label}`} size="sm" variant="flat" color="success">
                                                {label}
                                              </Chip>
                                            ))}
                                            {toArray(aggregate.entityLabels).slice(0, 4).map((label) => (
                                              <Chip key={`${aggregate.displayId}_entity_${label}`} size="sm" variant="flat">
                                                {label}
                                              </Chip>
                                            ))}
                                          </div>
                                        ) : null}
                                        {aggregate.childLabels?.length ? (
                                          <div className="mt-3 flex flex-wrap gap-2">
                                            {aggregate.childLabels.map((label) => (
                                              <Chip key={`${aggregate.displayId}_child_${label}`} size="sm" variant="bordered">
                                                {label}
                                              </Chip>
                                            ))}
                                          </div>
                                        ) : null}
                                    </button>
                                    <Button
                                      size="sm"
                                      variant="flat"
                                      className="bg-slate-900 text-white"
                                      onPress={() =>
                                        onOpenDeepDive?.({
                                          anchorType: "aggregate",
                                          anchorIds: aggregate.aggregateIds.length ? aggregate.aggregateIds : [aggregate.aggregateId].filter(Boolean),
                                          label: aggregate.label,
                                        })
                                      }
                                    >
                                      Go deep
                                    </Button>
                                  </div>
                                </div>
                                {aggregateExpanded ? (
                                  <div className="border-t border-slate-100 px-4 py-3">
                                    <div className="flex items-center justify-between gap-3">
                                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Evidence</p>
                                      <span className="text-xs text-slate-500">{resolvedAggregateEvidenceCount}</span>
                                    </div>
                                    <div className="mt-2 space-y-2">
                                      {aggregateClaims.length ? (
                                        aggregateClaims.map((claim) => (
                                          <div key={`${aggregate.displayId}_${claim.id}`} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                                            <p className="text-sm text-slate-800">{claim.text}</p>
                                            <div className="mt-2 flex flex-wrap gap-2 text-[11px] text-slate-500">
                                              <span>{humanizeKey(claim.groupKey, "claim")}</span>
                                              <span>{humanizeKey(claim.primarySection, "unassigned")}</span>
                                              {claim.confidence !== null && claim.confidence !== undefined ? (
                                                <span>{`confidence ${Number(claim.confidence).toFixed(2)}`}</span>
                                              ) : null}
                                            </div>
                                          </div>
                                        ))
                                      ) : (
                                        <p className="rounded-xl border border-dashed border-slate-200 px-3 py-3 text-sm text-slate-500">
                                          No evidence items are linked to this sub-theme yet.
                                        </p>
                                      )}
                                    </div>
                                    {onUpdateTopicPolicy ? (
                                      <div className="mt-3 flex flex-wrap gap-2">
                                        <Button size="sm" variant={aggregate.userPolicy?.followed ? "solid" : "flat"} color="primary" onPress={() => onUpdateTopicPolicy?.(aggregate.aggregateId, "followed", !aggregate.userPolicy?.followed)}>
                                          {aggregate.userPolicy?.followed ? "Following" : "Follow"}
                                        </Button>
                                        <Button size="sm" variant={aggregate.userPolicy?.hidden ? "solid" : "flat"} onPress={() => onUpdateTopicPolicy?.(aggregate.aggregateId, "hidden", !aggregate.userPolicy?.hidden)}>
                                          {aggregate.userPolicy?.hidden ? "Unhide" : "Hide"}
                                        </Button>
                                      </div>
                                    ) : null}
                                  </div>
                                ) : null}
                              </div>
                            );
                          })
                        ) : (
                          <p className="rounded-xl border border-dashed border-slate-200 px-3 py-3 text-sm text-slate-500">
                            No sub-themes are available for this theme yet.
                          </p>
                        )}
                        {parentClaims.length ? (
                          <div className="rounded-xl border border-dashed border-slate-200 px-3 py-3">
                            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Theme-Level Evidence</p>
                            <p className="mt-1 text-sm text-slate-500">
                              Some evidence items are linked to the parent theme directly rather than a visible sub-theme.
                            </p>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                </article>
              );
            })
          ) : (
            <p className="py-3 text-sm text-slate-500">No hierarchy themes are available yet.</p>
          )}
        </div>
      </section>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,2.2fr)_minmax(18rem,1fr)]">
        <div className="space-y-3">
          {topClaims.length ? (
            topClaims.map((claim) => (
              <article key={claim.id} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Chip size="sm" variant="flat">{humanizeKey(claim.groupKey, "claim")}</Chip>
                      <Chip size="sm" variant="bordered">{humanizeKey(claim.primarySection, "unassigned")}</Chip>
                      {claim.date ? <Chip size="sm" variant="bordered">{claim.date}</Chip> : null}
                    </div>
                    <p className="mt-3 text-sm font-medium leading-6 text-slate-900">{claim.text}</p>
                    <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-500">
                      {claim.confidence !== null && claim.confidence !== undefined ? (
                        <span>{`confidence ${Number(claim.confidence).toFixed(2)}`}</span>
                      ) : null}
                      {claim.relevance !== null && claim.relevance !== undefined ? (
                        <span>{`relevance ${Number(claim.relevance).toFixed(2)}`}</span>
                      ) : null}
                    </div>
                    {claim.entityLabels?.length ? (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {claim.entityLabels.slice(0, 6).map((label) => (
                          <Chip key={`${claim.id}_${label}`} size="sm" variant="flat" color="success">
                            {label}
                          </Chip>
                        ))}
                      </div>
                    ) : null}
                    {claim.sourceDomains?.length ? (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {claim.sourceDomains.slice(0, 5).map((domain) => (
                          <Chip key={`${claim.id}_${domain}`} size="sm" variant="flat" color="secondary">
                            {domain}
                          </Chip>
                        ))}
                      </div>
                    ) : null}
                  </div>
                  <Button
                    size="sm"
                    variant="flat"
                    className="bg-slate-900 text-white"
                    onPress={() =>
                      onOpenDeepDive?.({
                        anchorType: "claim",
                        anchorIds: [claim.id],
                        label: truncateText(claim.text, 48),
                      })
                    }
                  >
                    Go deep
                  </Button>
                </div>
              </article>
            ))
          ) : (
            <p className="text-sm text-slate-500">No claims collected yet.</p>
          )}
        </div>
        <div className="space-y-4">
          <aside className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-900">Top domains</h3>
            <div className="mt-3 space-y-2">
              {(data.domains ?? []).length ? (
                data.domains.map((entry) => (
                  <div key={entry.domain} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <span className="text-sm text-slate-800">{entry.domain}</span>
                    <span className="text-xs font-semibold text-slate-500">{entry.count}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500">No source domains linked yet.</p>
              )}
            </div>
          </aside>
          <aside className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-slate-900">Top sections</h3>
            <div className="mt-3 space-y-2">
              {(data.sections ?? []).length ? (
                data.sections.map((entry) => (
                  <div key={entry.key} className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                    <span className="text-sm text-slate-800">{humanizeKey(entry.key, "unassigned")}</span>
                    <span className="text-xs font-semibold text-slate-500">{entry.count}</span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500">No section groupings yet.</p>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function QuestionsPanel({ questions = [] }) {
  const [filterKind, setFilterKind] = useState("all");

  if (!questions.length) {
    return <p className="text-sm text-slate-500">No research questions have been generated yet.</p>;
  }

  const kinds = Array.from(new Set(questions.map((question) => question.kind).filter(Boolean)));
  const filteredQuestions = questions.filter((question) => {
    if (filterKind !== "all" && question.kind !== filterKind) return false;
    return true;
  });
  const statusSummary = summarizeStatuses(filteredQuestions);
  const statusCounts = countStatuses(
    filteredQuestions.map((question) => ({
      ...question,
      status: question?.kanbanStatus ?? question?.status,
    })),
  );

  const statusIconMap = {
    synthesizing: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M6 4h8M6 16h8" strokeLinecap="round" />
        <path d="M7.5 4c0 3 5 3.5 5 6s-5 3-5 6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    active: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="10" cy="10" r="7" />
        <path d="M10 6v4l2.5 2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    open: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="10" cy="10" r="7" />
      </svg>
    ),
    closed: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z" />
        <path d="M7.5 9.5 9 11l3-3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    skipped: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 16l5-5 5 5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M10 1v18" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    deferred: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10 4v8" strokeLinecap="round" />
        <path d="M7.5 12h5" strokeLinecap="round" />
        <circle cx="10" cy="3" r="2" />
      </svg>
    ),
    blocked: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="10" cy="10" r="7" />
        <path d="M6 6l8 8" strokeLinecap="round" />
      </svg>
    )
  };

  const kindIconMap = {
    question: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M8.8 7.1a1.9 1.9 0 1 1 2.72 1.72c-.94.45-1.52 1.03-1.52 2.08" strokeLinecap="round" />
        <circle cx="10" cy="14.1" r="0.85" fill="currentColor" stroke="none" />
        <path d="M10 17a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z" />
      </svg>
    ),
    hypothesis: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 4h12v8H4z" />
        <path d="M8 16h4" strokeLinecap="round" />
      </svg>
    ),
    task: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 5h12v2H4z" />
        <path d="M4 9h12v2H4z" />
        <path d="M4 13h8v2H4z" />
      </svg>
    ),
    default: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="10" cy="10" r="3" />
      </svg>
    )
  };

  const getKindIcon = (kind) => kindIconMap[kind?.toLowerCase()] || kindIconMap.default;
  const getStatusIcon = (status) => statusIconMap[normalizeStatus(status)] ?? statusIconMap.blocked;
  const getLifecycleBadgeClass = (tone = "slate") => ({
    blue: "bg-blue-50 text-blue-700",
    violet: "bg-violet-50 text-violet-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
    emerald: "bg-emerald-50 text-emerald-700",
    slate: "bg-slate-100 text-slate-600",
  }[tone] ?? "bg-slate-100 text-slate-600");
  const renderQuestionCard = (question) => {
    const normalizedStatus = normalizeStatus(question.kanbanStatus ?? question.status);
    const theme = getKanbanStatusTheme(normalizedStatus);
    const priorityMeta = normalizeQuestionPriority(question);
    const statusReason = getQuestionStatusReason(question);
    const lifecycle = question.lifecycle ?? {};
    const priorityBadgeClass =
      priorityMeta.tone === "rose"
        ? "bg-rose-50 text-rose-700"
        : priorityMeta.tone === "amber"
          ? "bg-amber-50 text-amber-700"
          : "bg-slate-100 text-slate-600";
    return (
      <article
        key={question.id}
        className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:shadow-md"
      >
        <div className={clsx("absolute inset-y-0 left-0 w-1", theme.barClass)} />
        <div className="pl-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {question.kind ? (
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                  {getKindIcon(question.kind)}
                  {titleizeKey(question.kind)}
                </span>
              ) : null}
              <span className={clsx("inline-flex items-center gap-1.5 rounded-lg px-2 py-0.5 text-[11px] font-medium", theme.badgeClass)}>
                {getStatusIcon(question.kanbanStatus ?? question.status)}
                <span>{titleizeKey(normalizedStatus)}</span>
              </span>
            </div>
            <span className={clsx("inline-flex items-center rounded-lg px-2 py-0.5 text-[11px] font-semibold", priorityBadgeClass)}>
              {`Priority ${titleizeKey(priorityMeta.label)}`}
            </span>
          </div>

          <p className="mt-2.5 text-sm font-medium leading-snug text-slate-900">
            {question.question}
          </p>

          <div className="mt-2.5 flex flex-wrap items-center gap-2 text-[11px]">
            <span className={clsx("inline-flex items-center rounded-lg px-2 py-0.5 font-medium", getLifecycleBadgeClass(lifecycle.engineStateTone))}>
              {lifecycle.engineStateLabel ?? "No linked work"}
            </span>
            {lifecycle.latestActivityAge ? (
              <span className="text-slate-500">{`Last activity ${lifecycle.latestActivityAge}`}</span>
            ) : null}
            {Number(lifecycle.linkedTaskCount ?? 0) > 0 || Number(lifecycle.linkedObjectiveCount ?? 0) > 0 ? (
              <span className="text-slate-500">
                {`${Number(lifecycle.linkedTaskCount ?? 0)} tasks · ${Number(lifecycle.linkedObjectiveCount ?? 0)} objectives`}
              </span>
            ) : null}
          </div>

          {statusReason && (normalizedStatus === "deferred" || normalizedStatus === "blocked") ? (
            <div
              className={clsx(
                "mt-3 rounded-xl border px-3 py-2 text-xs leading-5",
                normalizedStatus === "deferred"
                  ? "border-slate-200 bg-slate-50 text-slate-600"
                  : "border-rose-200 bg-rose-50 text-rose-700",
              )}
            >
              <span className="font-semibold">{normalizedStatus === "deferred" ? "Why deferred:" : "Why blocked:"}</span>{" "}
              <span>{statusReason}</span>
            </div>
          ) : null}

          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-slate-500">
            {question.id ? (
              <span className="flex items-center gap-1 font-mono">
                {`ID: ${question.id}`}
              </span>
            ) : null}
            {question.priority_score !== undefined ? (
              <span className="flex items-center gap-1">
                <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 2l2.5 6h6l-5 3.5L15 18l-5-3-5 3 1.5-6.5L1 8h6z" />
                </svg>
                {`Score: ${question.priority_score}`}
              </span>
            ) : null}
            {question.answerability ? (
              <span className="flex items-center gap-1">
                {`Answerability: ${question.answerability}`}
              </span>
            ) : null}
            {question.parent_claim_id ? (
              <span className="flex items-center gap-1">
                {`Claim: ${question.parent_claim_id}`}
              </span>
            ) : null}
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="space-y-4 overflow-auto">
      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Question board</p>
            <p className="text-xs text-slate-500">
              {`${filteredQuestions.length} visible of ${questions.length} total · ${statusSummary}`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-slate-500">
             {Object.entries(statusCounts).filter(([_, count]) => count > 0).map(([status, count]) => (
              <span key={status} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1">
                <span className={clsx("h-2 w-2 rounded-full", getKanbanStatusTheme(status).dotClass)} aria-hidden="true" />
                {`${titleizeKey(status)} ${count}`}
              </span>
            ))}
          </div>
        </div>
        {kinds.length ? (
          <div className="mt-3 flex flex-wrap gap-1.5">
            <button
              onClick={() => setFilterKind("all")}
              className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
                filterKind === "all"
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              All types
            </button>
            {kinds.map((kind) => (
              <button
                key={kind}
                onClick={() => setFilterKind(kind)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
                  filterKind === kind
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {getKindIcon(kind)}
                {titleizeKey(kind)}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <KanbanBoard
        items={filteredQuestions}
        renderCard={renderQuestionCard}
        emptyMessage="No questions match the selected filters."
      />
    </div>
  );
}


function TasksPanel({ tasks = [] }) {
  const [filterType, setFilterType] = useState("all");

  const types = Array.from(new Set(tasks.map((task) => task.type).filter(Boolean)));
  const filteredTasks = tasks.filter((task) => {
    if (filterType !== "all" && task.type !== filterType) return false;
    return true;
  });
  const groupedTasks = useMemo(() => {
    const topLevelTasks = filteredTasks
      .filter((task) => task.type !== "fetch_page")
      .map((task) => ({ ...task, nestedFetches: [] }));
    const fetchTasks = filteredTasks.filter((task) => task.type === "fetch_page");
    const topLevelById = new Map(topLevelTasks.map((task) => [task.id, task]));
    const topLevelIdsByObjective = new Map();

    topLevelTasks.forEach((task) => {
      if (!task?.objective_id) {
        return;
      }
      const existing = topLevelIdsByObjective.get(task.objective_id) ?? [];
      topLevelIdsByObjective.set(task.objective_id, [...existing, task.id]);
    });

    const orphanFetches = [];

    fetchTasks.forEach((task) => {
      const explicitParentId = task?.metadata?.parent_task_id ?? task?.parent_task_id ?? null;
      let parentId = explicitParentId && topLevelById.has(explicitParentId) ? explicitParentId : null;

      if (!parentId && task?.objective_id) {
        const candidates = topLevelIdsByObjective.get(task.objective_id) ?? [];
        if (candidates.length === 1 && topLevelById.has(candidates[0])) {
          parentId = candidates[0];
        }
      }

      if (parentId && topLevelById.has(parentId)) {
        topLevelById.get(parentId).nestedFetches.push(task);
      } else {
        orphanFetches.push({ ...task, nestedFetches: [] });
      }
    });

    const sortByStatusAndLabel = (left, right) => {
      const statusOrder = { active: 0, open: 1, blocked: 2, deferred: 3, closed: 4, skipped: 5 };
      const leftOrder = statusOrder[normalizeStatus(left?.status)] ?? 9;
      const rightOrder = statusOrder[normalizeStatus(right?.status)] ?? 9;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      return String(left?.metadata?.title ?? left?.query ?? left?.id ?? "").localeCompare(
        String(right?.metadata?.title ?? right?.query ?? right?.id ?? ""),
      );
    };

    const displayTasks = [
      ...topLevelTasks.map((task) => ({
        ...task,
        nestedFetches: [...task.nestedFetches].sort(sortByStatusAndLabel),
      })),
      ...orphanFetches,
    ].map((task) => ({
      ...task,
      kanbanStatus: aggregateNestedTaskStatus(task),
    }));

    return {
      displayTasks,
    };
  }, [filteredTasks]);

  if (!tasks.length) {
    return <p className="text-sm text-slate-500">No research tasks are queued right now.</p>;
  }

  const statusSummary = summarizeStatuses(groupedTasks.displayTasks);
  const statusCounts = countStatuses(groupedTasks.displayTasks);

  const statusIconMap = {
    synthesizing: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M6 4h8M6 16h8" strokeLinecap="round" />
        <path d="M7.5 4c0 3 5 3.5 5 6s-5 3-5 6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    active: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="10" cy="10" r="7" />
        <path d="M10 6v4l2.5 2.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    open: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="10" cy="10" r="7" />
      </svg>
    ),
    closed: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z" />
        <path d="M7.5 9.5 9 11l3-3" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    skipped: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 16l5-5 5 5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M10 1v18" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    deferred: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10 4v8" strokeLinecap="round" />
        <path d="M7.5 12h5" strokeLinecap="round" />
        <circle cx="10" cy="3" r="2" />
      </svg>
    ),
    blocked: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="10" cy="10" r="7" />
        <path d="M6 6l8 8" strokeLinecap="round" />
      </svg>
    )
  };

  const typeIconMap = {
    analysis: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 3h14v14H3z" />
        <path d="M3 9h14" strokeLinecap="round" />
        <path d="M9 3v14" strokeLinecap="round" />
      </svg>
    ),
    research: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M8.8 7.1a1.9 1.9 0 1 1 2.72 1.72c-.94.45-1.52 1.03-1.52 2.08" strokeLinecap="round" />
        <circle cx="10" cy="14.1" r="0.85" fill="currentColor" stroke="none" />
        <path d="M10 17a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z" />
      </svg>
    ),
    synthesis: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 5h12v10H4z" />
        <path d="M8 15h4" strokeLinecap="round" />
      </svg>
    ),
    default: (
      <svg className="h-4 w-4" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="10" cy="10" r="3" />
      </svg>
    )
  };

  const getTaskTypeIcon = (type) => typeIconMap[type?.toLowerCase()] || typeIconMap.default;
  const getStatusIcon = (status) => statusIconMap[normalizeStatus(status)] ?? statusIconMap.blocked;
  const getLifecycleBadgeClass = (tone = "slate") => ({
    blue: "bg-blue-50 text-blue-700",
    violet: "bg-violet-50 text-violet-700",
    amber: "bg-amber-50 text-amber-700",
    rose: "bg-rose-50 text-rose-700",
    emerald: "bg-emerald-50 text-emerald-700",
    slate: "bg-slate-100 text-slate-600",
  }[tone] ?? "bg-slate-100 text-slate-600");
  const renderNestedFetchCard = (task) => {
    const normalizedStatus = normalizeStatus(task.status);
    const theme = getKanbanStatusTheme(normalizedStatus);
    const fetchStatusMessage = formatFetchStatusMessage(task);
    const lifecycle = task.lifecycle ?? {};
    return (
      <div
        key={task.id}
        className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <span className={clsx("h-2 w-2 rounded-full", theme.dotClass)} aria-hidden="true" />
            <span className="truncate text-[11px] font-medium text-slate-800">
              {task.metadata?.title ?? task.query ?? task.metadata?.url ?? task.id}
            </span>
          </div>
          <span className={clsx("inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-[10px] font-medium", theme.badgeClass)}>
            {getStatusIcon(task.status)}
            <span>{formatTaskStatusLabel(task.status)}</span>
          </span>
        </div>
        <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-slate-500">
          <span className={clsx("inline-flex items-center rounded-lg px-2 py-0.5 font-medium", getLifecycleBadgeClass(lifecycle.engineStateTone))}>
            {lifecycle.engineStateLabel ?? "Idle"}
          </span>
          {lifecycle.latestActivityAge ? <span>{`Last activity ${lifecycle.latestActivityAge}`}</span> : null}
          {task.query ? <span className="truncate">{task.query}</span> : null}
          {fetchStatusMessage ? <span>{fetchStatusMessage}</span> : null}
          {task.metadata?.followup_reason ? <span>{titleizeKey(task.metadata.followup_reason)}</span> : null}
        </div>
      </div>
    );
  };
  const renderTaskCard = (task) => {
    const normalizedStatus = normalizeStatus(task.kanbanStatus ?? task.status);
    const actualTaskStatus = normalizeStatus(task.status);
    const theme = getKanbanStatusTheme(normalizedStatus);
    const statusLabel = formatTaskStatusLabel(task.kanbanStatus ?? task.status);
    const nestedFetchSummary = summarizeNestedFetches(task.nestedFetches);
    const fetchStatusMessage = task.type === "fetch_page" ? formatFetchStatusMessage(task) : null;
    const lifecycle = task.lifecycle ?? {};
    return (
      <article
        key={task.id}
        className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:shadow-md"
      >
        <div className={clsx("absolute inset-y-0 left-0 w-1", theme.barClass)} />
        <div className="pl-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {task.type ? (
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                  {getTaskTypeIcon(task.type)}
                  {titleizeKey(task.type)}
                </span>
              ) : null}
              <span className={clsx("inline-flex items-center gap-1.5 rounded-lg px-2 py-0.5 text-[11px] font-medium", theme.badgeClass)}>
                {getStatusIcon(task.kanbanStatus ?? task.status)}
                <span>{statusLabel}</span>
              </span>
              {Array.isArray(task.nestedFetches) && task.nestedFetches.length ? (
                <span className="inline-flex items-center rounded-lg bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                  {`${task.nestedFetches.length} page ${task.nestedFetches.length === 1 ? "fetch" : "fetches"}`}
                </span>
              ) : null}
            </div>

            {task.phase ? (
              <span className="inline-flex items-center rounded-lg bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                {titleizeKey(task.phase)}
              </span>
            ) : null}
          </div>

          {task.query ? (
            <p className="mt-2.5 text-sm font-medium leading-snug text-slate-900">
              {task.query}
            </p>
          ) : null}

          <div className="mt-2.5 flex flex-wrap items-center gap-2 text-[11px]">
            <span className={clsx("inline-flex items-center rounded-lg px-2 py-0.5 font-medium", getLifecycleBadgeClass(lifecycle.engineStateTone))}>
              {lifecycle.engineStateLabel ?? "Idle"}
            </span>
            {lifecycle.latestActivityAge ? (
              <span className="text-slate-500">{`Last activity ${lifecycle.latestActivityAge}`}</span>
            ) : null}
            {lifecycle.objectiveStatus && lifecycle.objectiveStatus !== "unknown" ? (
              <span className="text-slate-500">{`Objective ${titleizeKey(lifecycle.objectiveStatus)}`}</span>
            ) : null}
          </div>

          {normalizedStatus !== actualTaskStatus ? (
            <p className="mt-2 text-[11px] text-slate-500">
              {actualTaskStatus === "closed" && normalizedStatus === "open"
                ? "Search step finished and queued child page fetches; this card stays open until they settle."
                : `Parent task is ${formatTaskStatusLabel(task.status)}; board status reflects child page fetch activity.`}
            </p>
          ) : null}

          {fetchStatusMessage ? (
            <p className="mt-2 text-[11px] text-slate-500">{fetchStatusMessage}</p>
          ) : null}

          <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-slate-500">
            {task.id ? (
              <span className="flex items-center gap-1 font-mono">
                {`ID: ${task.id}`}
              </span>
            ) : null}
            {task.objective_id ? (
              <span className="flex items-center gap-1">
                {`Objective: ${task.objective_id}`}
              </span>
            ) : null}
            {task.claim_id ? (
              <span className="flex items-center gap-1">
                {`Claim: ${task.claim_id}`}
              </span>
            ) : null}
            {task.provider ? (
              <span className="flex items-center gap-1">
                {`Provider: ${titleizeKey(task.provider)}`}
              </span>
            ) : null}
          </div>

          {Array.isArray(task.nestedFetches) && task.nestedFetches.length ? (
            <div className="mt-3 border-t border-slate-100 pt-3">
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Page Fetches</p>
                <span className="text-[10px] text-slate-400">
                  {nestedFetchSummary}
                </span>
              </div>
              <div className="space-y-2">
                {task.nestedFetches.map(renderNestedFetchCard)}
              </div>
            </div>
          ) : null}
        </div>
      </article>
    );
  };

  return (
    <div className="space-y-4 overflow-auto">
      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Task board</p>
            <p className="text-xs text-slate-500">
              {`${groupedTasks.displayTasks.length} tasks · ${statusSummary}`} 
            </p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-slate-500">
             {Object.entries(statusCounts).filter(([_, count]) => count > 0).map(([status, count]) => (
              <span key={status} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1">
                <span className={clsx("h-2 w-2 rounded-full", getKanbanStatusTheme(status).dotClass)} aria-hidden="true" />
                {`${titleizeKey(status)} ${count} cards`}
              </span>
            ))}
          </div>
        </div>
        {types.length ? (
          <div className="mt-3 flex flex-wrap gap-1.5">
            <button
              onClick={() => setFilterType("all")}
              className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
                filterType === "all"
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              All types
            </button>
            {types.map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
                  filterType === type
                    ? "bg-slate-900 text-white"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {getTaskTypeIcon(type)}
                {titleizeKey(type)}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <KanbanBoard
        items={groupedTasks.displayTasks}
        renderCard={renderTaskCard}
        emptyMessage="No tasks match the selected filters."
      />
    </div>
  );
}

function SubAgentsPanel({ data = null, onReviewAction = null, reviewSubmittingAgentId = null }) {
  const agentRuntime = data?.agentRuntime ?? {};
  const runtimeV3 = data?.researchRuntimeV3 ?? data?.runtimeV3 ?? {};
  const includeCoordinatorAgent = shouldIncludeCoordinatorAgent(
    runtimeV3,
    agentRuntime,
    {
      current_agent_id:
        agentRuntime?.current_agent_id ??
        runtimeV3?.current_agent_id ??
        null,
      agent_runs: toArray(data?.agentRuns),
    },
  );
  const plannedAgentUnits = useMemo(() => {
    const persistedUnits = toArray(data?.plannedAgentUnits);
    if (persistedUnits.length > 0) {
      return persistedUnits;
    }
    return getVisibleAgentRuns(toArray(data?.agentRuns), { includeRoot: includeCoordinatorAgent });
  }, [data?.plannedAgentUnits, data?.agentRuns, includeCoordinatorAgent]);
  const agentThreads = useMemo(
    () =>
      plannedAgentUnits.map((unit) => ({
        ...unit,
        scope_type:
          unit?.scope_type === "matrix_cell"
            ? "section"
            : unit?.scope_type,
        scope_key:
          unit?.scope_type === "matrix_cell"
            ? unit?.input_refs?.section_key ?? unit?.scope_key ?? null
            : unit?.scope_key ?? null,
        status_text: unit?.mission ?? unit?.display_label ?? null,
      })),
    [plannedAgentUnits],
  );
  const allAgentThreads = useMemo(
    () =>
      toArray(data?.agentRuns).map((unit) => ({
        ...unit,
        scope_type:
          unit?.scope_type === "matrix_cell"
            ? "section"
            : unit?.scope_type,
        scope_key:
          unit?.scope_type === "matrix_cell"
            ? unit?.input_refs?.section_key ?? unit?.scope_key ?? null
            : unit?.scope_key ?? null,
        status_text: unit?.mission ?? unit?.display_label ?? null,
      })),
    [data?.agentRuns],
  );
  const claims = toArray(data?.claims);
  const sharedGraph = data?.sharedGraph ?? {};
  const agentHierarchy =
    toArray(data?.agentHierarchy).length > 0
      ? toArray(data?.agentHierarchy)
      : toArray(data?.unitHierarchy);
  const flattenHierarchyNodes = useCallback((nodes = [], parentNode = null) => {
    const flattened = [];
    toArray(nodes).forEach((node) => {
      if (!node) {
        return;
      }
      if (String(node?.id ?? "").startsWith("display-group:")) {
        flattened.push(...flattenHierarchyNodes(node?.child_items, parentNode));
        return;
      }
      if (!isRedundantQuestionWrapperNode(node, parentNode)) {
        flattened.push(node);
      }
      flattened.push(...flattenHierarchyNodes(node?.child_items, node));
    });
    return flattened;
  }, []);
  const hierarchyAgentNodes = useMemo(
    () => flattenHierarchyNodes(agentHierarchy),
    [agentHierarchy, flattenHierarchyNodes],
  );
  const hierarchyAgentById = useMemo(
    () => new Map(hierarchyAgentNodes.filter((entry) => entry?.id).map((entry) => [entry.id, entry])),
    [hierarchyAgentNodes],
  );
  const reviewableAwaitingAgentIds = useMemo(
    () =>
      new Set(
        toArray(data?.agentRuntime?.awaiting_review_agents)
          .map((entry) => String(entry?.id ?? "").trim())
          .filter(Boolean),
      ),
    [data?.agentRuntime?.awaiting_review_agents],
  );
  const objectives = toArray(data?.objectives);
  const questions = toArray(data?.questions);
  const tasks = toArray(data?.tasks);

  const resolvedTaskObjectiveAgentIdByClaimId = useMemo(() => {
    const taskById = new Map(tasks.filter((t) => t?.id).map((t) => [t.id, t]));
    const objectiveById = new Map(objectives.filter((o) => o?.id).map((o) => [o.id, o]));
    const mapping = new Map();
    claims.forEach((claim) => {
      if (!claim?.id) return;
      const originTaskIds = uniqueStrings([
        ...toArray(claim?.originating_task_ids),
        ...(claim?.origin_task_id ? [claim.origin_task_id] : []),
      ]);
      for (const taskId of originTaskIds) {
        const task = taskById.get(taskId);
        if (!task) continue;
        const objectiveId = String(task?.objective_id ?? task?.metadata?.objective_id ?? "").trim();
        if (!objectiveId) continue;
        const objective = objectiveById.get(objectiveId);
        if (!objective) continue;
        const agentId = String(objective?.agent_id ?? "").trim() || (objectiveId ? `agent:objective:${objectiveId}` : "");
        if (!agentId) continue;
        mapping.set(claim.id, agentId);
        break;
      }
    });
    return mapping;
  }, [claims, tasks, objectives]);

  const formatTimestamp = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleString();
  };
  const formatScopeType = (value = "") => titleizeKey(String(value ?? "").replace(/[_-]+/g, " "), "Sub-run");
  const humanizeAgentLabel = useCallback((value = "", fallback = "Untitled agent") => {
    const normalized = String(value ?? "").trim();
    if (!normalized) return fallback;
    if (normalized === "root" || normalized === "agent:root") return "Research coordinator";
    if (/^[a-z0-9_:-]+$/i.test(normalized)) {
      const trimmed = normalized.includes(":") ? normalized.split(":").pop() : normalized;
      return titleizeKey(String(trimmed ?? "").replace(/[_-]+/g, " "), fallback);
    }
    return normalized;
  }, []);
  const formatScopeBadgeLabel = useCallback((agentThread = {}) => {
    if (agentThread?.node_role === "root") return "Coordinator";
    if (agentThread?.node_role === "objective") return "Objective Agent";
    if (agentThread?.node_role === "question") return "Question Agent";
    if (agentThread?.kind === "section_writer") return "Writer Agent";
    if (agentThread?.kind === "frame_setting") return "Frame Agent";
    if (agentThread?.kind === "selection_setting") return "Selection Agent";
    if (agentThread?.kind === "synthesis_producing") return "Synthesis Agent";
    if (agentThread?.kind === "evidence_producing") return "Evidence Agent";
    if (agentThread?.scope_type === "focus_area") return "Focus Aggregate";
    if (agentThread?.scope_type === "section") return "Section Agent";
    return formatScopeType(agentThread?.scope_type);
  }, []);
  const formatSubRunLabel = useCallback((agentThread = {}) =>
    humanizeAgentLabel(
      agentThread?.display_label ?? agentThread?.scope_key ?? agentThread?.id ?? "Sub-run",
      "Untitled agent",
    ), [humanizeAgentLabel]);
  const getAgentPrimaryText = useCallback((agentThread = {}) => {
    const workPackage = agentThread?.work_package ?? {};
    const genericLabel = String(agentThread?.label ?? "").trim().toLowerCase();
    const prefersQuestionLabelFirst =
      [
        "brief_question",
        "topic_question",
        "verification_question",
        "frame_question",
      ].includes(genericLabel) ||
      ["frame_setting", "selection_setting"].includes(String(agentThread?.kind ?? "").trim());
    const prefersObjectiveLabelFirst = ![
      "frame_setting",
      "selection_setting",
    ].includes(String(agentThread?.kind ?? "").trim()) && !prefersQuestionLabelFirst;
    const candidates = [
      prefersQuestionLabelFirst ? workPackage?.question : workPackage?.objective,
      agentThread?.label,
      agentThread?.display_label,
      prefersObjectiveLabelFirst ? workPackage?.question : null,
      prefersObjectiveLabelFirst ? agentThread?.question : null,
      workPackage?.mission,
      prefersObjectiveLabelFirst ? null : workPackage?.objective,
      prefersObjectiveLabelFirst ? null : workPackage?.question,
      prefersObjectiveLabelFirst ? null : agentThread?.question,
      agentThread?.mission,
      agentThread?.scope_key,
      agentThread?.id,
    ];
    for (const candidate of candidates) {
      const normalized = String(candidate ?? "").trim();
      if (!normalized) continue;
      if (candidate === agentThread?.scope_key || candidate === agentThread?.id || candidate === agentThread?.display_label) {
        return humanizeAgentLabel(normalized, "Untitled agent");
      }
      return normalized;
    }
    return "Untitled agent";
  }, []);
  const getAgentSecondaryText = useCallback((agentThread = {}, relationshipLabels = []) => {
    const workPackage = agentThread?.work_package ?? {};
    const primaryText = getAgentPrimaryText(agentThread);
    const candidates = [
      workPackage?.objective,
      workPackage?.mission,
      agentThread?.status_text,
    ];
    for (const candidate of candidates) {
      const normalized = String(candidate ?? "").trim();
      if (!normalized) continue;
      if (normalized === primaryText) continue;
      return normalized;
    }
    if (relationshipLabels.length > 0) {
      return agentThread?.scope_type === "section"
        ? `Under ${relationshipLabels.map((entry) => humanizeAgentLabel(entry)).join(" · ")}`
        : `Covers ${relationshipLabels.map((entry) => humanizeAgentLabel(entry)).join(" · ")}`;
    }
    return null;
  }, [getAgentPrimaryText]);
  const formatActionLabel = useCallback((action = {}) => {
    if (!action || typeof action !== "object") return null;
    return titleizeKey(
      action?.task_type ?? action?.provider ?? action?.action ?? "",
      "",
    ) || null;
  }, []);
  const formatActionMeta = useCallback((action = {}) => {
    if (!action || typeof action !== "object") return [];
    return [
      action?.provider ? `Provider ${humanizeKey(action.provider)}` : null,
      action?.section_key ? `Section ${action.section_key}` : null,
      action?.task_id ? `Task ${action.task_id}` : null,
    ].filter(Boolean);
  }, []);
  const describeSubRunAction = useCallback((agentThread = {}, linkedAgent = null) => {
    const liveAction = linkedAgent?.action ?? agentThread?.current_action ?? null;
    const actionLabel = formatActionLabel(liveAction);
    if (actionLabel) {
      return {
        label: actionLabel,
        meta: formatActionMeta(liveAction),
      };
    }
    const runningTasks = Number(agentThread?.coverage?.active_task_count ?? 0);
    const queuedTasks = Number(agentThread?.coverage?.queued_task_count ?? 0);
    if (runningTasks > 0) {
      return {
        label: runningTasks === 1 ? "Running 1 task" : `Running ${runningTasks} tasks`,
        meta: [],
      };
    }
    if (normalizeStatus(agentThread?.status) === "active") {
      return {
        label: agentThread?.status_text ? truncateText(agentThread.status_text, 80) : "Scheduling next step",
        meta: [],
      };
    }
    if (queuedTasks > 0) {
      return {
        label: queuedTasks === 1 ? "1 queued task" : `${queuedTasks} queued tasks`,
        meta: [],
      };
    }
    if (agentThread?.status_text) {
      return {
        label: truncateText(agentThread.status_text, 80),
        meta: [],
      };
    }
    return {
      label: formatTaskStatusLabel(agentThread?.status),
      meta: [],
    };
  }, [formatActionLabel, formatActionMeta]);
  const [selectedAgentThreadId, setSelectedSubRunId] = useState(null);
  const [viewMode, setViewMode] = useState("hierarchy");
  const [collapsedBranches, setCollapsedBranches] = useState(new Set());
  const [detailTab, setDetailTab] = useState("claims");
  const [reviewDraft, setReviewDraft] = useState({
    objective: "",
    question: "",
    constraintsText: "",
    plannedFirstStepsText: "",
    acceptanceCriteria: "",
    recipeOverrideNotes: "",
    reviewNotes: "",
  });

  const focusAreaAgentThreads = useMemo(
    () => agentThreads.filter((agentThread) => agentThread?.scope_type === "focus_area"),
    [agentThreads],
  );
  const sectionAgentThreads = useMemo(
    () => agentThreads.filter((agentThread) => agentThread?.scope_type === "section"),
    [agentThreads],
  );
  const childSectionLabelsByFocusId = useMemo(() => {
    const mapping = new Map();
    focusAreaAgentThreads.forEach((focusSubRun) => {
      const labels = sectionAgentThreads
        .filter((sectionSubRun) => toArray(focusSubRun?.section_keys).includes(sectionSubRun?.scope_key))
        .map((sectionSubRun) => formatSubRunLabel(sectionSubRun));
      mapping.set(focusSubRun?.id, uniqueStrings(labels));
    });
    return mapping;
  }, [focusAreaAgentThreads, sectionAgentThreads]);
  const parentFocusLabelsBySectionId = useMemo(() => {
    const mapping = new Map();
    sectionAgentThreads.forEach((sectionSubRun) => {
      const labels = focusAreaAgentThreads
        .filter((focusSubRun) => toArray(focusSubRun?.section_keys).includes(sectionSubRun?.scope_key))
        .map((focusSubRun) => formatSubRunLabel(focusSubRun));
      mapping.set(sectionSubRun?.id, uniqueStrings(labels));
    });
    return mapping;
  }, [focusAreaAgentThreads, sectionAgentThreads]);
  const primaryParentFocusIdBySectionId = useMemo(() => {
    const mapping = new Map();
    sectionAgentThreads.forEach((sectionSubRun) => {
      const parentFocus =
        focusAreaAgentThreads.find((focusSubRun) => toArray(focusSubRun?.section_keys).includes(sectionSubRun?.scope_key)) ?? null;
      if (parentFocus?.id && sectionSubRun?.id) {
        mapping.set(sectionSubRun.id, parentFocus.id);
      }
    });
    return mapping;
  }, [focusAreaAgentThreads, sectionAgentThreads]);
  const orderAgentThreadsForDisplay = useCallback(
    (items = []) => {
      const itemList = toArray(items).filter(Boolean);
      if (itemList.length < 2) {
        return itemList;
      }
      const itemById = new Map(itemList.filter((item) => item?.id).map((item) => [item.id, item]));
      const ordered = [];
      const seen = new Set();
      const pushItem = (item) => {
        if (!item?.id || seen.has(item.id)) {
          return;
        }
        seen.add(item.id);
        ordered.push(item);
      };

      focusAreaAgentThreads.forEach((focusSubRun) => {
        const focusItem = itemById.get(focusSubRun?.id);
        if (!focusItem) {
          return;
        }
        pushItem(focusItem);
        sectionAgentThreads.forEach((sectionSubRun) => {
          if (primaryParentFocusIdBySectionId.get(sectionSubRun?.id) !== focusSubRun?.id) {
            return;
          }
          pushItem(itemById.get(sectionSubRun?.id));
        });
      });

      itemList.forEach(pushItem);
      return ordered;
    },
    [focusAreaAgentThreads, primaryParentFocusIdBySectionId, sectionAgentThreads],
  );
  const agentThreadById = useMemo(
    () => new Map(agentThreads.filter((entry) => entry?.id).map((entry) => [entry.id, entry])),
    [agentThreads],
  );
  const allAgentThreadById = useMemo(
    () => new Map(allAgentThreads.filter((entry) => entry?.id).map((entry) => [entry.id, entry])),
    [allAgentThreads],
  );
  const getAgentThreadClaimIds = useCallback(
    (agentThread) =>
      uniqueStrings([
        ...toArray(agentThread?.produced_claim_ids),
        ...toArray(agentThread?.claim_ids),
        ...toArray(agentThread?.graph_writes?.claim_ids),
      ]),
    [],
  );
  const directClaimCountBySubRunId = useMemo(() => {
    const counts = new Map();
    agentThreads.forEach((agentThread) => {
      if (!agentThread?.id) {
        return;
      }
      const explicitClaimIds = new Set(getAgentThreadClaimIds(agentThread));
      let count = explicitClaimIds.size;
      claims.forEach((claim) => {
        if (!claim?.id || explicitClaimIds.has(claim.id)) {
          return;
        }
        if (
          toArray(claim?.originating_agent_ids).includes(agentThread.id) ||
          toArray(claim?.originating_unit_ids).includes(agentThread.id) ||
          toArray(claim?.originating_agent_thread_ids).includes(agentThread.id)
        ) {
          count += 1;
        } else if (resolvedTaskObjectiveAgentIdByClaimId.get(claim.id) === agentThread.id) {
          count += 1;
        }
      });
      counts.set(agentThread.id, count);
    });
    return counts;
  }, [claims, agentThreads, getAgentThreadClaimIds, resolvedTaskObjectiveAgentIdByClaimId]);
  const loadedDirectClaimCountBySubRunId = useMemo(() => {
    const counts = new Map();
    agentThreads.forEach((agentThread) => {
      if (!agentThread?.id) {
        return;
      }
      let count = 0;
      claims.forEach((claim) => {
        if (!claim?.id) {
          return;
        }
        if (
          toArray(claim?.originating_agent_ids).includes(agentThread.id) ||
          toArray(claim?.originating_unit_ids).includes(agentThread.id) ||
          toArray(claim?.originating_agent_thread_ids).includes(agentThread.id) ||
          getAgentThreadClaimIds(agentThread).includes(claim.id) ||
          resolvedTaskObjectiveAgentIdByClaimId.get(claim.id) === agentThread.id
        ) {
          count += 1;
        }
      });
      counts.set(agentThread.id, count);
    });
    return counts;
  }, [claims, agentThreads, getAgentThreadClaimIds, resolvedTaskObjectiveAgentIdByClaimId]);
  const taskStatusCountsBySubRunId = useMemo(() => {
    const counts = new Map();
    agentThreads.forEach((agentThread) => {
      if (!agentThread?.id) return;
      const summary = { total: 0, active: 0, open: 0, closed: 0, blocked: 0, skipped: 0 };
      tasks.forEach((task) => {
        if (!(task?.unit_id === agentThread.id || task?.agent_thread_id === agentThread.id)) {
          return;
        }
        const normalized = normalizeStatus(task?.status);
        summary.total += 1;
        if (Object.prototype.hasOwnProperty.call(summary, normalized)) {
          summary[normalized] += 1;
        }
      });
      counts.set(agentThread.id, summary);
    });
    return counts;
  }, [agentThreads, tasks]);
  const taskReferences = useMemo(
    () => buildResearchReferenceMaps({
      research_questions: questions,
      research_objectives: objectives,
    }),
    [objectives, questions],
  );
  const taskBelongsToAgentThread = useCallback(
    (task, agentThreadId) => {
      if (!task || !agentThreadId) return false;
      return (
        task?.agent_id === agentThreadId ||
        task?.unit_id === agentThreadId ||
        task?.agent_thread_id === agentThreadId
      );
    },
    [],
  );
  const tasksByAgentThreadId = useMemo(() => {
    const mapping = new Map();
    agentThreads.forEach((agentThread) => {
      if (!agentThread?.id) return;
      const executionTaskIds = new Set(
        uniqueStrings([
          ...toArray(agentThread?.execution?.queued_task_ids),
          ...toArray(agentThread?.execution?.active_task_ids),
          ...toArray(agentThread?.execution?.completed_task_ids),
        ]),
      );
      const executionObjectiveIds = new Set(uniqueStrings(toArray(agentThread?.objective_ids)));
      const executionQuestionIds = new Set(uniqueStrings(toArray(agentThread?.question_ids)));
      mapping.set(
        agentThread.id,
        tasks.filter((task) => (
          taskBelongsToAgentThread(task, agentThread.id) ||
          executionTaskIds.has(task?.id) ||
          executionObjectiveIds.has(task?.objective_id) ||
          executionQuestionIds.has(task?.research_question_id) ||
          executionQuestionIds.has(task?.question_id)
        )),
      );
    });
    return mapping;
  }, [agentThreads, taskBelongsToAgentThread, tasks]);
  const recentTaskActivityByAgentThreadId = useMemo(() => {
    const mapping = new Map();
    agentThreads.forEach((agentThread) => {
      if (!agentThread?.id) return;
      const entries = toArray(tasksByAgentThreadId.get(agentThread.id))
        .flatMap((task) => {
          const normalizedStatus = normalizeTaskActivityStatus(task?.status);
          const baseLabel = summarizeTaskLabel(task, taskReferences);
          const statusWeight =
            normalizedStatus === "running"
              ? 0
              : normalizedStatus === "queued"
                ? 1
                : normalizedStatus === "blocked"
                  ? 2
                  : normalizedStatus === "completed"
                    ? 3
                    : normalizedStatus === "skipped"
                      ? 4
                      : 5;
          const runtimeEvents = toArray(task?.runtime?.events).slice(-10);
          if (runtimeEvents.length > 0) {
            return runtimeEvents.map((event, index) => {
              const sortTimestamp = String(
                event?.at ??
                task?.runtime?.updated_at ??
                task?.updated_at ??
                task?.started_at ??
                task?.created_at ??
                "",
              ) || "";
              return {
                id: `${task?.id ?? agentThread.id}:${event?.at ?? index}:${index}`,
                task,
                label: event?.detail || baseLabel,
                detail: event?.detail && event?.detail !== baseLabel ? baseLabel : null,
                status: normalizedStatus,
                timestamp: event?.at ?? task?.runtime?.updated_at ?? task?.updated_at ?? null,
                sortKey: `${String(statusWeight).padStart(2, "0")}|${sortTimestamp}`,
              };
            });
          }
          const sortTimestamp =
            String(
              task?.runtime?.updated_at ??
              task?.started_at ??
              task?.completed_at ??
              task?.updated_at ??
              task?.created_at ??
              "",
            ) || "";
          return [{
            id: task?.id ?? `${agentThread.id}_${sortTimestamp}`,
            task,
            label: task?.runtime?.detail || baseLabel,
            detail: task?.runtime?.detail && task?.runtime?.detail !== baseLabel ? baseLabel : null,
            status: normalizedStatus,
            timestamp:
              task?.runtime?.updated_at ??
              task?.started_at ??
              task?.completed_at ??
              task?.updated_at ??
              task?.created_at ??
              null,
            sortKey: `${String(statusWeight).padStart(2, "0")}|${sortTimestamp}`,
          }];
        })
        .sort((left, right) => {
          if (left.sortKey === right.sortKey) {
            return String(right.id ?? "").localeCompare(String(left.id ?? ""));
          }
          return left.sortKey.localeCompare(right.sortKey);
        })
        .slice(0, 10);
      mapping.set(agentThread.id, entries);
    });
    return mapping;
  }, [agentThreads, taskReferences, tasksByAgentThreadId]);
  const questionStatusCountsBySubRunId = useMemo(() => {
    const counts = new Map();
    agentThreads.forEach((agentThread) => {
      if (!agentThread?.id) return;
      const selectedAgentThreadStatus = normalizeStatus(agentThread?.status);
      const selectedAgentThreadIsTerminal = ["closed", "blocked", "skipped"].includes(selectedAgentThreadStatus);
      const scopeSectionKeys = uniqueStrings([
        ...toArray(agentThread?.section_keys),
        agentThread?.scope_type === "section" ? agentThread?.scope_key : null,
      ]);
      const objectiveIds = uniqueStrings([
        ...toArray(agentThread?.objective_ids),
        ...tasks
          .filter((task) => taskBelongsToAgentThread(task, agentThread.id))
          .map((task) => task?.objective_id)
          .filter(Boolean),
      ]);
      const directQuestionIds = uniqueStrings([
        ...toArray(agentThread?.question_ids),
        ...tasks
          .filter((task) => {
            return (
              taskBelongsToAgentThread(task, agentThread.id) ||
              objectiveIds.includes(task?.objective_id) ||
              toArray(agentThread?.question_ids).includes(task?.research_question_id) ||
              toArray(agentThread?.question_ids).includes(task?.question_id)
            );
          })
          .map((task) => task?.research_question_id ?? task?.question_id)
          .filter(Boolean),
      ]);
      const directMatches = questions.filter((question) => {
        return (
          directQuestionIds.includes(question?.id) ||
          objectiveIds.includes(question?.objective_id)
        );
      });
      const effectiveQuestions =
        directMatches.length || selectedAgentThreadIsTerminal
          ? directMatches
          : questions.filter((question) => {
              const questionSectionKeys = uniqueStrings([
                ...(Array.isArray(question?.section_keys) ? question.section_keys : []),
                ...(Array.isArray(question?.linked_section_keys) ? question.linked_section_keys : []),
              ]);
              return (
                directMatches.includes(question) ||
                questionSectionKeys.some((sectionKey) => scopeSectionKeys.includes(sectionKey))
              );
            });
      const summary = {
        total: effectiveQuestions.length,
        loaded: effectiveQuestions.length,
        active: 0,
        open: 0,
        closed: 0,
        blocked: 0,
        skipped: 0,
      };
      effectiveQuestions.forEach((question) => {
        const normalized = normalizeStatus(question?.status);
        if (Object.prototype.hasOwnProperty.call(summary, normalized)) {
          summary[normalized] += 1;
        }
      });
      counts.set(agentThread.id, summary);
    });
    return counts;
  }, [questions, agentThreads, taskBelongsToAgentThread, tasks]);
  const questionsByAgentThreadId = useMemo(() => {
    const mapping = new Map();
    agentThreads.forEach((agentThread) => {
      if (!agentThread?.id) return;
      const selectedAgentThreadStatus = normalizeStatus(agentThread?.status);
      const selectedAgentThreadIsTerminal = ["closed", "blocked", "skipped"].includes(selectedAgentThreadStatus);
      const scopeSectionKeys = uniqueStrings([
        ...toArray(agentThread?.section_keys),
        agentThread?.scope_type === "section" ? agentThread?.scope_key : null,
      ]);
      const linkedTasks = tasksByAgentThreadId.get(agentThread.id) ?? [];
      const objectiveIds = uniqueStrings([
        ...toArray(agentThread?.objective_ids),
        ...linkedTasks.map((task) => task?.objective_id).filter(Boolean),
      ]);
      const directQuestionIds = uniqueStrings([
        ...toArray(agentThread?.question_ids),
        ...linkedTasks
          .map((task) => task?.research_question_id ?? task?.question_id)
          .filter(Boolean),
      ]);
      const directMatches = questions.filter((question) =>
        question?.agent_id === agentThread?.id ||
        directQuestionIds.includes(question?.id) ||
        objectiveIds.includes(question?.objective_id),
      );
      const effectiveQuestions =
        directMatches.length || selectedAgentThreadIsTerminal
          ? directMatches
          : questions.filter((question) => {
              const questionSectionKeys = uniqueStrings([
                ...(Array.isArray(question?.section_keys) ? question.section_keys : []),
                ...(Array.isArray(question?.linked_section_keys) ? question.linked_section_keys : []),
              ]);
              return questionSectionKeys.some((sectionKey) => scopeSectionKeys.includes(sectionKey));
            });
      mapping.set(agentThread.id, effectiveQuestions);
    });
    return mapping;
  }, [agentThreads, questions, tasksByAgentThreadId]);
  const objectivesByAgentThreadId = useMemo(() => {
    const mapping = new Map();
    agentThreads.forEach((agentThread) => {
      if (!agentThread?.id) return;
      const selectedAgentThreadStatus = normalizeStatus(agentThread?.status);
      const selectedAgentThreadIsTerminal = ["closed", "blocked", "skipped", "completed"].includes(selectedAgentThreadStatus);
      const scopeSectionKeys = uniqueStrings([
        ...toArray(agentThread?.section_keys),
        agentThread?.scope_type === "section" ? agentThread?.scope_key : null,
      ]);
      const linkedTasks = tasksByAgentThreadId.get(agentThread.id) ?? [];
      const directObjectiveIds = uniqueStrings([
        ...toArray(agentThread?.objective_ids),
        ...linkedTasks.map((task) => task?.objective_id).filter(Boolean),
      ]);
      const directMatches = objectives.filter((objective) =>
        objective?.agent_id === agentThread?.id ||
        directObjectiveIds.includes(objective?.id),
      );
      const effectiveObjectives =
        directMatches.length || selectedAgentThreadIsTerminal
          ? directMatches
          : objectives.filter((objective) => {
              const objectiveSectionKeys = uniqueStrings(toArray(objective?.section_keys));
              return objectiveSectionKeys.some((sectionKey) => scopeSectionKeys.includes(sectionKey));
            });
      mapping.set(agentThread.id, effectiveObjectives);
    });
    return mapping;
  }, [agentThreads, objectives, tasksByAgentThreadId]);
  const reviewGateAgentIdsByBlockedAgentId = useMemo(() => new Map(), []);
  const getUnderlyingAgentThread = useCallback(
    (agentThread = null) => {
      const normalizedId = String(agentThread?.id ?? "").trim();
      if (!normalizedId) {
        return agentThread ?? null;
      }
      return agentThreadById.get(normalizedId) ?? agentThread ?? null;
    },
    [agentThreadById],
  );


  const selectableAgentThreads = useMemo(
    () => uniqueObjectsBy(
      [
        ...agentThreads,
        ...hierarchyAgentNodes.filter(
          (entry) => entry?.id && !String(entry.id).startsWith("display-group:"),
        ),
      ].filter(Boolean),
      (agentThread) => agentThread?.id ?? `${agentThread?.scope_type}_${agentThread?.scope_key}`,
    ),
    [agentThreads, hierarchyAgentNodes],
  );

  useEffect(() => {
    if (!selectableAgentThreads.length) {
      setSelectedSubRunId(null);
      return;
    }
    if (selectedAgentThreadId && selectableAgentThreads.some((agentThread) => agentThread?.id === selectedAgentThreadId)) {
      return;
    }
    setSelectedSubRunId(selectableAgentThreads[0]?.id ?? null);
  }, [selectableAgentThreads, selectedAgentThreadId]);

  const selectedAgentThread = useMemo(
    () => selectableAgentThreads.find((agentThread) => agentThread?.id === selectedAgentThreadId) ?? selectableAgentThreads[0] ?? null,
    [selectableAgentThreads, selectedAgentThreadId],
  );
  const isActuallyReviewableAgentThread = useCallback(
    (agentThread = null) => {
      const targetAgent = getUnderlyingAgentThread(agentThread);
      if (!targetAgent || !isActionableAgentThread(targetAgent)) {
        return false;
      }
      return Boolean(
        reviewableAwaitingAgentIds.has(String(targetAgent?.id ?? "").trim()) &&
        normalizeAgentLifecycleStatus(targetAgent?.status) === "awaiting_review" &&
        targetAgent?.reviewable === true
      );
    },
    [
      getUnderlyingAgentThread,
      isActionableAgentThread,
      normalizeAgentLifecycleStatus,
      reviewableAwaitingAgentIds,
    ],
  );
  const selectedReviewTargetAgent = useMemo(() => {
    return isActuallyReviewableAgentThread(selectedAgentThread)
      ? getUnderlyingAgentThread(selectedAgentThread)
      : null;
  }, [getUnderlyingAgentThread, isActuallyReviewableAgentThread, selectedAgentThread]);
  const selectedAgentThreadReviewSeed = useMemo(() => {
    const reviewTarget = selectedReviewTargetAgent ?? selectedAgentThread;
    const workPackage = reviewTarget?.work_package ?? {};
    return {
      objective: String(workPackage?.objective ?? "").trim(),
      question: String(workPackage?.question ?? "").trim(),
      constraintsText: uniqueStrings(toArray(workPackage?.constraints)).join("\n"),
      plannedFirstStepsText: uniqueStrings(toArray(workPackage?.planned_first_steps)).join("\n"),
      acceptanceCriteria: String(workPackage?.acceptance_criteria ?? "").trim(),
      recipeOverrideNotes: String(workPackage?.recipe_override_notes ?? "").trim(),
      reviewNotes: String(reviewTarget?.review_notes ?? "").trim(),
    };
  }, [
    selectedAgentThread?.id,
    selectedReviewTargetAgent?.id,
    selectedReviewTargetAgent?.review_notes,
    selectedReviewTargetAgent?.work_package?.objective,
    selectedReviewTargetAgent?.work_package?.question,
    selectedReviewTargetAgent?.work_package?.constraints,
    selectedReviewTargetAgent?.work_package?.planned_first_steps,
    selectedReviewTargetAgent?.work_package?.acceptance_criteria,
    selectedReviewTargetAgent?.work_package?.recipe_override_notes,
  ]);
  useEffect(() => {
    setReviewDraft((previous) => {
      const keys = Object.keys(selectedAgentThreadReviewSeed);
      const unchanged = keys.every((key) => previous?.[key] === selectedAgentThreadReviewSeed[key]);
      return unchanged ? previous : selectedAgentThreadReviewSeed;
    });
  }, [selectedAgentThreadReviewSeed]);

  const selectedScopeKeySet = useMemo(() => {
    if (!selectedAgentThread) return new Set();
    return new Set(
      uniqueStrings([
        ...toArray(selectedAgentThread?.section_keys),
        selectedAgentThread?.scope_type === "section" ? selectedAgentThread?.scope_key : null,
      ]),
    );
  }, [selectedAgentThread]);
  const taskMatchesSelectedUnit = useCallback(
    (task) => {
      if (!selectedAgentThread?.id || !task) return false;
      return (
        task?.agent_id === selectedAgentThread.id ||
        task?.unit_id === selectedAgentThread.id ||
        task?.agent_thread_id === selectedAgentThread.id
      );
    },
    [selectedAgentThread],
  );
  const claimMatchesSelectedUnit = useCallback(
    (claim, unitId = selectedAgentThread?.id) => {
      if (!unitId || !claim) return false;
      return (
        toArray(claim?.originating_agent_ids).includes(unitId) ||
        toArray(claim?.originating_unit_ids).includes(unitId) ||
        toArray(claim?.originating_agent_thread_ids).includes(unitId)
      );
    },
    [selectedAgentThread],
  );
  const derivedObjectiveIdsForSelectedUnit = useMemo(
    () =>
      uniqueStrings(
        tasks
          .filter((task) => taskMatchesSelectedUnit(task))
          .map((task) => task?.objective_id)
          .filter(Boolean),
      ),
    [taskMatchesSelectedUnit, tasks],
  );
  const derivedQuestionIdsForSelectedUnit = useMemo(
    () =>
      uniqueStrings(
        tasks
          .filter((task) => taskMatchesSelectedUnit(task))
          .map((task) => task?.research_question_id ?? task?.question_id)
          .filter(Boolean),
      ),
    [taskMatchesSelectedUnit, tasks],
  );
  const selectedQuestionIdSet = useMemo(
    () => new Set(uniqueStrings([...toArray(selectedAgentThread?.question_ids), ...derivedQuestionIdsForSelectedUnit])),
    [derivedQuestionIdsForSelectedUnit, selectedAgentThread],
  );
  const selectedObjectiveIdSet = useMemo(
    () => new Set(uniqueStrings([...toArray(selectedAgentThread?.objective_ids), ...derivedObjectiveIdsForSelectedUnit])),
    [derivedObjectiveIdsForSelectedUnit, selectedAgentThread],
  );
  const selectedClaimIdSet = useMemo(
    () => new Set(getAgentThreadClaimIds(selectedAgentThread)),
    [getAgentThreadClaimIds, selectedAgentThread],
  );
  const selectedAgentThreadStatus = normalizeStatus(selectedAgentThread?.status);
  const selectedAgentThreadIsTerminal = ["closed", "completed", "blocked", "skipped"].includes(selectedAgentThreadStatus);
  const selectedAgentThreadCanReview = Boolean(selectedReviewTargetAgent);
  const selectedAgentThreadAwaitingInheritedReview = Boolean(
    selectedAgentThread &&
      normalizeAgentLifecycleStatus(selectedAgentThread?.status) === "awaiting_review" &&
      !selectedAgentThreadCanReview,
  );
  const selectedAgentThreadReviewSubmitting = Boolean(
    selectedReviewTargetAgent?.id &&
      reviewSubmittingAgentId &&
      selectedReviewTargetAgent.id === reviewSubmittingAgentId,
  );
  const directLinkedClaimIds = useMemo(
    () =>
      uniqueStrings([
        ...getAgentThreadClaimIds(selectedAgentThread),
        ...claims
          .filter((claim) => claimMatchesSelectedUnit(claim))
          .map((claim) => claim?.id)
          .filter(Boolean),
      ]),
    [claimMatchesSelectedUnit, claims, getAgentThreadClaimIds, selectedAgentThread],
  );
  const directLoadedClaimIds = useMemo(
    () =>
      new Set(
        claims
          .filter((claim) =>
            claim?.id &&
            (selectedClaimIdSet.has(claim.id) || claimMatchesSelectedUnit(claim)),
          )
          .map((claim) => claim.id),
      ),
    [claimMatchesSelectedUnit, claims, selectedClaimIdSet],
  );
  const unresolvedDirectClaimIds = useMemo(
    () => directLinkedClaimIds.filter((claimId) => !directLoadedClaimIds.has(claimId)),
    [directLinkedClaimIds, directLoadedClaimIds],
  );

  const describePlannedQuestionAction = useCallback((question = {}) => {
    const resolution = String(question?.recommended_resolution ?? "").trim().toLowerCase();
    if (resolution === "grounded_query") return "Preparing grounded search";
    if (resolution === "search_google") return "Preparing web search";
    if (resolution === "verify_claim") return "Preparing claim verification";
    if (resolution === "local_rag" || resolution === "local_rag_claims") return "Checking internal evidence";
    if (resolution === "fetch_page") return "Preparing source extraction";
    if (resolution === "synthesize_from_state") return "Preparing synthesis";
    return "Planning next research step";
  }, []);

  const describeAgentWork = useCallback((agentThread = {}, linkedAgent = null) => {
    const activeTasks = toArray(tasksByAgentThreadId.get(agentThread?.id)).filter((task) =>
      ["running", "active", "in_progress"].includes(normalizeStatus(task?.status)),
    );
    const queuedTasks = toArray(tasksByAgentThreadId.get(agentThread?.id)).filter((task) =>
      normalizeStatus(task?.status) === "open",
    );
    const recentCompletedTasks = toArray(tasksByAgentThreadId.get(agentThread?.id))
      .filter((task) => normalizeStatus(task?.status) === "closed")
      .sort((left, right) =>
        String(right?.completed_at ?? right?.updated_at ?? right?.started_at ?? "")
          .localeCompare(String(left?.completed_at ?? left?.updated_at ?? left?.started_at ?? "")),
      );
    const openQuestions = toArray(questionsByAgentThreadId.get(agentThread?.id)).filter((question) =>
      ["open", "active", "planned"].includes(normalizeStatus(question?.status)),
    );

    const liveAction = linkedAgent?.action ?? agentThread?.current_action ?? null;
    if (activeTasks.length > 0) {
      return {
        label: summarizeTaskLabel(activeTasks[0], taskReferences),
        meta: activeTasks.length > 1 ? [`${activeTasks.length} tasks in flight`] : [],
      };
    }
    const actionLabel = formatActionLabel(liveAction);
    if (actionLabel && actionLabel.toLowerCase() !== "run tasks") {
      return {
        label: actionLabel,
        meta: formatActionMeta(liveAction),
      };
    }
    if (queuedTasks.length > 0) {
      return {
        label: `Next: ${summarizeTaskLabel(queuedTasks[0], taskReferences)}`,
        meta: queuedTasks.length > 1 ? [`${queuedTasks.length} queued tasks`] : [],
      };
    }
    if (recentCompletedTasks.length > 0) {
      return {
        label: `Recently: ${summarizeTaskLabel(recentCompletedTasks[0], taskReferences)}`,
        meta: recentCompletedTasks.length > 1 ? [`${recentCompletedTasks.length} completed tasks`] : [],
      };
    }
    if (openQuestions.length > 0) {
      return {
        label: describePlannedQuestionAction(openQuestions[0]),
        meta: [resolveQuestionReferenceLabel(openQuestions[0])].filter(Boolean),
      };
    }
    return describeSubRunAction(agentThread, linkedAgent);
  }, [describePlannedQuestionAction, describeSubRunAction, formatActionLabel, formatActionMeta, questionsByAgentThreadId, taskReferences, tasksByAgentThreadId]);
  const getAgentStatusReason = useCallback((agentThread = {}) => {
    if (!agentThread?.id) return null;
    const explicitReason = [
      agentThread?.status_reason,
      agentThread?.reason,
      agentThread?.blocked_reason,
      agentThread?.deferred_reason,
      agentThread?.status_text,
      agentThread?.metadata?.status_reason,
      agentThread?.metadata?.reason,
      agentThread?.metadata?.blocked_reason,
      agentThread?.metadata?.deferred_reason,
    ]
      .map((value) => (typeof value === "string" ? value.trim() : null))
      .find(Boolean);
    if (explicitReason) {
      return humanizeObjectiveStatusReason(explicitReason) ?? humanizeQuestionStatusReason(explicitReason) ?? explicitReason;
    }
    const linkedObjectives = toArray(objectivesByAgentThreadId.get(agentThread.id));
    const objectiveReason =
      linkedObjectives
        .map((objective) => getObjectiveStatusReason(objective))
        .find(Boolean) ?? null;
    if (objectiveReason) {
      return objectiveReason;
    }
    const linkedQuestions = toArray(questionsByAgentThreadId.get(agentThread.id));
    const questionReason =
      linkedQuestions
        .map((question) => getQuestionStatusReason(question))
        .find(Boolean) ?? null;
    if (questionReason) {
      return questionReason;
    }
    return null;
  }, [objectivesByAgentThreadId, questionsByAgentThreadId]);
  const selectedAgentThreadStatusReason = useMemo(
    () => (selectedAgentThread ? getAgentStatusReason(selectedAgentThread) : null),
    [getAgentStatusReason, selectedAgentThread],
  );

  const directTasks = useMemo(() => {
    if (!selectedAgentThread) return [];
    return tasks.filter((task) => {
      return (
        taskMatchesSelectedUnit(task) ||
        selectedObjectiveIdSet.has(task?.objective_id) ||
        selectedQuestionIdSet.has(task?.research_question_id) ||
        selectedQuestionIdSet.has(task?.question_id)
      );
    });
  }, [selectedObjectiveIdSet, selectedQuestionIdSet, selectedAgentThread, taskMatchesSelectedUnit, tasks]);

  const relatedTasks = useMemo(() => {
    if (!selectedAgentThread) return [];
    if (directTasks.length || selectedAgentThreadIsTerminal) {
      return directTasks;
    }
    return tasks.filter((task) => {
      const taskSectionKeys = uniqueStrings([
        task?.section_key,
        ...(Array.isArray(task?.section_keys) ? task.section_keys : []),
      ]);
      return (
        directTasks.includes(task) ||
        taskSectionKeys.some((sectionKey) => selectedScopeKeySet.has(sectionKey))
      );
    });
  }, [directTasks, selectedScopeKeySet, selectedAgentThread, selectedAgentThreadIsTerminal, tasks]);

  const relatedQuestionIdSet = useMemo(
    () =>
      new Set(
        uniqueStrings([
          ...Array.from(selectedQuestionIdSet),
          ...relatedTasks.map((task) => task?.research_question_id ?? task?.question_id).filter(Boolean),
        ]),
      ),
    [relatedTasks, selectedQuestionIdSet],
  );

  const relatedQuestions = useMemo(() => {
    if (!selectedAgentThread) return [];
      const directMatches = questions.filter((question) => {
        return (
          question?.agent_id === selectedAgentThread?.id ||
          relatedQuestionIdSet.has(question?.id) ||
          selectedObjectiveIdSet.has(question?.objective_id)
        );
      });
    if (directMatches.length || selectedAgentThreadIsTerminal) {
      return directMatches;
    }
    return questions.filter((question) => {
      const questionSectionKeys = uniqueStrings([
        ...(Array.isArray(question?.section_keys) ? question.section_keys : []),
        ...(Array.isArray(question?.linked_section_keys) ? question.linked_section_keys : []),
      ]);
      return (
        directMatches.includes(question) ||
        questionSectionKeys.some((sectionKey) => selectedScopeKeySet.has(sectionKey))
      );
    });
  }, [questions, relatedQuestionIdSet, selectedObjectiveIdSet, selectedScopeKeySet, selectedAgentThread, selectedAgentThreadIsTerminal]);

  const directClaims = useMemo(() => {
    if (!selectedAgentThread) return [];
    return claims.filter((claim) => {
      return (
        selectedClaimIdSet.has(claim?.id) ||
        claimMatchesSelectedUnit(claim)
      );
    });
  }, [claimMatchesSelectedUnit, claims, selectedClaimIdSet, selectedAgentThread]);

  const relatedClaims = useMemo(() => {
    if (!selectedAgentThread) return [];
    if (directClaims.length || selectedAgentThreadIsTerminal) {
      return directClaims;
    }
    return claims.filter((claim) => {
      const claimSectionKeys = uniqueStrings([
        claim?.primary_section,
        ...(Array.isArray(claim?.section_keys) ? claim.section_keys : []),
        ...(Array.isArray(claim?.candidate_section_keys) ? claim.candidate_section_keys : []),
        claim?.assignment?.primary_section,
        ...(Array.isArray(claim?.assignment?.supporting_section_keys)
          ? claim.assignment.supporting_section_keys
          : []),
      ]);
      return (
        directClaims.includes(claim) ||
        claimSectionKeys.some((sectionKey) => selectedScopeKeySet.has(sectionKey))
      );
    });
  }, [claims, directClaims, selectedScopeKeySet, selectedAgentThread, selectedAgentThreadIsTerminal]);

  const detailTabs = [
    { key: "overview", label: "Overview" },
    { key: "claims", label: `Claims (${Math.max(relatedClaims.length, directLinkedClaimIds.length)})` },
    { key: "questions", label: `Questions (${Math.max(
      relatedQuestions.length,
      toArray(selectedAgentThread?.question_set?.active).length +
        toArray(selectedAgentThread?.question_set?.closed).length +
        toArray(selectedAgentThread?.question_set?.blocked).length,
    )})` },
    { key: "tasks", label: `Tasks (${relatedTasks.length})` },
  ];

  const writerAgents = useMemo(
    () => agentThreads.filter((entry) => entry?.kind === "section_writer"),
    [agentThreads],
  );
  const getEffectiveAgentLifecycleStatus = useCallback(
    (entry) => {
      const directStatus = normalizeAgentLifecycleStatus(entry?.status);
      const execution = entry?.execution ?? {};
      const progress = entry?.progress ?? entry?.coverage ?? {};
      const linkedTasks = toArray(tasksByAgentThreadId.get(entry?.id));
      const hasActiveTask =
        Number(progress?.active_task_count ?? 0) > 0 ||
        toArray(execution?.active_task_ids).length > 0 ||
        hasLiveTaskExecutionSignal(entry?.current_action) ||
        hasLiveTaskExecutionSignal(execution?.current_action) ||
        linkedTasks.some((task) => ["running", "active", "in_progress"].includes(normalizeStatus(task?.status)));
      if (hasActiveTask) return "running";
      const hasQueuedTask =
        Number(progress?.queued_task_count ?? 0) > 0 ||
        toArray(execution?.queued_task_ids).length > 0 ||
        linkedTasks.some((task) => normalizeStatus(task?.status) === "open");
      if (directStatus === "awaiting_review") return "awaiting_review";
      if (directStatus === "ready" || hasQueuedTask) return "ready";
      return directStatus;
    },
    [tasksByAgentThreadId],
  );
  const isAwaitingReviewAgent = useCallback(
    (entry) =>
      isActionableAgentThread(entry) &&
      entry?.kind !== "section_writer" &&
      getEffectiveAgentLifecycleStatus(entry) === "awaiting_review",
    [getEffectiveAgentLifecycleStatus],
  );
  const isReadyAgent = useCallback(
    (entry) =>
      isActionableAgentThread(entry) &&
      entry?.kind !== "section_writer" &&
      ["ready", "ready_to_reason"].includes(getEffectiveAgentLifecycleStatus(entry)),
    [getEffectiveAgentLifecycleStatus],
  );
  const isActiveAgent = useCallback(
    (entry) =>
      isActionableAgentThread(entry) &&
      entry?.kind !== "section_writer" &&
      getEffectiveAgentLifecycleStatus(entry) === "running",
    [getEffectiveAgentLifecycleStatus],
  );
  const isBlockedAgent = useCallback(
    (entry) =>
      isActionableAgentThread(entry) &&
      entry?.kind !== "section_writer" &&
      ["blocked", "awaiting_children"].includes(getEffectiveAgentLifecycleStatus(entry)),
    [getEffectiveAgentLifecycleStatus],
  );
  const isCompletedAgent = useCallback(
    (entry) =>
      isActionableAgentThread(entry) &&
      entry?.kind !== "section_writer" &&
      ["completed", "answered"].includes(getEffectiveAgentLifecycleStatus(entry)),
    [getEffectiveAgentLifecycleStatus],
  );
  const hierarchyLifecycleStatusById = useMemo(() => {
    const statuses = new Map();

    const visitNode = (node = {}, parentDisplayNode = null) => {
      if (!node) {
        return "blocked";
      }

      const linkedSubRun = agentThreadById.get(node?.id) ?? null;
      const displayNode = linkedSubRun ?? node;
      const nodeId = String(displayNode?.id ?? node?.id ?? "").trim();
      const syntheticGroup = nodeId.startsWith("display-group:");
      const redundantWrapper = parentDisplayNode && isRedundantQuestionWrapperNode(node, parentDisplayNode);
      const childItems = toArray(node?.child_items);

      if (syntheticGroup || redundantWrapper) {
        let inheritedStatus = "blocked";
        childItems.forEach((child) => {
          const childStatus = visitNode(child, parentDisplayNode ?? displayNode);
          if (childStatus === "running") {
            inheritedStatus = "running";
          } else if (
            inheritedStatus !== "running" &&
            inheritedStatus !== "awaiting_review" &&
            childStatus === "awaiting_review"
          ) {
            inheritedStatus = "awaiting_review";
          } else if (
            !["running", "awaiting_review"].includes(inheritedStatus) &&
            childStatus === "ready"
          ) {
            inheritedStatus = "ready";
          }
        });
        return inheritedStatus;
      }

      let lifecycleStatus = isActionableAgentThread(displayNode)
        ? getEffectiveAgentLifecycleStatus(displayNode)
        : normalizeAgentLifecycleStatus(displayNode?.status);

      childItems.forEach((child) => {
        const childStatus = visitNode(child, displayNode);
        if (childStatus === "running") {
          lifecycleStatus = "running";
        }
      });

      if (nodeId) {
        statuses.set(nodeId, lifecycleStatus);
      }
      return lifecycleStatus;
    };

    agentHierarchy.forEach((rootNode) => {
      visitNode(rootNode, null);
    });

    return statuses;
  }, [
    agentHierarchy,
    agentThreadById,
    getEffectiveAgentLifecycleStatus,
    isActionableAgentThread,
    isRedundantQuestionWrapperNode,
  ]);
  const flowNodeDepthById = useMemo(() => {
    const depths = new Map();

    const visit = (node = {}, parentDisplayNode = null, depth = 0) => {
      if (!node) {
        return;
      }
      const linkedSubRun = agentThreadById.get(node?.id) ?? null;
      const displayNode = linkedSubRun ?? node;
      const nodeId = String(displayNode?.id ?? node?.id ?? "").trim();
      const syntheticGroup = nodeId.startsWith("display-group:");
      const redundantWrapper = parentDisplayNode && isRedundantQuestionWrapperNode(node, parentDisplayNode);
      const childItems = toArray(node?.child_items);

      if (syntheticGroup || redundantWrapper) {
        childItems.forEach((child) => visit(child, parentDisplayNode ?? displayNode, depth));
        return;
      }

      if (nodeId) {
        depths.set(nodeId, Math.max(depths.get(nodeId) ?? 0, depth));
      }

      childItems.forEach((child) => visit(child, displayNode, depth + 1));
    };

    agentHierarchy.forEach((rootNode) => visit(rootNode, null, 0));
    return depths;
  }, [agentHierarchy, agentThreadById, isRedundantQuestionWrapperNode]);
  const flowExclusiveClaimIdsBySubRunId = useMemo(() => {
    const visibleAgentThreads = agentThreads.filter((agentThread) => flowNodeDepthById.has(agentThread?.id));
    const childIdsByParentId = new Map();
    visibleAgentThreads.forEach((agentThread) => {
      const agentId = String(agentThread?.id ?? "").trim();
      const parentId = String(agentThread?.parent_agent_id ?? "").trim();
      if (!agentId || !parentId) {
        return;
      }
      if (!childIdsByParentId.has(parentId)) {
        childIdsByParentId.set(parentId, []);
      }
      childIdsByParentId.get(parentId).push(agentId);
    });

    const explicitClaimIdsByAgentId = new Map(
      visibleAgentThreads.map((agentThread) => [
        String(agentThread?.id ?? "").trim(),
        new Set(getAgentThreadClaimIds(agentThread)),
      ]),
    );
    const descendantClaimIdsByAgentId = new Map();

    const collectDescendantClaimIds = (agentId) => {
      if (!agentId) {
        return new Set();
      }
      if (descendantClaimIdsByAgentId.has(agentId)) {
        return descendantClaimIdsByAgentId.get(agentId);
      }
      const descendantClaimIds = new Set();
      toArray(childIdsByParentId.get(agentId)).forEach((childId) => {
        uniqueStrings(toArray(explicitClaimIdsByAgentId.get(childId))).forEach((claimId) => descendantClaimIds.add(claimId));
        uniqueStrings(toArray(collectDescendantClaimIds(childId))).forEach((claimId) => descendantClaimIds.add(claimId));
      });
      descendantClaimIdsByAgentId.set(agentId, descendantClaimIds);
      return descendantClaimIds;
    };

    const mapping = new Map();
    visibleAgentThreads.forEach((agentThread) => {
      const agentId = String(agentThread?.id ?? "").trim();
      if (!agentId) {
        return;
      }
      const explicitClaimIds = new Set(uniqueStrings(toArray(explicitClaimIdsByAgentId.get(agentId))));
      const descendantClaimIds = collectDescendantClaimIds(agentId);
      const exclusiveClaimIds = uniqueStrings(
        Array.from(explicitClaimIds).filter((claimId) => !descendantClaimIds.has(claimId)),
      );
      mapping.set(agentId, exclusiveClaimIds);
    });
    return mapping;
  }, [agentThreads, flowNodeDepthById, getAgentThreadClaimIds]);
  const allFlowAgents = useMemo(() => {
    const byId = new Map();
    agentThreads.forEach((agent) => {
      if (agent?.id) byId.set(agent.id, agent);
    });
    allAgentThreads.forEach((agent) => {
      if (agent?.id) byId.set(agent.id, agent);
    });
    return Array.from(byId.values());
  }, [agentThreads, allAgentThreads]);

  const flowClaimProducerAgentIdByClaimId = useMemo(() => {
    const mapping = new Map();
    const visibleFlowAgentIds = new Set(allFlowAgents.map((agentThread) => String(agentThread?.id ?? "").trim()).filter(Boolean));

    allFlowAgents.forEach((agentThread) => {
      const agentId = String(agentThread?.id ?? "").trim();
      if (!agentId) {
        return;
      }
      uniqueStrings(toArray(agentThread?.produced_claim_ids)).forEach((claimId) => {
        if (claimId && !mapping.has(String(claimId))) {
          mapping.set(String(claimId), agentId);
        }
      });
    });

    claims.forEach((claim) => {
      const claimId = String(claim?.id ?? "").trim();
      if (!claimId || mapping.has(claimId)) {
        return;
      }
      const producerAgentId = uniqueStrings([
        ...toArray(claim?.originating_agent_thread_ids),
        ...toArray(claim?.originating_unit_ids),
        ...toArray(claim?.originating_agent_ids),
      ]).find((candidateId) => visibleFlowAgentIds.has(String(candidateId ?? "").trim()));
      if (producerAgentId) {
        mapping.set(claimId, String(producerAgentId));
      }
    });

    return mapping;
  }, [allFlowAgents, claims]);

  const flowClaimsBySubRunId = useMemo(() => {
    const mapping = new Map();
    allFlowAgents.forEach((agentThread) => {
      if (agentThread?.id) {
        mapping.set(agentThread.id, []);
      }
    });

    claims.forEach((claim) => {
      if (!claim?.id) {
        return;
      }
      const producerAgentId = flowClaimProducerAgentIdByClaimId.get(String(claim.id));
      if (!producerAgentId) {
        return;
      }
      const existing = mapping.get(producerAgentId);
      if (!existing) {
        return;
      }
      existing.push(claim);
    });

    return mapping;
  }, [allFlowAgents, claims, flowClaimProducerAgentIdByClaimId]);

  const flowClaimProducerQuestionIdByClaimId = useMemo(() => {
    const mapping = new Map();
    allAgentThreads
      .filter(
        (agentThread) =>
          String(agentThread?.node_role ?? "").trim() === "question",
      )
      .forEach((agentThread) => {
        const questionId = String(agentThread?.id ?? "").trim();
        if (!questionId || !flowNodeDepthById.has(questionId)) {
          return;
        }
        uniqueStrings(toArray(agentThread?.produced_claim_ids)).forEach((claimId) => {
          if (claimId && !mapping.has(claimId)) {
            mapping.set(String(claimId), questionId);
          }
        });
      });

    claims.forEach((claim) => {
      const claimId = String(claim?.id ?? "").trim();
      if (!claimId || mapping.has(claimId)) {
        return;
      }
      const originCandidates = uniqueStrings([
        ...toArray(claim?.originating_agent_thread_ids),
        ...toArray(claim?.originating_unit_ids),
        ...toArray(claim?.originating_agent_ids),
        resolvedTaskObjectiveAgentIdByClaimId.get(claimId) ?? "",
      ]);
      const preferredQuestionId =
        originCandidates.find((candidateId) => String(agentThreadById.get(candidateId)?.node_role ?? "").trim() === "question") ??
        originCandidates[0] ??
        "";
      if (preferredQuestionId) {
        mapping.set(claimId, preferredQuestionId);
      }
    });

    return mapping;
  }, [agentThreadById, allAgentThreads, claims, flowNodeDepthById, resolvedTaskObjectiveAgentIdByClaimId]);
  const reviewAgents = useMemo(
    () => agentThreads.filter(isActuallyReviewableAgentThread),
    [agentThreads, isActuallyReviewableAgentThread],
  );
  const readyAgents = useMemo(
    () => agentThreads.filter(isReadyAgent),
    [agentThreads, isReadyAgent],
  );
  const activeAgents = useMemo(
    () => agentThreads.filter(isActiveAgent),
    [agentThreads, isActiveAgent],
  );
  const blockedAgents = useMemo(
    () => agentThreads.filter(isBlockedAgent),
    [agentThreads, isBlockedAgent],
  );
  const completedAgents = useMemo(
    () => agentThreads.filter(isCompletedAgent),
    [agentThreads, isCompletedAgent],
  );
  const agentColumns = [
    { key: "active", label: "Active", items: activeAgents },
    { key: "review", label: "Review", items: reviewAgents },
    { key: "ready", label: "Ready", items: readyAgents },
    { key: "blocked", label: "Blocked", items: blockedAgents },
    { key: "complete", label: "Complete", items: completedAgents },
  ];
  const reviewCount = Number(reviewAgents.length || 0);
  const writerCount = Number(writerAgents.length || 0);
  const blockedCount = Number(blockedAgents.length || 0);
  const completedCount = Number(completedAgents.length || 0);
  const activeCount = Number(activeAgents.length || 0);
  const readyCount = Number(readyAgents.length || 0);
  const viewTabs = [
    { key: "hierarchy", label: "Hierarchy" },
    { key: "flow", label: "Flow" },
    { key: "review", label: `Review Queue (${reviewCount})` },
    { key: "active", label: `Active (${activeCount})` },
    { key: "ready", label: `Ready (${readyCount})` },
    { key: "blocked", label: `Blocked (${blockedCount})` },
    { key: "complete", label: `Complete (${completedCount})` },
    { key: "writers", label: `Writers (${writerAgents.length})` },
    { key: "board", label: "Board" },
  ];

  const buildQuestionSetPreview = useCallback((agentThread = {}, maxItems = 2) => {
    const questionSet = agentThread?.question_set ?? {};
    return {
      activeEntries: toArray(questionSet?.active)
        .filter((entry) => entry?.question || entry?.id)
        .slice(0, maxItems),
      blockedEntries: toArray(questionSet?.blocked)
        .filter((entry) => entry?.question || entry?.id)
        .slice(0, maxItems),
      counts: {
        active: toArray(questionSet?.active).length,
        closed: toArray(questionSet?.closed).length,
        blocked: toArray(questionSet?.blocked).length,
      },
    };
  }, []);

  const summarizeQuestionSetPreview = useCallback((entries = [], totalCount = 0) => {
    if (!entries.length && !totalCount) return null;
    const labels = entries
      .map((entry) => String(entry?.question ?? entry?.id ?? "").trim())
      .filter(Boolean)
      .slice(0, 2);
    if (!labels.length) {
      return totalCount ? `${totalCount} mission question${totalCount === 1 ? "" : "s"}` : null;
    }
    const remaining = Math.max(totalCount - labels.length, 0);
    return remaining > 0 ? `${labels.join(" · ")} · +${remaining} more` : labels.join(" · ");
  }, []);

  const renderAgentCard = (agentThread) => {
    const lifecycleStatus = getEffectiveAgentLifecycleStatus(agentThread);
    const status = normalizeStatus(agentThread?.status);
    const theme = getKanbanStatusTheme(
      lifecycleStatus === "ready"
        ? "open"
        : lifecycleStatus === "completed"
          ? "closed"
          : lifecycleStatus === "running"
            ? "active"
            : lifecycleStatus,
    );
    const directClaimCount = Number(directClaimCountBySubRunId.get(agentThread?.id) ?? 0);
    const loadedDirectClaimCount = Number(loadedDirectClaimCountBySubRunId.get(agentThread?.id) ?? 0);
    const fallbackClaimCount = Number(agentThread?.coverage?.claim_count ?? getAgentThreadClaimIds(agentThread).length ?? 0);
    const claimCount =
      directClaimCount > 0 || ["closed", "completed", "blocked", "skipped"].includes(status)
        ? directClaimCount
        : fallbackClaimCount;
    const isFocusArea = agentThread?.scope_type === "focus_area";
    const relationshipLabels = isFocusArea
      ? childSectionLabelsByFocusId.get(agentThread?.id) ?? []
      : parentFocusLabelsBySectionId.get(agentThread?.id) ?? [];
    const isChildSection = agentThread?.scope_type === "section" && relationshipLabels.length > 0;
    const claimCountLabel = isFocusArea ? `${claimCount} claims` : `${claimCount} claims`;
    const runningTasks = Number(agentThread?.coverage?.active_task_count ?? 0);
    const queuedTasks = Number(agentThread?.coverage?.queued_task_count ?? 0);
    const taskStatusCounts = taskStatusCountsBySubRunId.get(agentThread?.id) ?? { total: 0, active: 0, open: 0, closed: 0, blocked: 0, skipped: 0 };
    const questionStatusCounts = questionStatusCountsBySubRunId.get(agentThread?.id) ?? {
      total: 0,
      loaded: 0,
      active: 0,
      open: 0,
      closed: 0,
      blocked: 0,
      skipped: 0,
    };
    const actionSummary = describeAgentWork(agentThread, null);
    const recentTaskActivity = toArray(recentTaskActivityByAgentThreadId.get(agentThread?.id));
    const isSelected = selectedAgentThread?.id === agentThread?.id;
    const resultSummaryShort = String(agentThread?.result?.summary_short ?? "").trim();
    const resultStatus = String(agentThread?.result?.status ?? "").trim();
    const nextRecommendations = toArray(agentThread?.result?.next_recommendations);
    const questionSetPreview = buildQuestionSetPreview(agentThread);
    const questionSetCounts = questionSetPreview.counts;
    const questionSetSummaryParts = [
      questionSetCounts.active > 0 ? `${questionSetCounts.active} active` : null,
      questionSetCounts.closed > 0 ? `${questionSetCounts.closed} closed` : null,
      questionSetCounts.blocked > 0 ? `${questionSetCounts.blocked} blocked` : null,
    ].filter(Boolean);
    const questionSetSummary = questionSetSummaryParts.length > 0
      ? `Question set ${questionSetSummaryParts.join(" · ")}`
      : null;
    const activeQuestionPreview = summarizeQuestionSetPreview(questionSetPreview.activeEntries, questionSetCounts.active);
    const blockedQuestionPreview = summarizeQuestionSetPreview(questionSetPreview.blockedEntries, questionSetCounts.blocked);
    const taskSummaryParts = [
      taskStatusCounts.active > 0 ? `${taskStatusCounts.active} active` : null,
      taskStatusCounts.open > 0 || queuedTasks > 0
        ? `${Math.max(taskStatusCounts.open, queuedTasks)} queued`
        : null,
      taskStatusCounts.blocked > 0 ? `${taskStatusCounts.blocked} blocked` : null,
      taskStatusCounts.closed > 0 ? `${taskStatusCounts.closed} done` : null,
    ].filter(Boolean);
    const taskSummary =
      taskStatusCounts.total > 0 || runningTasks > 0 || queuedTasks > 0
        ? `Tasks ${taskSummaryParts.slice(0, 2).join(" · ") || `${taskStatusCounts.total || runningTasks + queuedTasks} total`}`
        : null;
    const questionSummaryParts = [
      questionStatusCounts.active > 0 ? `${questionStatusCounts.active} active` : null,
      questionStatusCounts.open > 0 ? `${questionStatusCounts.open} open` : null,
      questionStatusCounts.blocked > 0 ? `${questionStatusCounts.blocked} blocked` : null,
      questionStatusCounts.closed > 0 ? `${questionStatusCounts.closed} answered` : null,
    ].filter(Boolean);
    const questionSummary =
      questionStatusCounts.total > 0
        ? `Questions ${
            questionSummaryParts.slice(0, 2).join(" · ") ||
            (questionStatusCounts.loaded > 0
              ? `${questionStatusCounts.loaded}/${questionStatusCounts.total} loaded`
              : `${questionStatusCounts.total} linked`)
          }`
        : null;
    const primaryText = getAgentPrimaryText(agentThread);
    const secondaryText = getAgentSecondaryText(agentThread, relationshipLabels);
    const statusReason = getAgentStatusReason(agentThread);
    const normalizedPrimary = normalizeAgentDisplayText(primaryText);
    const normalizedSecondary = normalizeAgentDisplayText(secondaryText);
    const normalizedResultSummaryShort = normalizeAgentDisplayText(resultSummaryShort);
    const normalizedActiveQuestionPreview = normalizeAgentDisplayText(activeQuestionPreview);
    const normalizedActionLabel = normalizeAgentDisplayText(actionSummary?.label);
    const summaryText = resultSummaryShort && normalizedResultSummaryShort !== normalizedPrimary
      ? resultSummaryShort
      : secondaryText && normalizedSecondary !== normalizedPrimary
        ? secondaryText
        : null;
    const showCurrentQuestions =
      activeQuestionPreview &&
      normalizedActiveQuestionPreview !== normalizedPrimary &&
      normalizedActiveQuestionPreview !== normalizedSecondary &&
      normalizedActiveQuestionPreview !== normalizedResultSummaryShort;
    const showBlockedQuestions =
      !showCurrentQuestions &&
      blockedQuestionPreview &&
      lifecycleStatus === "blocked" &&
      normalizeAgentDisplayText(blockedQuestionPreview) !== normalizedPrimary &&
      normalizeAgentDisplayText(blockedQuestionPreview) !== normalizedSecondary &&
      normalizeAgentDisplayText(blockedQuestionPreview) !== normalizedResultSummaryShort;
    const showActionSummary =
      actionSummary?.label &&
      normalizedActionLabel !== normalizedPrimary &&
      normalizedActionLabel !== normalizedSecondary &&
      normalizedActionLabel !== normalizedResultSummaryShort &&
      normalizedActionLabel !== normalizedActiveQuestionPreview;
    const showNextRecommendation =
      nextRecommendations.length > 0 &&
      normalizeAgentDisplayText(String(nextRecommendations[0] ?? "")) !== normalizedActionLabel;
    return (
      <button
        key={agentThread?.id ?? `${agentThread?.scope_type}_${agentThread?.scope_key}`}
        type="button"
        onClick={() => setSelectedSubRunId(agentThread?.id ?? null)}
        className={clsx(
          "group relative w-full overflow-hidden rounded-2xl border bg-white p-4 text-left shadow-sm transition-all hover:shadow-md",
          isChildSection ? "ml-4 w-[calc(100%-1rem)] border-violet-200 bg-violet-50/20" : null,
          isSelected ? "border-slate-900 ring-1 ring-slate-900/10" : "border-slate-200",
        )}
      >
        {isChildSection ? (
          <>
            <div className="absolute -left-4 top-7 h-px w-4 bg-violet-200" aria-hidden="true" />
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-violet-200/70" aria-hidden="true" />
          </>
        ) : null}
        <div className={clsx("absolute inset-y-0 left-0 w-1", theme.barClass)} />
        <div className={clsx(isChildSection ? "pl-5" : "pl-3")}>
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">
                {formatScopeBadgeLabel(agentThread)}
              </p>
              <p className="mt-1 text-sm font-medium leading-snug text-slate-900">
                {primaryText}
              </p>
              {agentThread?.id ? (
                <p className="mt-1 font-mono text-[11px] text-slate-400">
                  {agentThread.id}
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {resultStatus ? (
                <Chip size="sm" variant="flat" color="primary">{titleizeKey(resultStatus)}</Chip>
              ) : null}
              <span className={clsx("inline-flex items-center gap-1.5 rounded-lg px-2 py-0.5 text-[11px] font-medium", theme.badgeClass)}>
                <span className={clsx("h-2 w-2 rounded-full", theme.dotClass)} aria-hidden="true" />
                <span>{formatAgentLifecycleLabel(lifecycleStatus)}</span>
              </span>
            </div>
          </div>
          {summaryText ? (
            <p className="mt-1 text-xs text-slate-600">{truncateText(summaryText, 170)}</p>
          ) : null}
          {showCurrentQuestions ? (
            <p className="mt-1 text-xs text-slate-600">
              <span className="font-semibold">Current questions:</span> {truncateText(activeQuestionPreview, 150)}
            </p>
          ) : null}
          {showBlockedQuestions ? (
            <p className="mt-1 text-xs text-slate-600">
              <span className="font-semibold">Blocked questions:</span> {truncateText(blockedQuestionPreview, 150)}
            </p>
          ) : null}
          {showActionSummary ? (
            <p className="mt-1 text-xs text-slate-500">
              {`Now: ${truncateText(actionSummary.label, 120)}`}
              {actionSummary?.meta?.length ? ` · ${actionSummary.meta.join(" · ")}` : ""}
            </p>
          ) : agentThread?.status_text && agentThread.status_text !== secondaryText ? (
            <p className="mt-1 text-xs text-slate-500">{truncateText(agentThread.status_text, 110)}</p>
          ) : null}
          {showNextRecommendation ? (
            <p className="mt-1 text-xs text-slate-500">
              <span className="font-semibold">Next:</span> {truncateText(String(nextRecommendations[0] ?? ""), 150)}
            </p>
          ) : null}
          {statusReason && lifecycleStatus === "blocked" ? (
            <div className="mt-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-5 text-rose-700">
              <span className="font-semibold">Why blocked:</span> <span>{truncateText(statusReason, 220)}</span>
            </div>
          ) : null}
          {relationshipLabels.length ? (
            <p className="mt-1 text-xs font-medium text-slate-600">
              {isFocusArea
                ? `Covers ${relationshipLabels.length} ${relationshipLabels.length === 1 ? "child area" : "child areas"}: ${truncateText(relationshipLabels.map((entry) => humanizeAgentLabel(entry)).join(" · "), 110)}`
                : `Under ${truncateText(relationshipLabels.map((entry) => humanizeAgentLabel(entry)).join(" · "), 110)}`}
            </p>
          ) : null}
          {recentTaskActivity.length > 0 && (lifecycleStatus === "running" || lifecycleStatus === "ready") ? (
            <div className="mt-2.5 space-y-1.5 rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">
                Recent Task Activity
              </p>
              <div className="space-y-1.5">
                {recentTaskActivity.map((entry) => (
                  <div key={entry.id} className="flex items-start justify-between gap-2 text-xs">
                    <div className="min-w-0">
                      <p className="truncate text-slate-700">{truncateText(entry.label, 110)}</p>
                      {entry.detail ? (
                        <p className="truncate text-[11px] text-slate-400">{truncateText(entry.detail, 110)}</p>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {entry.timestamp ? (
                        <span className="text-[11px] text-slate-400">{formatTimestamp(entry.timestamp)}</span>
                      ) : null}
                      <span
                        className={clsx(
                          "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium",
                          getTaskActivityStatusTheme(entry.status),
                        )}
                      >
                        {formatTaskActivityStatusLabel(entry.status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
          <div className="mt-2.5 flex flex-wrap items-center gap-2 text-[11px]">
            {claimCount > 0 ? (
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700">
                {claimCountLabel}
              </span>
            ) : null}
            {taskSummary ? (
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700">
                {taskSummary}
              </span>
            ) : null}
            {questionSummary ? (
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700">
                {questionSummary}
              </span>
            ) : null}
            {questionSetSummary && questionSetSummary !== questionSummary ? (
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700">
                {questionSetSummary}
              </span>
            ) : null}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-[11px] text-slate-400">
            {formatTimestamp(agentThread?.started_at) ? <span>{`Started ${formatTimestamp(agentThread.started_at)}`}</span> : null}
            {formatTimestamp(agentThread?.updated_at) ? <span>{`Updated ${formatTimestamp(agentThread.updated_at)}`}</span> : null}
            {formatTimestamp(agentThread?.completed_at) ? <span>{`Completed ${formatTimestamp(agentThread.completed_at)}`}</span> : null}
          </div>
        </div>
      </button>
    );
  };

  const renderAgentListSection = (items = [], emptyMessage = "Nothing to show.") => {
    if (!toArray(items).length) {
      return (
        <p className="rounded-2xl border border-dashed border-slate-200 bg-white/80 px-4 py-6 text-center text-sm text-slate-500">
          {emptyMessage}
        </p>
      );
    }
    return <div className="space-y-3">{orderAgentThreadsForDisplay(items).map(renderAgentCard)}</div>;
  };

  const renderHierarchyNode = (node = {}, depth = 0) => {
    const linkedSubRun = agentThreadById.get(node?.id) ?? null;
    const displayNode = linkedSubRun ?? node;
    const isSelected = selectedAgentThread?.id === node?.id;
    const isSyntheticGroup = String(node?.id ?? "").startsWith("display-group:");
    const isOperationalNode = isActionableAgentThread(displayNode);
    const lifecycleStatus =
      hierarchyLifecycleStatusById.get(String(displayNode?.id ?? node?.id ?? "").trim()) ??
      normalizeAgentLifecycleStatus(displayNode?.status);
    const effectiveLifecycleStatus =
      !isOperationalNode && lifecycleStatus === "awaiting_review"
        ? "blocked"
        : lifecycleStatus;
    const status = normalizeStatus(displayNode?.status);
    const theme = getKanbanStatusTheme(
      effectiveLifecycleStatus === "ready"
        ? "open"
        : effectiveLifecycleStatus === "completed"
          ? "closed"
          : effectiveLifecycleStatus === "running"
            ? "active"
            : effectiveLifecycleStatus,
    );
    const childItems = toArray(node?.child_items).filter((child) => !isRedundantQuestionWrapperNode(child, displayNode));
    const primaryText = getAgentPrimaryText(displayNode);
    const secondaryText = getAgentSecondaryText(displayNode);
    const actionSummary = describeAgentWork(displayNode, linkedSubRun);
    const resultSummaryShort = String(displayNode?.result?.summary_short ?? "").trim();
    const resultStatus = String(displayNode?.result?.status ?? "").trim();
    const nextRecommendations = toArray(displayNode?.result?.next_recommendations);
    const hierarchyQuestionPreview = summarizeQuestionSetPreview(
      toArray(displayNode?.question_set?.active).slice(0, 2),
      toArray(displayNode?.question_set?.active).length,
    );
    if (isSyntheticGroup) {
      return (
        <div key={node?.id ?? `${node?.label}_${depth}`} className="space-y-2">
          <div
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-left"
            style={{ marginLeft: `${depth * 18}px`, width: `calc(100% - ${depth * 18}px)` }}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-900">{displayNode?.label ?? "Group"}</p>
                {displayNode?.question ? (
                  <p className="mt-1 text-xs text-slate-500">{displayNode.question}</p>
                ) : null}
              </div>
              <span className={clsx("inline-flex items-center gap-1.5 rounded-lg px-2 py-0.5 text-[11px] font-medium", theme.badgeClass)}>
                <span className={clsx("h-2 w-2 rounded-full", theme.dotClass)} aria-hidden="true" />
                <span>{formatAgentLifecycleLabel(effectiveLifecycleStatus)}</span>
              </span>
            </div>
          </div>
          {childItems.length ? (
            <div className="space-y-2">
              {childItems.map((child) => renderHierarchyNode(child, depth + 1))}
            </div>
          ) : null}
        </div>
      );
    }
    return (
      <div key={node?.id ?? `${node?.label}_${depth}`} className="space-y-2">
        <button
          type="button"
          onClick={() => setSelectedSubRunId(node?.id ?? null)}
          className={clsx(
            "w-full rounded-2xl border bg-white px-4 py-3 text-left shadow-sm transition-colors",
            isSelected ? "border-slate-900 ring-1 ring-slate-900/10" : "border-slate-200 hover:border-slate-300",
          )}
          style={{ marginLeft: `${depth * 18}px`, width: `calc(100% - ${depth * 18}px)` }}
        >
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900">
                {truncateText(primaryText, 140)}
              </p>
              {displayNode?.id ? (
                <p className="mt-1 font-mono text-[11px] text-slate-400">{displayNode.id}</p>
              ) : null}
              {secondaryText ? (
                <p className="mt-1 text-xs text-slate-500">{truncateText(secondaryText, 160)}</p>
              ) : null}
              {resultSummaryShort && resultSummaryShort !== secondaryText ? (
                <p className="mt-1 text-xs font-medium text-slate-600">{truncateText(resultSummaryShort, 180)}</p>
              ) : null}
              {hierarchyQuestionPreview ? (
                <p className="mt-1 text-xs text-slate-600">
                  <span className="font-semibold">Current questions:</span> {truncateText(hierarchyQuestionPreview, 150)}
                </p>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Chip size="sm" variant="flat" color="primary">{formatScopeBadgeLabel(displayNode)}</Chip>
              {resultStatus ? (
                <Chip size="sm" variant="flat">{titleizeKey(resultStatus)}</Chip>
              ) : null}
              <span className={clsx("inline-flex items-center gap-1.5 rounded-lg px-2 py-0.5 text-[11px] font-medium", theme.badgeClass)}>
                <span className={clsx("h-2 w-2 rounded-full", theme.dotClass)} aria-hidden="true" />
                <span>{formatAgentLifecycleLabel(effectiveLifecycleStatus)}</span>
              </span>
            </div>
          </div>
          {actionSummary?.label && actionSummary.label !== secondaryText ? (
            <p className="mt-2 text-xs text-slate-600">
              {truncateText(actionSummary.label, 150)}
              {actionSummary?.meta?.length ? ` · ${actionSummary.meta.join(" · ")}` : ""}
            </p>
          ) : null}
          {nextRecommendations.length > 0 ? (
            <p className="mt-1 text-xs text-slate-500">
              <span className="font-semibold">Next:</span> {truncateText(String(nextRecommendations[0] ?? ""), 150)}
            </p>
          ) : null}
        </button>
        {childItems.length ? (
          <div className="space-y-2">
            {childItems.map((child) => renderHierarchyNode(child, depth + 1))}
          </div>
        ) : null}
      </div>
    );
  };

  const agentFlowGraph = useMemo(() => {
    const graphNodes = [];
    const graphEdges = [];
    const lineageEdges = [];
    const seenNodeIds = new Set();
    const seenEdgeIds = new Set();
    const seenLineageEdgeIds = new Set();
    // Build a set of objective agent IDs that are awaiting review
    const awaitingReviewObjectiveIds = new Set(
      agentThreads
        .filter(
          (agent) =>
            String(agent?.node_role ?? "").trim() === "objective" &&
            normalizeAgentLifecycleStatus(agent?.status) === "awaiting_review",
        )
        .map((agent) => String(agent?.id ?? "").trim())
        .filter(Boolean),
    );
    const visibleFlowAgents = agentThreads.filter((agent) => {
      const agentId = String(agent?.id ?? "").trim();
      if (!agentId) return false;
      // Hide child questions whose parent objective is awaiting review
      const parentId = String(agent?.parent_agent_id ?? "").trim();
      if (parentId && awaitingReviewObjectiveIds.has(parentId)) {
        return false;
      }
      return true;
    });
    const visibleFlowAgentIds = new Set(
      visibleFlowAgents.map((agent) => String(agent?.id ?? "").trim()).filter(Boolean),
    );

    const parseConstraintValue = (constraints = [], prefix = "") =>
      uniqueStrings(toArray(constraints))
        .find((entry) => String(entry).startsWith(prefix))
        ?.slice(prefix.length)
        ?.trim() ?? "";

    const resolveQuestionAgentId = (value) => {
      const rawValue = String(value ?? "").trim();
      if (!rawValue) {
        return "";
      }
      if (rawValue.startsWith("agent:question:")) {
        return rawValue;
      }
      return `agent:question:${rawValue}`;
    };

    const resolveVisibleFlowParentId = (agent = {}) => {
      const sourceAgent = getUnderlyingAgentThread(agent);
      let currentParentId = String(sourceAgent?.parent_agent_id ?? agent?.parent_agent_id ?? "").trim();
      const visited = new Set([String(agent?.id ?? "").trim()]);
      while (currentParentId && !visited.has(currentParentId)) {
        visited.add(currentParentId);
        if (visibleFlowAgentIds.has(currentParentId)) {
          return currentParentId;
        }
        const parentAgent = agentThreadById.get(currentParentId) ?? null;
        currentParentId = String(parentAgent?.parent_agent_id ?? "").trim();
      }
      return null;
    };

    visibleFlowAgents.forEach((displayNode) => {
      const nodeId = String(displayNode?.id ?? "").trim();
      const lifecycleStatus = isActionableAgentThread(displayNode)
        ? getEffectiveAgentLifecycleStatus(displayNode)
        : normalizeAgentLifecycleStatus(displayNode?.status);
      const actionSummary = describeAgentWork(displayNode, displayNode);
      const resultSummaryShort = String(displayNode?.result?.summary_short ?? "").trim();
      const nextRecommendation = String(toArray(displayNode?.result?.next_recommendations)[0] ?? "").trim();
      const questionSetPreview = buildQuestionSetPreview(displayNode, 2);
      const questionPreview = summarizeQuestionSetPreview(
        questionSetPreview.activeEntries,
        questionSetPreview.counts.active,
      );
      const taskStatusCounts = taskStatusCountsBySubRunId.get(nodeId) ?? {};
      const metaParts = [
        Number(taskStatusCounts.active ?? 0) > 0 ? `${taskStatusCounts.active} active tasks` : null,
        Number(taskStatusCounts.open ?? 0) > 0 ? `${taskStatusCounts.open} queued tasks` : null,
        Number(directClaimCountBySubRunId.get(nodeId) ?? 0) > 0
          ? `${Number(directClaimCountBySubRunId.get(nodeId) ?? 0)} claims`
          : null,
      ].filter(Boolean);

      if (!seenNodeIds.has(nodeId)) {
        seenNodeIds.add(nodeId);
        graphNodes.push({
          id: nodeId,
          title: getAgentPrimaryText(displayNode),
          scopeLabel: formatScopeBadgeLabel(displayNode),
          lifecycleStatus,
          lifecycleLabel: formatAgentLifecycleLabel(lifecycleStatus),
          summary: resultSummaryShort || getAgentSecondaryText(displayNode) || actionSummary?.label || "",
          questionPreview,
          nextStep: nextRecommendation || actionSummary?.label || "",
          meta: metaParts.join(" · "),
        });
      }

      const resolvedParentVisibleId = resolveVisibleFlowParentId(displayNode);
      if (resolvedParentVisibleId && resolvedParentVisibleId !== nodeId) {
        const edgeId = `${resolvedParentVisibleId}:${nodeId}`;
        if (!seenEdgeIds.has(edgeId)) {
          seenEdgeIds.add(edgeId);
          graphEdges.push({ from: resolvedParentVisibleId, to: nodeId });
        }
      }

      if (String(displayNode?.node_role ?? "").trim() === "objective") {
        const primaryQuestionId = resolveQuestionAgentId(displayNode?.primary_question_id);
        const primaryQuestion =
          allAgentThreadById.get(primaryQuestionId) ??
          agentThreadById.get(primaryQuestionId) ??
          null;
        const parentClaimId =
          parseConstraintValue(primaryQuestion?.work_package?.constraints, "parent_claim:") ||
          String(primaryQuestion?.parent_claim_id ?? "").trim();
        const sourceQuestionId = flowClaimProducerQuestionIdByClaimId.get(parentClaimId) ?? "";
        if (
          sourceQuestionId &&
          sourceQuestionId !== nodeId &&
          sourceQuestionId !== resolvedParentVisibleId &&
          visibleFlowAgentIds.has(sourceQuestionId)
        ) {
          const lineageEdgeId = `${sourceQuestionId}:${nodeId}`;
          if (!seenLineageEdgeIds.has(lineageEdgeId)) {
            seenLineageEdgeIds.add(lineageEdgeId);
            lineageEdges.push({ from: sourceQuestionId, to: nodeId, claimId: parentClaimId });
          }
        }
      }

      const nodeClaimIds = new Set();
      toArray(flowClaimsBySubRunId.get(nodeId)).forEach((claim) => {
          const claimId = String(claim?.id ?? "").trim();
          if (!claimId || nodeClaimIds.has(claimId)) {
            return;
          }
          nodeClaimIds.add(claimId);
          const claimNodeId = `claim:${claimId}`;
          if (!seenNodeIds.has(claimNodeId)) {
            seenNodeIds.add(claimNodeId);
            const confidenceValue = Number(claim?.assignment?.confidence ?? claim?.confidence);
            const claimSection = claim?.assignment?.primary_section ?? claim?.primary_section ?? null;
            const claimMetaParts = [
              claim?.type ? titleizeKey(claim.type) : null,
              claimSection ? humanizeKey(claimSection) : null,
              Number.isFinite(confidenceValue) ? `Confidence ${confidenceValue.toFixed(2)}` : null,
            ].filter(Boolean);
            graphNodes.push({
              id: claimNodeId,
              selectId: nodeId,
              title: String(claim?.text ?? "").trim() || claimId,
              scopeLabel: "Claim",
              lifecycleStatus: "claim",
              lifecycleLabel: "Claim",
              summary: "",
              questionPreview: "",
              nextStep: "",
              meta: claimMetaParts.join(" · "),
              width: 240,
              height: 132,
            });
          }
          const claimEdgeId = `${nodeId}:${claimNodeId}`;
          if (!seenEdgeIds.has(claimEdgeId)) {
            seenEdgeIds.add(claimEdgeId);
            graphEdges.push({ from: nodeId, to: claimNodeId });
          }
        });
      });

    return {
      nodes: graphNodes,
      edges: graphEdges,
      lineageEdges,
      reviewGateEdges: [],
    };
  }, [
    agentThreadById,
    agentThreads,
    allAgentThreadById,
    buildQuestionSetPreview,
    describeAgentWork,
    directClaimCountBySubRunId,
    formatScopeBadgeLabel,
    flowClaimProducerQuestionIdByClaimId,
    flowClaimsBySubRunId,
    getAgentPrimaryText,
    getAgentSecondaryText,
    getEffectiveAgentLifecycleStatus,
    getUnderlyingAgentThread,
    isActionableAgentThread,
    isRedundantQuestionWrapperAgent,
    normalizeAgentLifecycleStatus,
    summarizeQuestionSetPreview,
    taskStatusCountsBySubRunId,
  ]);

  const renderPrimaryAgentView = () => {
    if (viewMode === "hierarchy") {
      return agentHierarchy.length
        ? <div className="space-y-3">{agentHierarchy.map((node) => renderHierarchyNode(node, 0))}</div>
        : renderAgentListSection(agentThreads, "Agent hierarchy is not available yet.");
    }
    if (viewMode === "flow") {
      return (
        <AgentFlowDiagram
          nodes={agentFlowGraph.nodes}
          edges={agentFlowGraph.edges}
          lineageEdges={agentFlowGraph.lineageEdges}
          selectedNodeId={selectedAgentThread?.id ?? null}
          onSelectNode={setSelectedSubRunId}
          collapsedBranches={collapsedBranches}
          onCollapseChange={setCollapsedBranches}
          emptyMessage="No agent flow is available yet."
        />
      );
    }
    if (viewMode === "review") {
      return renderAgentListSection(reviewAgents, "No agents are currently awaiting review.");
    }
    if (viewMode === "active") {
      return renderAgentListSection(activeAgents, "No active agent threads right now.");
    }
    if (viewMode === "ready") {
      return renderAgentListSection(readyAgents, "No ready agent threads right now.");
    }
    if (viewMode === "blocked") {
      return renderAgentListSection(blockedAgents, "No blocked agents right now.");
    }
    if (viewMode === "complete") {
      return renderAgentListSection(completedAgents, "No completed agents yet.");
    }
    if (viewMode === "writers") {
      return renderAgentListSection(writerAgents, "No writer agents have been drafted yet.");
    }
    return (
      <div className="grid gap-4 xl:grid-cols-3">
        {agentColumns.map((column) => (
          <section
            key={column.key}
            className={clsx(
              "min-w-0 rounded-2xl border p-3",
                getKanbanStatusTheme(
                  column.key === "ready"
                    ? "open"
                    : column.key === "review"
                      ? "awaiting_review"
                      : column.key === "blocked"
                        ? "blocked"
                        : column.key === "complete"
                          ? "closed"
                          : column.key,
                ).columnClass,
            )}
          >
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span
                  className={clsx(
                    "h-2.5 w-2.5 rounded-full",
                    getKanbanStatusTheme(
                      column.key === "ready"
                        ? "open"
                        : column.key === "review"
                          ? "awaiting_review"
                          : column.key === "blocked"
                            ? "blocked"
                            : column.key === "complete"
                              ? "closed"
                              : column.key,
                    ).dotClass,
                  )}
                  aria-hidden="true"
                />
                <h3 className="text-sm font-semibold text-slate-900">{column.label}</h3>
              </div>
              <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-600">
                {column.items.length}
              </span>
            </div>
            {renderAgentListSection(column.items)}
          </section>
        ))}
      </div>
    );
  };

  const renderClaimsTab = () => {
    if (!relatedClaims.length && !unresolvedDirectClaimIds.length) {
      return <p className="text-sm text-slate-500">No claims are linked to this agent yet.</p>;
    }
    return (
      <div className="space-y-3">
        {unresolvedDirectClaimIds.length ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-medium text-amber-900">
              {`${relatedClaims.length} loaded of ${directLinkedClaimIds.length} linked claims`}
            </p>
            <p className="mt-1 text-xs text-amber-700">
              Some linked claim objects are not present in the current claim state yet.
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {unresolvedDirectClaimIds.slice(0, 12).map((claimId) => (
                <Chip key={claimId} size="sm" variant="flat" color="warning">
                  {claimId}
                </Chip>
              ))}
              {unresolvedDirectClaimIds.length > 12 ? (
                <Chip size="sm" variant="flat" color="warning">
                  {`+${unresolvedDirectClaimIds.length - 12} more`}
                </Chip>
              ) : null}
            </div>
          </div>
        ) : null}
        {relatedClaims.map((claim) => {
          const claimSection = claim?.assignment?.primary_section ?? claim?.primary_section ?? null;
          const confidenceValue = Number(claim?.assignment?.confidence ?? claim?.confidence);
          return (
            <article key={claim?.id ?? JSON.stringify(claim)} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2">
                  {claim?.type ? (
                    <Chip size="sm" variant="flat" color="primary">
                      {titleizeKey(claim.type)}
                    </Chip>
                  ) : null}
                  {claimSection ? (
                    <Chip size="sm" variant="flat" color="secondary">
                      {humanizeKey(claimSection)}
                    </Chip>
                  ) : null}
                  {Number.isFinite(confidenceValue) ? (
                    <Chip size="sm" variant="flat">
                      {`Confidence ${confidenceValue.toFixed(2)}`}
                    </Chip>
                  ) : null}
                </div>
                {claim?.date ? <span className="text-xs text-slate-400">{claim.date}</span> : null}
              </div>
              <p className="mt-2 text-sm leading-6 text-slate-800">{claim?.text ?? "Untitled claim"}</p>
              <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                {claim?.id ? <span className="font-mono">{claim.id}</span> : null}
                {Array.isArray(claim?.source_ids) && claim.source_ids.length ? (
                  <span>{`${claim.source_ids.length} sources`}</span>
                ) : null}
                {Array.isArray(claim?.quote_support) && claim.quote_support.length ? (
                  <span>{`${claim.quote_support.length} quotes`}</span>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    );
  };

  const renderOverviewTab = () => {
    if (!selectedAgentThread) return null;
    const selectedLifecycleStatus = getEffectiveAgentLifecycleStatus(selectedAgentThread);
    const workPackage = selectedAgentThread?.work_package ?? {};
    const normalizedMission = normalizeAgentDisplayText(workPackage?.mission ?? "");
    const normalizedObjective = normalizeAgentDisplayText(workPackage?.objective ?? "");
    const normalizedQuestion = normalizeAgentDisplayText(workPackage?.question ?? "");
    const showObjective = Boolean(workPackage?.objective) && normalizedObjective !== normalizedQuestion;
    const resultSummaryShort = String(selectedAgentThread?.result?.summary_short ?? "").trim();
    const resultSummaryLong = String(selectedAgentThread?.result?.summary_long ?? "").trim();
    const nextRecommendations = toArray(selectedAgentThread?.result?.next_recommendations);
    const normalizedResultSummaryShort = normalizeAgentDisplayText(resultSummaryShort);
    const normalizedResultSummaryLong = normalizeAgentDisplayText(resultSummaryLong);
    const resultShortIsRedundant = Boolean(
      normalizedResultSummaryShort &&
      [
        normalizedMission,
        normalizedObjective,
        normalizedQuestion,
      ].filter(Boolean).some((value) =>
        normalizedResultSummaryShort === value ||
        normalizedResultSummaryShort.includes(value) ||
        value.includes(normalizedResultSummaryShort),
      ),
    );
    const resultLongIsRedundant = Boolean(
      normalizedResultSummaryLong &&
      [
        normalizedMission,
        normalizedObjective,
        normalizedQuestion,
        normalizedResultSummaryShort,
      ].filter(Boolean).some((value) =>
        normalizedResultSummaryLong === value ||
        normalizedResultSummaryLong.includes(value),
      ),
    );
    const visibleResultSummaryShort = resultShortIsRedundant ? "" : resultSummaryShort;
    const visibleResultSummaryLong =
      resultLongIsRedundant || normalizedResultSummaryLong === normalizedResultSummaryShort
        ? ""
        : resultSummaryLong;
    const questionSet = selectedAgentThread?.question_set ?? {};
    const questionSetGroups = [
      { label: "Active", items: toArray(questionSet?.active) },
      { label: "Closed", items: toArray(questionSet?.closed) },
      { label: "Blocked", items: toArray(questionSet?.blocked) },
    ].filter((group) => group.items.length > 0);
    const activeQuestionPreview = summarizeQuestionSetPreview(
      toArray(questionSet?.active).slice(0, 3),
      toArray(questionSet?.active).length,
    );
    const blockedQuestionPreview = summarizeQuestionSetPreview(
      toArray(questionSet?.blocked).slice(0, 3),
      toArray(questionSet?.blocked).length,
    );
    const verificationAgent = isVerificationAgent(selectedAgentThread);
    const verificationOutcome = String(selectedAgentThread?.result?.verification_outcome ?? "").trim().toLowerCase();
    const artifacts = toArray(selectedAgentThread?.artifacts_produced);
    const graphWrites = selectedAgentThread?.graph_writes ?? {};
    const parentAgent = selectedAgentThread?.parent_agent_id
      ? (agentThreadById.get(selectedAgentThread.parent_agent_id) ?? null)
      : null;
    const childAgents = uniqueStrings([
      ...toArray(selectedAgentThread?.child_agent_ids),
      ...agentThreads
        .filter((agent) => String(agent?.parent_agent_id ?? "").trim() === String(selectedAgentThread?.id ?? "").trim())
        .map((agent) => agent?.id),
    ])
      .map((agentId) => agentThreadById.get(agentId))
      .filter(Boolean);
    const relatedTopics = uniqueStrings(toArray(graphWrites?.topic_ids));
    const relatedAggregates = uniqueStrings([
      ...toArray(graphWrites?.aggregate_ids),
      ...toArray(sharedGraph?.topic_aggregates)
        .filter((aggregate) => toArray(aggregate?.claim_ids).some((claimId) => directLinkedClaimIds.includes(claimId)))
        .map((aggregate) => aggregate?.aggregate_id)
        .filter(Boolean),
      ]);
    const editableWorkPackage = {
      objective: reviewDraft.objective,
      question: reviewDraft.question,
      constraints: reviewDraft.constraintsText.split("\n").map((entry) => entry.trim()).filter(Boolean),
      planned_first_steps: reviewDraft.plannedFirstStepsText.split("\n").map((entry) => entry.trim()).filter(Boolean),
      acceptance_criteria: reviewDraft.acceptanceCriteria,
      recipe_override_notes: reviewDraft.recipeOverrideNotes,
    };
    const handleReviewSubmit = async (action) => {
      if (!selectedReviewTargetAgent?.id || typeof onReviewAction !== "function") {
        return;
      }
      await onReviewAction({
        agentId: selectedReviewTargetAgent.id,
        action,
        workPackage: editableWorkPackage,
        reviewNotes: reviewDraft.reviewNotes,
      });
    };
    return (
      <div className="space-y-4">
        <div className="grid gap-3 lg:grid-cols-2">
          <article className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Work package</p>
            {workPackage?.mission ? <p className="mt-2 text-sm font-medium text-slate-900">{workPackage.mission}</p> : null}
            {showObjective ? <p className="mt-3 text-sm text-slate-800"><span className="font-semibold">Objective:</span> {workPackage.objective}</p> : null}
            {workPackage?.question && normalizedQuestion !== normalizedMission ? <p className="mt-2 text-sm text-slate-800"><span className="font-semibold">{verificationAgent ? "Claim to verify" : "Question"}:</span> {workPackage.question}</p> : null}
            {selectedAgentThreadStatus === "blocked" && selectedAgentThreadStatusReason ? (
              <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-5 text-rose-700">
                <span className="font-semibold">Why blocked:</span> <span>{selectedAgentThreadStatusReason}</span>
              </div>
            ) : null}
            {selectedAgentThreadAwaitingInheritedReview ? (
              <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-5 text-amber-800">
                <span className="font-semibold">Review gate:</span>{" "}
                <span>This node is blocked by a child or scope review gate. Select the actual reviewable agent to approve work.</span>
              </div>
            ) : null}
          </article>
          <article className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Progress</p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <Chip size="sm" variant="flat" color="primary">{formatAgentLifecycleLabel(selectedLifecycleStatus)}</Chip>
              {verificationOutcome ? (
                <Chip size="sm" variant="flat" color="success">{titleizeKey(verificationOutcome)}</Chip>
              ) : null}
              {selectedAgentThread?.result?.status ? (
                <Chip size="sm" variant="flat">{titleizeKey(selectedAgentThread.result.status)}</Chip>
              ) : null}
              {selectedAgentThread?.dependency_kind ? <Chip size="sm" variant="flat">{`Depends via ${humanizeKey(selectedAgentThread.dependency_kind)}`}</Chip> : null}
              {selectedAgentThread?.frame_stability_required ? <Chip size="sm" variant="flat">{`Needs ${humanizeKey(selectedAgentThread.frame_stability_required)}`}</Chip> : null}
            </div>
            <div className="mt-3 space-y-1.5 text-sm text-slate-600">
              <p>{`Claims: ${Number(
                directClaimCountBySubRunId.get(selectedAgentThread?.id) ??
                selectedAgentThread?.progress?.claim_count ??
                toArray(graphWrites?.claim_ids).length ??
                0,
              )}`}</p>
              <p>{`Active tasks: ${Number(selectedAgentThread?.progress?.active_task_count ?? toArray(selectedAgentThread?.execution?.active_task_ids).length ?? 0)}`}</p>
              <p>{`Queued tasks: ${Number(selectedAgentThread?.progress?.queued_task_count ?? toArray(selectedAgentThread?.execution?.queued_task_ids).length ?? 0)}`}</p>
              {activeQuestionPreview ? <p>{`Current questions: ${activeQuestionPreview}`}</p> : null}
              {!activeQuestionPreview && blockedQuestionPreview ? <p>{`Blocked questions: ${blockedQuestionPreview}`}</p> : null}
              {selectedAgentThread?.progress?.summary ? <p>{selectedAgentThread.progress.summary}</p> : null}
            </div>
          </article>
        </div>

        {visibleResultSummaryShort || visibleResultSummaryLong || nextRecommendations.length > 0 || selectedAgentThread?.result?.status ? (
          <article className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Result</p>
            {selectedAgentThread?.result?.status ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {verificationOutcome ? (
                  <Chip size="sm" variant="flat" color="success">{titleizeKey(verificationOutcome)}</Chip>
                ) : null}
                <Chip size="sm" variant="flat" color="primary">{titleizeKey(selectedAgentThread.result.status)}</Chip>
              </div>
            ) : null}
            {visibleResultSummaryShort ? <p className="mt-3 text-sm font-medium text-slate-900">{visibleResultSummaryShort}</p> : null}
            {visibleResultSummaryLong ? (
              <p className="mt-2 text-sm leading-6 text-slate-600">{visibleResultSummaryLong}</p>
            ) : null}
            {nextRecommendations.length > 0 ? (
              <div className="mt-3 space-y-1.5 text-sm text-slate-600">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Next recommendations</p>
                {nextRecommendations.map((recommendation) => (
                  <p key={recommendation}>{`• ${recommendation}`}</p>
                ))}
              </div>
            ) : null}
          </article>
        ) : null}

        {questionSetGroups.length > 0 ? (
          <article className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Mission questions</p>
            <div className="mt-3 grid gap-3 lg:grid-cols-3">
              {questionSetGroups.map((group) => (
                <div key={group.label} className="rounded-2xl border border-slate-100 bg-slate-50 p-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{group.label}</p>
                  <div className="mt-2 space-y-2">
                    {group.items.slice(0, 5).map((entry) => (
                      <div key={entry?.id ?? `${group.label}:${entry?.question}`} className="rounded-xl bg-white px-3 py-2 text-sm text-slate-700 shadow-sm">
                        <p className="font-medium text-slate-900">{entry?.question ?? entry?.id}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          {[entry?.priority ? titleizeKey(entry.priority) : null, formatTimestamp(entry?.last_updated_at)].filter(Boolean).join(" · ")}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </article>
        ) : null}

        {toArray(workPackage?.planned_first_steps).length ? (
          <article className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Planned first steps</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {toArray(workPackage.planned_first_steps).map((step) => (
                <Chip key={step} size="sm" variant="flat">{humanizeKey(step)}</Chip>
              ))}
            </div>
          </article>
        ) : null}

        {artifacts.length ? (
          <article className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Artifacts</p>
            <div className="mt-3 grid gap-3 lg:grid-cols-2">
              {artifacts.map((artifact) => (
                <div key={artifact?.key ?? JSON.stringify(artifact)} className="rounded-2xl border border-slate-100 bg-slate-50 p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <Chip size="sm" variant="flat" color={artifact?.state === "satisfied" ? "success" : artifact?.state === "missing" ? "warning" : "primary"}>
                      {humanizeKey(artifact?.state ?? "unknown")}
                    </Chip>
                    {artifact?.type ? <Chip size="sm" variant="flat">{humanizeKey(artifact.type)}</Chip> : null}
                  </div>
                  <p className="mt-2 text-sm font-medium text-slate-900">{artifact?.key ?? "Artifact"}</p>
                  {artifact?.summary ? <p className="mt-1 text-xs text-slate-600">{artifact.summary}</p> : null}
                </div>
              ))}
            </div>
          </article>
        ) : null}

        <article className="rounded-2xl border border-slate-200 bg-white p-4">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Graph writes</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {toArray(graphWrites?.claim_ids).length ? <Chip size="sm" variant="flat">{`${toArray(graphWrites.claim_ids).length} claims`}</Chip> : null}
            {relatedTopics.length ? <Chip size="sm" variant="flat">{`${relatedTopics.length} topics`}</Chip> : null}
            {relatedAggregates.length ? <Chip size="sm" variant="flat">{`${relatedAggregates.length} aggregates`}</Chip> : null}
            {toArray(graphWrites?.rendered_section_ids).length ? <Chip size="sm" variant="flat" color="success">{`${toArray(graphWrites.rendered_section_ids).length} rendered sections`}</Chip> : null}
          </div>
        </article>

        {parentAgent || childAgents.length ? (
          <article className="rounded-2xl border border-slate-200 bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Hierarchy Links</p>
            <div className="mt-3 space-y-3">
              {parentAgent ? (
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Parent</p>
                  <button
                    type="button"
                    onClick={() => setSelectedSubRunId(parentAgent.id)}
                    className="mt-1 rounded-xl bg-slate-100 px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-200"
                  >
                    {formatSubRunLabel(parentAgent)}
                  </button>
                </div>
              ) : null}
              {childAgents.length ? (
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Children</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {childAgents.map((agent) => (
                      <button
                        key={agent.id}
                        type="button"
                        onClick={() => setSelectedSubRunId(agent.id)}
                        className="rounded-xl bg-slate-100 px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-200"
                      >
                        {formatSubRunLabel(agent)}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </article>
        ) : null}

        {selectedAgentThreadCanReview ? (
          <article className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">Review Work Package</p>
                <p className="mt-1 text-sm text-amber-900">
                  Edit the briefing before approving this agent to run.
                </p>
              </div>
              {selectedReviewTargetAgent?.review_state ? (
                <Chip size="sm" variant="flat" color="warning">{titleizeKey(selectedReviewTargetAgent.review_state)}</Chip>
              ) : null}
            </div>
            <div className="mt-4 grid gap-3 lg:grid-cols-2">
              <label className="space-y-1 text-sm text-slate-700">
                <span className="text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Objective</span>
                <input
                  className="w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm"
                  value={reviewDraft.objective}
                  onChange={(event) => setReviewDraft((prev) => ({ ...prev, objective: event.target.value }))}
                />
              </label>
              <label className="space-y-1 text-sm text-slate-700">
                <span className="text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Question</span>
                <input
                  className="w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm"
                  value={reviewDraft.question}
                  onChange={(event) => setReviewDraft((prev) => ({ ...prev, question: event.target.value }))}
                />
              </label>
              <label className="space-y-1 text-sm text-slate-700">
                <span className="text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Constraints</span>
                <textarea
                  className="min-h-[108px] w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm"
                  value={reviewDraft.constraintsText}
                  onChange={(event) => setReviewDraft((prev) => ({ ...prev, constraintsText: event.target.value }))}
                />
              </label>
              <label className="space-y-1 text-sm text-slate-700">
                <span className="text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Planned First Steps</span>
                <textarea
                  className="min-h-[108px] w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm"
                  value={reviewDraft.plannedFirstStepsText}
                  onChange={(event) => setReviewDraft((prev) => ({ ...prev, plannedFirstStepsText: event.target.value }))}
                />
              </label>
              <label className="space-y-1 text-sm text-slate-700">
                <span className="text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Acceptance Criteria</span>
                <textarea
                  className="min-h-[108px] w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm"
                  value={reviewDraft.acceptanceCriteria}
                  onChange={(event) => setReviewDraft((prev) => ({ ...prev, acceptanceCriteria: event.target.value }))}
                />
              </label>
              <label className="space-y-1 text-sm text-slate-700">
                <span className="text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Recipe Override Notes</span>
                <textarea
                  className="min-h-[108px] w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm"
                  value={reviewDraft.recipeOverrideNotes}
                  onChange={(event) => setReviewDraft((prev) => ({ ...prev, recipeOverrideNotes: event.target.value }))}
                />
              </label>
            </div>
            <label className="mt-3 block space-y-1 text-sm text-slate-700">
              <span className="text-xs font-medium uppercase tracking-[0.12em] text-slate-500">Review Notes</span>
              <textarea
                className="min-h-[88px] w-full rounded-xl border border-amber-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm"
                value={reviewDraft.reviewNotes}
                onChange={(event) => setReviewDraft((prev) => ({ ...prev, reviewNotes: event.target.value }))}
              />
            </label>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="flat"
                onPress={() => handleReviewSubmit("edit")}
                isDisabled={selectedAgentThreadReviewSubmitting || typeof onReviewAction !== "function"}
              >
                Save Draft
              </Button>
              <Button
                size="sm"
                color="primary"
                onPress={() => handleReviewSubmit("approve")}
                isLoading={selectedAgentThreadReviewSubmitting}
                isDisabled={typeof onReviewAction !== "function"}
              >
                Approve
              </Button>
              <Button
                size="sm"
                variant="flat"
                color="warning"
                onPress={() => handleReviewSubmit("defer")}
                isDisabled={selectedAgentThreadReviewSubmitting || typeof onReviewAction !== "function"}
              >
                Defer
              </Button>
              <Button
                size="sm"
                variant="flat"
                color="danger"
                onPress={() => handleReviewSubmit("reject")}
                isDisabled={selectedAgentThreadReviewSubmitting || typeof onReviewAction !== "function"}
              >
                Reject
              </Button>
            </div>
          </article>
        ) : null}
      </div>
    );
  };

  if (!data) {
    return <p className="text-sm text-slate-500">Sub-agent runtime is not available yet.</p>;
  }

  return (
    <div className="space-y-4 overflow-auto">
      <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Sub-agents</p>
            <h3 className="mt-1 text-lg font-semibold text-slate-900">Agent board</h3>
            <p className="mt-1 text-sm text-slate-500">
              Active, queued, and completed agents. Select an agent to inspect its evidence and work queue.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {runtimeV3?.phase || runtimeV3?.active_phase ? (
              <Chip size="sm" variant="flat" color="primary">
                {titleizeKey(runtimeV3?.phase ?? runtimeV3?.active_phase)}
              </Chip>
            ) : null}
            {runtimeV3?.status || agentRuntime?.status ? (
              <Chip size="sm" variant="flat">
                {titleizeKey(runtimeV3?.status ?? agentRuntime?.status)}
              </Chip>
            ) : null}
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Chip size="sm" variant="flat" color="primary">{`${activeCount} active threads`}</Chip>
          <Chip size="sm" variant="flat">{`${readyCount} ready`}</Chip>
          <Chip size="sm" variant="flat" color="warning">{`${reviewCount} awaiting review`}</Chip>
          <Chip size="sm" variant="flat">{`${blockedCount} blocked`}</Chip>
          <Chip size="sm" variant="flat" color="success">{`${completedCount} completed`}</Chip>
          <Chip size="sm" variant="flat" color="secondary">{`${writerCount} writer agents`}</Chip>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {viewTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setViewMode(tab.key)}
              className={clsx(
                "rounded-xl px-3 py-2 text-sm font-medium transition-colors",
                viewMode === tab.key
                  ? "bg-slate-900 text-white"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="mt-4">
          {renderPrimaryAgentView()}
        </div>
      </section>

      <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm">
        {selectedAgentThread ? (
          <>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Selected agent</p>
                <h3 className="mt-1 text-lg font-semibold text-slate-900">{formatSubRunLabel(selectedAgentThread)}</h3>
                {selectedAgentThread?.id ? (
                  <p className="mt-1 font-mono text-xs text-slate-400">{selectedAgentThread.id}</p>
                ) : null}
                <p className="mt-1 text-sm text-slate-500">
                  {selectedAgentThread?.result?.summary_short || describeSubRunAction(selectedAgentThread, null).label}
                </p>
                {selectedAgentThread?.scope_type === "focus_area" ? (
                  <p className="mt-1 text-xs font-medium text-slate-600">
                    {`Parent aggregate over ${(childSectionLabelsByFocusId.get(selectedAgentThread?.id) ?? []).length} child ${(childSectionLabelsByFocusId.get(selectedAgentThread?.id) ?? []).length === 1 ? "section agent" : "section agents"}. Claims can appear here and on those child section agents.`}
                  </p>
                ) : (parentFocusLabelsBySectionId.get(selectedAgentThread?.id) ?? []).length ? (
                  <p className="mt-1 text-xs font-medium text-slate-600">
                    {`Child section agent under ${(parentFocusLabelsBySectionId.get(selectedAgentThread?.id) ?? []).join(" · ")}. Parent aggregates can repeat these claims.`}
                  </p>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                <Chip size="sm" variant="flat" color="primary">{formatScopeBadgeLabel(selectedAgentThread)}</Chip>
                <Chip size="sm" variant="flat">{formatAgentLifecycleLabel(getEffectiveAgentLifecycleStatus(selectedAgentThread))}</Chip>
                {selectedAgentThread?.result?.status ? (
                  <Chip size="sm" variant="flat" color="primary">{titleizeKey(selectedAgentThread.result.status)}</Chip>
                ) : null}
                {selectedAgentThread?.budget ? <Chip size="sm" variant="flat">{`Budget ${selectedAgentThread.budget}`}</Chip> : null}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2 border-b border-slate-200 pb-3">
              {detailTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setDetailTab(tab.key)}
                  className={clsx(
                    "rounded-xl px-3 py-2 text-sm font-medium transition-colors",
                    detailTab === tab.key
                      ? "bg-slate-900 text-white"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200",
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="mt-4">
              {detailTab === "overview" ? renderOverviewTab() : null}
              {detailTab === "claims" ? renderClaimsTab() : null}
              {detailTab === "questions" ? <QuestionsPanel questions={relatedQuestions} /> : null}
              {detailTab === "tasks" ? <TasksPanel tasks={relatedTasks} /> : null}
            </div>
          </>
        ) : (
          <p className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
            Select an agent to inspect its claims, questions, and tasks.
          </p>
        )}
      </section>
    </div>
  );
}

function InspectorShell({ loading, error, children, emptyMessage }) {
  if (loading) {
    return <Spinner color="primary" label="Loading" />;
  }
  if (error) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
        {error}
      </div>
    );
  }
  if (!children) {
    return <p className="text-sm text-slate-500">{emptyMessage}</p>;
  }
  return children;
}

export default function ResearchWorkspacePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const mainstore = useMemo(() => MainStore(), []);
  const [primitiveVersion, setPrimitiveVersion] = useState(0);
  const [activeConversationOverrideId, setActiveConversationOverrideId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabErrors, setTabErrors] = useState({});
  // Default to the Summary tab for better overview on load
  const [activeTab, setActiveTab] = useState("summary");
  const [selectedSummaryEntryId, setSelectedSummaryEntryId] = useState(null);
  const [tabData, setTabData] = useState({});
  const [tabLoading, setTabLoading] = useState({});
  const [composerValue, setComposerValue] = useState("");
  const [runDepth, setRunDepth] = useState("standard");
  const [sending, setSending] = useState(false);
  const [sendingMode, setSendingMode] = useState(null);
  const [rerenderingSummary, setRerenderingSummary] = useState(false);
  const [reviewSubmittingAgentId, setReviewSubmittingAgentId] = useState(null);
  const [ephemeralStatus, setEphemeralStatus] = useState({});
  const [currentRunStatus, setCurrentRunStatus] = useState(null);
  const [conversationMilestones, setConversationMilestones] = useState({});
  // Branch collapse state for the agent flow diagram
  const [collapsedBranches, setCollapsedBranches] = useState(new Set());
  const [graphFacetPreset, setGraphFacetPreset] = useState("hierarchy");
  const [facetPrimaryAxis, setFacetPrimaryAxis] = useState(() => getDefaultPrimaryAxisForPreset("hierarchy"));
  const [facetSecondaryAxis, setFacetSecondaryAxis] = useState(() => getDefaultSecondaryAxisForPreset("hierarchy", getDefaultPrimaryAxisForPreset("hierarchy")));
  const [facetOrder, setFacetOrder] = useState(() => getFacetPresetOrder("hierarchy"));
  const [facetCellRenderer, setFacetCellRenderer] = useState("tree");
  // Node type visibility toggles for the graph
  const [nodeTypeVisibility, setNodeTypeVisibility] = useState(() => getNodeTypeVisibilityForFacetPreset("hierarchy"));
  const [entityClaimThreshold, setEntityClaimThreshold] = useState(1);
  const [showWeakGraphEntities, setShowWeakGraphEntities] = useState(false);
  const [showSingleClaimThemes, setShowSingleClaimThemes] = useState(true);
  const [selectedFacetNodeId, setSelectedFacetNodeId] = useState(null);
  const [expandedRuns, setExpandedRuns] = useState({});
  const [visibleRunTurns, setVisibleRunTurns] = useState({});
  const [expandedTurnDetails, setExpandedTurnDetails] = useState({});
  const readerRef = useRef(null);
  const timelineEndRef = useRef(null);
  const timelineContainerRef = useRef(null);
  const primitiveSyncTimerRef = useRef(null);
  const resetInFlightRef = useRef(false);

  useEffect(() => {
    return () => {
      if (primitiveSyncTimerRef.current) {
        window.clearTimeout(primitiveSyncTimerRef.current);
        primitiveSyncTimerRef.current = null;
      }
    };
  }, []);

  const syncPrimitiveBackedPaneState = useCallback(
    (primitive) => {
      if (resetInFlightRef.current) {
        return;
      }
      if (!primitive) {
        return;
      }
      const nextConversationId =
        activeConversationOverrideId ??
        primitive?.referenceParameters?.research_chat?.activeConversationId ??
        null;
      const nextWorkspaceSnapshot = buildLocalWorkspaceSnapshot(primitive, nextConversationId);
      if (!nextWorkspaceSnapshot) {
        return;
      }
      const nextResearchState = buildLocalResearchState(primitive);
      const nextScope = nextWorkspaceSnapshot?.activeConversation?.scope ?? null;
      const nextEvidence = buildEvidenceSnapshot(nextResearchState, nextScope);
      const nextGraph = buildGraphSnapshot(nextResearchState, nextScope);
      const isLiveResearchRunning = Boolean(
        nextWorkspaceSnapshot?.liveRun?.running ||
        nextWorkspaceSnapshot?.researchRuntimeV3?.status === "running" ||
        Number(nextWorkspaceSnapshot?.status?.runningTaskCount ?? 0) > 0
      );
      setTabData((prev) => ({
        ...prev,
        evidence: mergeStableEvidenceSnapshot(prev?.evidence, nextEvidence, { isRunning: isLiveResearchRunning }),
        graph: hasGraphSnapshotData(nextGraph) ? nextGraph : prev?.graph ?? nextGraph,
        agents: {
          ...buildSubAgentsSnapshot(nextResearchState),
          questions: buildQuestionsSnapshot(nextResearchState, nextScope),
          tasks: buildTasksSnapshot(nextResearchState, nextScope),
        },
      }));
      setTabErrors((prev) => ({
        ...prev,
        evidence: null,
        graph: null,
        agents: null,
      }));
      setTabLoading((prev) => ({
        ...prev,
        evidence: false,
        graph: false,
        agents: false,
      }));
    },
    [activeConversationOverrideId],
  );

  const handleTargetPrimitiveUpdate = useCallback(() => {
    if (primitiveSyncTimerRef.current) {
      return false;
    }
    primitiveSyncTimerRef.current = window.setTimeout(() => {
      primitiveSyncTimerRef.current = null;
      const nextPrimitive = id ? mainstore.primitive(id) ?? null : null;
      syncPrimitiveBackedPaneState(nextPrimitive);
      setPrimitiveVersion((value) => value + 1);
    }, 100);
    return false;
  }, [id, mainstore, syncPrimitiveBackedPaneState]);

  useDataEvent(
    "set_parameter set_field set_fields relationship_update set_title",
    id ? [id] : undefined,
    handleTargetPrimitiveUpdate,
  );

  const targetPrimitive = useMemo(() => {
    if (!id) {
      return null;
    }
    return mainstore.primitive(id) ?? null;
  }, [id, mainstore, primitiveVersion]);
  const persistedActiveConversationId =
    targetPrimitive?.referenceParameters?.research_chat?.activeConversationId ?? null;
  const resolvedConversationId = activeConversationOverrideId ?? persistedActiveConversationId;
  const primitiveWorkspaceSnapshot = useMemo(
    () => buildLocalWorkspaceSnapshot(targetPrimitive, resolvedConversationId),
    [primitiveVersion, resolvedConversationId, targetPrimitive],
  );
  useEffect(() => {
    syncPrimitiveBackedPaneState(targetPrimitive);
  }, [primitiveVersion, syncPrimitiveBackedPaneState, targetPrimitive]);
  const renderWorkspace = primitiveWorkspaceSnapshot ?? null;
  const stableWorkspace = renderWorkspace;
  const stablePrimitive = stableWorkspace?.primitive ?? null;
  const stableStatus = stableWorkspace?.status ?? {};
  const stableTabCount = stableWorkspace?.tabCounts ?? {};
  const stableRuntimeV3 = stableWorkspace?.researchRuntimeV3 ?? null;

  const activeConversation = primitiveWorkspaceSnapshot?.activeConversation ?? null;
  const activeConversationId =
    activeConversation?.id ??
    primitiveWorkspaceSnapshot?.researchChat?.activeConversationId ??
    null;
  const pendingEntry = useMemo(
    () => findPendingEntry(activeConversation?.history),
    [activeConversation?.history],
  );
  const latestFinalResultId = useMemo(() => {
    const finalResults = (activeConversation?.history ?? [])
      .filter((entry) => entry?.kind === "final_result")
      .sort((left, right) => String(left.createdAt ?? "").localeCompare(String(right.createdAt ?? "")));
    return finalResults[finalResults.length - 1]?.id ?? null;
  }, [activeConversation?.history]);

  const summaryEntries = useMemo(() => {
    if (!latestFinalResultId) {
      return [];
    }
    const latest = (activeConversation?.history ?? []).find((entry) => entry?.id === latestFinalResultId);
    return latest ? [latest] : [];
  }, [activeConversation?.history, latestFinalResultId]);
  const selectedSummaryEntry = useMemo(
    () =>
      summaryEntries.find((entry) => entry.id === selectedSummaryEntryId) ??
      summaryEntries[summaryEntries.length - 1] ??
      null,
    [selectedSummaryEntryId, summaryEntries],
  );
  const activeScope = activeConversation?.scope ?? null;
  const localResearchState = useMemo(
    () => buildLocalResearchState(targetPrimitive),
    [primitiveVersion, targetPrimitive],
  );
  const localInspectorData = useMemo(
    () => ({
      evidence: buildEvidenceSnapshot(localResearchState, activeScope),
      graph: buildGraphSnapshot(localResearchState, activeScope),
      agents: {
        ...buildSubAgentsSnapshot(localResearchState),
        questions: buildQuestionsSnapshot(localResearchState, activeScope),
        tasks: buildTasksSnapshot(localResearchState, activeScope),
      },
    }),
    [activeScope, localResearchState],
  );
  const facetTreeSnapshot = useMemo(
    () => buildFacetTreeSnapshot(localResearchState, {
      scope: activeScope,
      preset: graphFacetPreset,
      order: facetOrder,
      showSingleClaimThemes,
      showWeakEntities: showWeakGraphEntities,
    }),
    [activeScope, facetOrder, graphFacetPreset, localResearchState, showSingleClaimThemes, showWeakGraphEntities],
  );
  const facetGraphSnapshot = useMemo(
    () => graphFacetPreset === "overview"
      ? null
      : buildFacetGraphSnapshot(localResearchState, {
          scope: activeScope,
          preset: graphFacetPreset,
          order: facetOrder,
          showSingleClaimThemes,
          showWeakEntities: showWeakGraphEntities,
        }),
    [activeScope, facetOrder, graphFacetPreset, localResearchState, showSingleClaimThemes, showWeakGraphEntities],
  );
  const facetGridSnapshot = useMemo(
    () => graphFacetPreset === "overview" || graphFacetPreset === "hierarchy"
      ? null
      : buildFacetGridSnapshot(localResearchState, {
          scope: activeScope,
          preset: graphFacetPreset,
          order: facetOrder,
          showSingleClaimThemes,
          showWeakEntities: showWeakGraphEntities,
          primaryAxis: facetPrimaryAxis,
          secondaryAxis: facetSecondaryAxis,
        }),
    [activeScope, facetOrder, facetPrimaryAxis, facetSecondaryAxis, graphFacetPreset, localResearchState, showSingleClaimThemes, showWeakGraphEntities],
  );
  const activeGraphSnapshot = graphFacetPreset === "overview"
      ? tabData.graph
      : facetGraphSnapshot;
  const activeGraphHasData = hasGraphSnapshotData(activeGraphSnapshot);
  const graphEntityStats = useMemo(() => {
    const entityNodes = toArray(activeGraphSnapshot?.nodes).filter((node) => node?.kind === "entity");
    const maxClaimCount = entityNodes.reduce(
      (maxValue, node) => Math.max(maxValue, Number(node?.claimCount ?? 0)),
      1,
    );
    const visibleEntityCount = entityNodes.filter(
      (node) => Number(node?.claimCount ?? 0) >= entityClaimThreshold,
    ).length;
    return {
      totalEntityCount: entityNodes.length,
      visibleEntityCount,
      maxClaimCount: Math.max(1, maxClaimCount),
    };
  }, [activeGraphSnapshot, entityClaimThreshold]);
  const selectedFacetNode = useMemo(
    () => findFacetNode(facetTreeSnapshot?.root, selectedFacetNodeId),
    [facetTreeSnapshot, selectedFacetNodeId],
  );
  const filteredGraphData = useMemo(() => {
    const threshold = showWeakGraphEntities ? 1 : Math.max(2, entityClaimThreshold);
    return filterGraphSnapshotByFacetNode(
      filterGraphSnapshotByEntityClaimCount(activeGraphSnapshot, threshold),
      selectedFacetNode,
    );
  }, [activeGraphSnapshot, entityClaimThreshold, selectedFacetNode, showWeakGraphEntities]);
  useEffect(() => {
    if (entityClaimThreshold > graphEntityStats.maxClaimCount) {
      setEntityClaimThreshold(graphEntityStats.maxClaimCount);
    }
  }, [entityClaimThreshold, graphEntityStats.maxClaimCount]);
  useEffect(() => {
    setNodeTypeVisibility(getNodeTypeVisibilityForFacetPreset(graphFacetPreset));
  }, [graphFacetPreset]);
  useEffect(() => {
    const nextPrimary = getDefaultPrimaryAxisForPreset(graphFacetPreset);
    const nextSecondary = getDefaultSecondaryAxisForPreset(graphFacetPreset, nextPrimary);
    const nextOrder = getFacetPresetOrder(graphFacetPreset);
    setFacetPrimaryAxis(nextPrimary);
    setFacetSecondaryAxis(nextSecondary);
    setFacetOrder(nextOrder);
    setFacetCellRenderer("tree");
  }, [graphFacetPreset]);
  useEffect(() => {
    setSelectedFacetNodeId(null);
  }, [activeScope, graphFacetPreset, showSingleClaimThemes, facetPrimaryAxis, facetSecondaryAxis, facetOrder]);
  const activeMilestones = conversationMilestones[activeConversationId] ?? [];
  const rawTimelineItems = useMemo(() => {
    const visibleHistory = (activeConversation?.history ?? []).filter(
      (entry) => entry?.kind !== "final_result" || entry?.id === latestFinalResultId,
    );
    const turnBlocks = visibleHistory
      .filter((entry) => entry?.kind === "turn_summary")
      .map((entry) => ({
        id: `turn_block_${entry.id}`,
        entry,
        timelineTime: new Date(entry.createdAt ?? 0).getTime(),
        turnIndex: Number(entry?.metadata?.turnIndex),
      }));
    const turnSummaryIndexes = new Set(
      turnBlocks
        .map((entry) => entry.turnIndex)
        .filter((value) => Number.isFinite(value)),
    );
    const groupedActivities = new Map();
    const inlineActivities = [];

    activeMilestones
      .slice()
      .sort(
        (left, right) =>
          new Date(left.createdAt ?? left.updatedAt ?? 0).getTime() -
          new Date(right.createdAt ?? right.updatedAt ?? 0).getTime(),
      )
      .forEach((entry) => {
      const turnIndex = Number(entry?.turnIndex);
      if (Number.isFinite(turnIndex) && turnSummaryIndexes.has(turnIndex)) {
        const existing = groupedActivities.get(turnIndex) ?? [];
        groupedActivities.set(turnIndex, [...existing, entry]);
        return;
      }
      const activityTime = new Date(entry.createdAt ?? entry.updatedAt ?? 0).getTime();
      const targetTurn = turnBlocks.find((turn) => turn.timelineTime >= activityTime);
      if (targetTurn) {
        const groupKey = Number.isFinite(targetTurn.turnIndex) ? targetTurn.turnIndex : targetTurn.id;
        const existing = groupedActivities.get(groupKey) ?? [];
        groupedActivities.set(groupKey, [...existing, entry]);
        return;
      }
      inlineActivities.push(entry);
    });

    const items = [
      ...visibleHistory.map((entry) => {
        if (entry?.kind === "turn_summary") {
          const turnIndex = Number(entry?.metadata?.turnIndex);
          const groupKey = Number.isFinite(turnIndex) ? turnIndex : `turn_block_${entry.id}`;
          const persistedWorkTrace = Array.isArray(entry?.metadata?.workTrace)
            ? entry.metadata.workTrace
            : [];
          return {
            id: `turn_block_${entry.id}`,
            timelineType: "turn_block",
            timelineTime: new Date(entry.createdAt ?? 0).getTime(),
            entry,
            turnIndex: Number.isFinite(turnIndex) ? turnIndex : null,
            updates: mergeActivityEntries(persistedWorkTrace, groupedActivities.get(groupKey) ?? []),
          };
        }
        return {
          id: entry.id,
          timelineType: "message",
          timelineTime: new Date(entry.createdAt ?? 0).getTime(),
          entry,
        };
      }),
      ...inlineActivities.map((entry) => ({
        id: entry.id,
        timelineType: "activity",
        timelineTime: new Date(entry.createdAt ?? entry.updatedAt ?? 0).getTime(),
        entry,
      })),
    ];

    return items.sort((left, right) => {
      if (left.timelineTime !== right.timelineTime) {
        return left.timelineTime - right.timelineTime;
      }
      if (left.timelineType !== right.timelineType) {
        return left.timelineType === "message" ? -1 : 1;
      }
      return String(left.id ?? "").localeCompare(String(right.id ?? ""));
    });
  }, [activeConversation?.history, activeMilestones, latestFinalResultId]);
  const timelineItems = useMemo(() => buildRunGroups(rawTimelineItems), [rawTimelineItems]);

  useEffect(() => {
    if (resetInFlightRef.current) {
      return;
    }
    if (!primitiveWorkspaceSnapshot) {
      return;
    }
    setLoading(false);
  }, [primitiveWorkspaceSnapshot]);

  useEffect(() => {
    if (!activeConversationId) {
      return undefined;
    }
    setCurrentRunStatus(null);
    setExpandedRuns({});
    setVisibleRunTurns({});
    setExpandedTurnDetails({});
    setConversationMilestones((prev) => ({
      ...prev,
      [activeConversationId]: prev[activeConversationId] ?? [],
    }));
  }, [activeConversationId]);

  useEffect(() => {
    if (resetInFlightRef.current) {
      return;
    }
    const nextGraph = localInspectorData.graph;
    const isLiveResearchRunning = Boolean(
      renderWorkspace?.liveRun?.running ||
      renderWorkspace?.researchRuntimeV3?.status === "running" ||
      Number(stableStatus?.runningTaskCount ?? 0) > 0
    );
    setTabData((prev) => ({
      ...prev,
      evidence: mergeStableEvidenceSnapshot(prev?.evidence, localInspectorData.evidence, { isRunning: isLiveResearchRunning }),
      graph: hasGraphSnapshotData(nextGraph) ? nextGraph : prev?.graph ?? nextGraph,
      agents: localInspectorData.agents,
    }));
    setTabErrors((prev) => ({
      ...prev,
      evidence: null,
      graph: null,
      agents: null,
    }));
    setTabLoading((prev) => ({
      ...prev,
      evidence: false,
      graph: false,
      agents: false,
    }));
  }, [localInspectorData, renderWorkspace?.liveRun?.running, renderWorkspace?.researchRuntimeV3?.status, stableStatus?.runningTaskCount]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      const now = Date.now();
      setEphemeralStatus((prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(([, entry]) => now - Number(entry.updatedAt ?? 0) < 20000),
        ),
      );
    }, 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    return () => {
      if (readerRef.current) {
        readerRef.current.cancel?.().catch?.(() => undefined);
        readerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const container = timelineContainerRef.current;
    if (!container || !timelineEndRef.current) {
      return;
    }
    const runIsActive =
      sending ||
      renderWorkspace?.liveRun?.running ||
      Number(stableStatus?.runningTaskCount ?? 0) > 0 ||
      currentRunStatus?.status === "running";
    const behavior = runIsActive ? "smooth" : "auto";
    container.scrollTo({ top: container.scrollHeight, behavior });
    timelineEndRef.current.scrollIntoView({ block: "end", behavior });
  }, [
    sending,
    currentRunStatus?.updatedAt,
    currentRunStatus?.content,
    currentRunStatus?.status,
    timelineItems.length,
    renderWorkspace?.liveRun?.updatedAt,
    renderWorkspace?.liveRun?.running,
    stableStatus?.turnIndex,
    stableStatus?.runningTaskCount,
  ]);

  const clearLocalWorkspaceState = useCallback(() => {
    setTabData({});
    setTabErrors({});
    setTabLoading({});
    setEphemeralStatus({});
    setCurrentRunStatus(null);
    setConversationMilestones({});
    setComposerValue("");
    setActiveConversationOverrideId(null);
    setLoading(true);
  }, []);

  const handleToggleRunExpansion = useCallback((runId, turnIds = []) => {
    setExpandedRuns((prev) => {
      const nextExpanded = !(prev[runId] === true);
      if (!nextExpanded) {
        setVisibleRunTurns((visiblePrev) => {
          const next = { ...visiblePrev };
          delete next[runId];
          return next;
        });
        setExpandedTurnDetails((detailPrev) => {
          const next = { ...detailPrev };
          turnIds.forEach((turnId) => {
            delete next[turnId];
          });
          return next;
        });
      }
      return {
        ...prev,
        [runId]: nextExpanded,
      };
    });
  }, []);

  const handleStreamPayload = useCallback(
    (payload) => {
      if (resetInFlightRef.current) {
        return;
      }
      const currentPrimitive = id ? mainstore.primitive(id) ?? null : null;
      let mutatedPrimitive = false;
      if (payload.research_state && currentPrimitive) {
        mutatedPrimitive = setPrimitiveResearchState(currentPrimitive, payload.research_state) || mutatedPrimitive;
      }
      if (Array.isArray(payload.research_claims) && currentPrimitive) {
        mutatedPrimitive = setPrimitiveResearchClaims(currentPrimitive, payload.research_claims) || mutatedPrimitive;
      }
      if (payload.research_chat && currentPrimitive) {
        mutatedPrimitive = setPrimitiveResearchChat(currentPrimitive, payload.research_chat) || mutatedPrimitive;
      } else if (payload.conversation_snapshot?.id && currentPrimitive) {
        const nextResearchChat = updateConversationSnapshot(
          currentPrimitive?.referenceParameters?.research_chat,
          payload.conversation_snapshot,
        );
        mutatedPrimitive = setPrimitiveResearchChat(currentPrimitive, nextResearchChat) || mutatedPrimitive;
      }
      if (mutatedPrimitive && currentPrimitive) {
        syncPrimitiveBackedPaneState(currentPrimitive);
        setPrimitiveVersion((value) => value + 1);
      }
      if (payload.ephemeral_status?.key) {
        const nextStatus = {
          ...normalizeActivityItem(
            {
              ...payload.ephemeral_status,
              id: payload.ephemeral_status.key,
              content: payload.ephemeral_status.message ?? payload.ephemeral_status.status,
            },
            "status",
          ),
          updatedAt: Date.now(),
        };
        setEphemeralStatus((prev) => ({
          ...prev,
          [payload.ephemeral_status.key]: nextStatus,
        }));
        setCurrentRunStatus(nextStatus);
      }
      if (payload.milestone?.id) {
        const targetConversationId =
          payload.milestone.conversationId ??
          payload.conversation_snapshot?.id ??
          activeConversationId;
        if (targetConversationId) {
          setConversationMilestones((prev) => {
            const current = Array.isArray(prev[targetConversationId]) ? prev[targetConversationId] : [];
            const next = [
              ...current.filter((entry) => entry.id !== payload.milestone.id),
              normalizeActivityItem(payload.milestone, "milestone"),
            ]
              .sort((left, right) => String(left.createdAt ?? "").localeCompare(String(right.createdAt ?? "")))
              .slice(-24);
            return {
              ...prev,
              [targetConversationId]: next,
            };
          });
        }
      }
      if (payload.activity?.id) {
        const nextActivity = normalizeActivityItem(payload.activity, payload.activity.kind ?? "update");
        const targetConversationId =
          payload.activity.conversationId ??
          activeConversationId;
        if (targetConversationId) {
          setConversationMilestones((prev) => {
            const current = Array.isArray(prev[targetConversationId]) ? prev[targetConversationId] : [];
            const matchingEntry = current.find(
              (entry) => getActivitySemanticKey(entry) === getActivitySemanticKey(nextActivity),
            );
            const mergedActivity = matchingEntry
              ? {
                  ...matchingEntry,
                  ...nextActivity,
                  id: matchingEntry.id,
                  createdAt: matchingEntry.createdAt ?? nextActivity.createdAt,
                  updatedAt: nextActivity.updatedAt ?? Date.now(),
                  repeatCount: Number(matchingEntry.repeatCount ?? 1) + 1,
                }
              : nextActivity;
            const next = [
              ...current.filter(
                (entry) =>
                  entry.id !== payload.activity.id &&
                  getActivitySemanticKey(entry) !== getActivitySemanticKey(nextActivity),
              ),
              mergedActivity,
            ]
              .sort((left, right) => String(left.createdAt ?? "").localeCompare(String(right.createdAt ?? "")))
              .slice(-240);
            return {
              ...prev,
              [targetConversationId]: next,
            };
          });
        }
        setCurrentRunStatus({
          ...nextActivity,
          kind: "status",
          status: "running",
          updatedAt: Date.now(),
        });
      }
      if (payload.error) {
        setError(`${payload.error}`);
      }
    },
    [activeConversationId, id, mainstore, syncPrimitiveBackedPaneState],
  );

  const streamConversationRun = useCallback(
    async (mode) => {
      const message = composerValue.trim();
      if (!message || !activeConversationId || sending) {
        return;
      }
      setSending(true);
      setSendingMode(mode);
      setError(null);
      setCurrentRunStatus(
        normalizeActivityItem(
          {
            id: "current_run",
            key: "current_run",
            status: "running",
            content: mode === "chat" ? "Checking the current research state…" : "Planning and running research…",
            updatedAt: Date.now(),
          },
          "status",
        ),
      );
      setComposerValue("");

      const response = await fetch(`/api/research/${id}/run`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          conversationId: activeConversationId,
          message,
          mode,
          desiredShape: DEFAULT_INTERACTIVE_DESIRED_SHAPE,
          ...(mode === "research" ? { depth: runDepth } : {}),
        }),
      });

      if (!response.ok || !response.body) {
        const payload = await response.json().catch(() => null);
        setSending(false);
        setSendingMode(null);
        setCurrentRunStatus(
          normalizeActivityItem(
            {
              id: "current_run_error",
              key: "current_run_error",
              status: "error",
              content: payload?.error ?? payload?.message ?? `Request failed: ${response.status}`,
              updatedAt: Date.now(),
            },
            "status",
          ),
        );
        throw new Error(payload?.error ?? payload?.message ?? `Request failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      readerRef.current = reader;
      const decoder = new TextDecoder();
      let buffer = "";

      try {
        let finished = false;
        while (true) {
          const { value, done } = await reader.read();
          if (done || finished) {
            break;
          }
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop() ?? "";
          for (const part of parts) {
            if (!part.startsWith("data:")) {
              continue;
            }
            const payload = JSON.parse(part.slice(5).trim());
            handleStreamPayload(payload);
            if (payload.done) {
              finished = true;
              break;
            }
          }
        }
      } finally {
        reader.releaseLock?.();
        if (readerRef.current === reader) {
          readerRef.current = null;
        }
        setSending(false);
        setSendingMode(null);
        window.setTimeout(() => {
          setCurrentRunStatus((previous) => {
            if (!previous) {
              return null;
            }
            return {
              ...previous,
              status: "idle",
            };
          });
        }, 1200);
      }
    },
    [
      activeConversationId,
      composerValue,
      handleStreamPayload,
      id,
      runDepth,
      sending,
    ],
  );

  const handleActivateConversation = useCallback(
    async (conversationId) => {
      if (!conversationId || conversationId === activeConversationId) {
        return;
      }
      setError(null);
      try {
        const result = await fetchJson(`/api/research/${id}/conversations/${conversationId}/activate`, {
          method: "POST",
          body: JSON.stringify({}),
        });
        setActiveConversationOverrideId(
          result?.workspace?.activeConversation?.id ??
          result?.workspace?.researchChat?.activeConversationId ??
          conversationId,
        );
        setPrimitiveVersion((value) => value + 1);
      } catch (fetchError) {
        setError(fetchError.message);
      }
    },
    [activeConversationId, id],
  );

  const handleCreateDeepDive = useCallback(
    async ({ anchorType, anchorIds, label }) => {
      setError(null);
      try {
        const result = await fetchJson(`/api/research/${id}/conversations`, {
          method: "POST",
          body: JSON.stringify({
            kind: "deep_dive",
            activate: true,
            title: label ? `Deep dive: ${truncateText(label, 48)}` : "",
            scope: {
              type: "deep_dive",
              anchorType,
              anchorIds,
              label,
            },
          }),
        });
        setActiveConversationOverrideId(
          result?.workspace?.activeConversation?.id ??
          result?.workspace?.researchChat?.activeConversationId ??
          null,
        );
        setPrimitiveVersion((value) => value + 1);
      } catch (fetchError) {
        setError(fetchError.message);
      }
    },
    [id],
  );

  const handleResetWorkspace = useCallback(
    async (mode) => {
      if (sending) {
        return;
      }
      const resetMode = mode === "full" ? "full" : "chat";
      const confirmed = window.confirm(
        resetMode === "full"
          ? "Restart the research run from scratch? This clears the current chat, claims, questions, tasks, and persisted research state."
          : "Reset the research chat? This keeps the current claims and research state but clears the conversation threads.",
      );
      if (!confirmed) {
        return;
      }
      setError(null);
      resetInFlightRef.current = resetMode === "full";
      clearLocalWorkspaceState();
      try {
        await fetchJson(`/api/research/${id}/reset`, {
          method: "POST",
          body: JSON.stringify({ mode: resetMode }),
        });
        resetInFlightRef.current = false;
        setPrimitiveVersion((value) => value + 1);
        setActiveTab("evidence");
      } catch (fetchError) {
        resetInFlightRef.current = false;
        setError(fetchError.message);
      }
    },
    [clearLocalWorkspaceState, id, sending],
  );

  const handleOpenSummary = useCallback((entryId) => {
    setSelectedSummaryEntryId(entryId);
    setActiveTab("summary");
  }, []);

  const handleUpdateTopicPolicy = useCallback(
    async (aggregateId, policy, enabled) => {
      if (!aggregateId || !id) return;
      setError(null);
      try {
        await fetchJson(`/api/research/${id}/topic-policy`, {
          method: "POST",
          body: JSON.stringify({
            aggregateId,
            policy,
            enabled,
            conversationId: activeConversationId,
          }),
        });
        const primitiveForUpdate = mainstore.primitive(id) ?? targetPrimitive ?? null;
        if (applyTopicPolicyToPrimitive(primitiveForUpdate, aggregateId, policy, enabled)) {
          syncPrimitiveBackedPaneState(primitiveForUpdate);
        }
      } catch (updateError) {
        setError(updateError.message);
      }
    },
    [activeConversationId, id, mainstore, syncPrimitiveBackedPaneState, targetPrimitive],
  );

  const handleRerenderSummary = useCallback(async () => {
    if (!activeConversationId || rerenderingSummary || sending) {
      return;
    }
    setRerenderingSummary(true);
    setError(null);
    try {
      const result = await fetchJson(`/api/research/${id}/rerender`, {
        method: "POST",
        body: JSON.stringify({
          conversationId: activeConversationId,
        }),
      });
      setPrimitiveVersion((value) => value + 1);
      if (result.entryId) {
        setSelectedSummaryEntryId(result.entryId);
      }
      setActiveTab("summary");
    } catch (fetchError) {
      setError(fetchError.message);
    } finally {
      setRerenderingSummary(false);
    }
  }, [activeConversationId, id, rerenderingSummary, sending]);

  const handleReviewAgent = useCallback(
    async ({ agentId, action, workPackage, reviewNotes }) => {
      if (!agentId || !id) {
        return;
      }
      setReviewSubmittingAgentId(agentId);
      setError(null);
      try {
        await fetchJson(`/api/research/${id}/agent-review`, {
          method: "POST",
          body: JSON.stringify({
            agentId,
            action,
            workPackage,
            reviewNotes,
          }),
        });
        setPrimitiveVersion((value) => value + 1);
      } catch (reviewError) {
        setError(reviewError.message);
      } finally {
        setReviewSubmittingAgentId(null);
      }
    },
    [id],
  );

  const conversations = renderWorkspace?.researchChat?.conversations ?? [];
  const rootConversation = useMemo(
    () => conversations.find((conversation) => conversation?.kind === "root") ?? null,
    [conversations],
  );
  const conversationFocusActive = Boolean(activeConversationId && rootConversation?.id && activeConversationId !== rootConversation.id);
  const handleResetConversationFocus = useCallback(async () => {
    if (!rootConversation?.id || rootConversation.id === activeConversationId) {
      return;
    }
    await handleActivateConversation(rootConversation.id);
  }, [activeConversationId, handleActivateConversation, rootConversation?.id]);
  const fallbackRunning = Boolean(
    sending ||
    renderWorkspace?.liveRun?.running ||
    renderWorkspace?.researchRuntimeV3?.status === "running" ||
    stableRuntimeV3?.status === "running" ||
    Number(stableStatus?.runningTaskCount ?? 0) > 0,
  );
  const activeWorkItems = useMemo(() => {
    const persisted = buildScopedActiveWorkItems(
      localResearchState,
      activeConversation?.scope ?? null,
    );
    if (persisted.length) {
      return persisted;
    }
    return rawTimelineItems
      .filter((item) => item.timelineType === "activity")
      .map((item) => {
        const normalized = normalizeActiveWorkDisplayItem(item.entry);
        return {
          ...normalized,
          label: resolveActivityContent(item.entry, localResearchState) || normalized.label,
          content: resolveActivityContent(item.entry, localResearchState) || normalized.content,
        };
      });
  }, [activeConversation?.scope, localResearchState, rawTimelineItems]);
  const visibleTimelineItems = useMemo(() => timelineItems, [timelineItems]);
  const liveStatusRunning = fallbackRunning;

  if (loading && !renderWorkspace) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-slate-100">
        <Spinner color="primary" label="Loading research workspace" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh_-_4rem)] w-full bg-[radial-gradient(circle_at_top_left,_rgba(15,23,42,0.08),_transparent_35%),linear-gradient(135deg,_#f8fafc,_#eef2ff_45%,_#f8fafc)] p-4">
      <div className="flex h-full gap-4">
        <section className="flex h-full min-w-[420px] max-w-[560px] flex-[1.05] flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white/95 shadow-2xl backdrop-blur">
          <header className="border-b border-slate-200 px-5 py-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <button
                  type="button"
                  onClick={() => navigate(`/item/${id}`)}
                  className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 transition hover:text-slate-600"
                >
                  Back to analysis
                </button>
                <h1 className="mt-2 text-xl font-semibold text-slate-900">
                  {stablePrimitive?.title ?? "Research workspace"}
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                  {stableStatus?.phase
                    ? `Phase: ${humanizeKey(stableStatus.phase, "unknown")}`
                    : "Interactive research"}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="flat"
                    startContent={<ResetChatIcon className="h-4 w-4" />}
                    onPress={() => void handleResetWorkspace("chat")}
                    isDisabled={sending}
                    className="bg-slate-100 text-slate-700"
                  >
                    Reset chat
                  </Button>
                  <Button
                    size="sm"
                    variant="flat"
                    color="danger"
                    startContent={<RestartRunIcon className="h-4 w-4" />}
                    onPress={() => void handleResetWorkspace("full")}
                    isDisabled={sending}
                  >
                    Restart run
                  </Button>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-wide text-slate-400">Turns</p>
                <p className="text-2xl font-semibold text-slate-900">{stableStatus?.turnIndex ?? 0}</p>
              </div>
            </div>
          </header>

          <div className="border-b border-slate-200 px-4 py-3">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Conversations</p>
              <div className="flex items-center gap-2">
                {activeConversation?.scope ? (
                  <Chip size="sm" variant="flat" color={activeConversation.scope.type === "deep_dive" ? "warning" : "default"}>
                    {formatScopeLabel(activeConversation.scope)}
                  </Chip>
                ) : null}
                {conversationFocusActive ? (
                  <Button
                    size="sm"
                    variant="flat"
                    className="bg-slate-100 text-slate-700"
                    onPress={() => void handleResetConversationFocus()}
                  >
                    Back to full view
                  </Button>
                ) : null}
              </div>
            </div>
            <div className="mt-3 flex max-h-40 flex-col gap-2 overflow-auto">
              {conversations.map((conversation) => {
                const isActive = conversation.id === activeConversationId;
                return (
                  <button
                    key={conversation.id}
                    type="button"
                    onClick={() => handleActivateConversation(conversation.id)}
                    className={`rounded-2xl border px-3 py-3 text-left transition ${
                      isActive
                        ? "border-slate-900 bg-slate-900 text-white shadow-lg"
                        : "border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-300 hover:bg-white"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-sm font-semibold">{conversation.title}</p>
                      {conversation.hasPending ? (
                        <Chip size="sm" variant="flat" color={isActive ? "warning" : "secondary"}>
                          Pending
                        </Chip>
                      ) : null}
                    </div>
                    <p className={`mt-1 text-xs ${isActive ? "text-slate-200" : "text-slate-500"}`}>
                      {conversation.lastMessage ?? "No messages yet."}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          <div ref={timelineContainerRef} className="flex-1 overflow-auto px-4 py-4">
            <div className="space-y-3">
              {visibleTimelineItems.map((item) => {
                if (item.timelineType === "activity") {
                  const activity = item.entry;
                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-2 pl-1 text-[11px] leading-5 text-slate-500"
                    >
                      <span>{resolveActivityContent(activity, localResearchState) || activity.content}</span>
                      {Number(activity.repeatCount ?? 1) > 1 ? (
                        <span className="inline-flex items-center rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                          x{activity.repeatCount}
                        </span>
                      ) : null}
                    </div>
                  );
                }
                if (item.timelineType === "run_group") {
                  const isExpanded = expandedRuns[item.id] === true;
                  const turns = item.turns ?? [];
                  const latestTurn = item.latestTurn ?? turns[turns.length - 1] ?? null;
                  const maxTurnIndex = turns.reduce((max, turn) => {
                    const turnIndex = Number(turn?.entry?.metadata?.turnIndex ?? turn?.turnIndex ?? 0);
                    return Number.isFinite(turnIndex) ? Math.max(max, turnIndex) : max;
                  }, 0);
                  const latestMetrics = parseTurnSummaryMetrics(latestTurn?.entry);
                  const latestRunRuntime = formatDurationCompact(latestTurn?.entry?.metadata?.runRuntimeMs);
                  const turnCount = Math.max(turns.length, maxTurnIndex);
                  const revealedCount = Math.min(
                    Math.max(visibleRunTurns[item.id] ?? 1, 1),
                    Math.max(1, turns.length),
                  );
                  const visibleTurns = isExpanded ? turns.slice(Math.max(0, turns.length - revealedCount)) : [];
                  const runSummaryRow = (
                    <div className="flex items-center gap-3">
                      <div className="h-px flex-1 bg-slate-200" />
                      <div className="flex shrink-0 items-center gap-2 text-[11px] text-slate-500">
                        {isExpanded ? (
                          <span className="font-medium text-slate-500">Close run steps</span>
                        ) : (
                          <>
                            <span className="font-medium text-slate-600">{`Run ${turnCount} ${turnCount === 1 ? "turn" : "turns"}`}</span>
                            {latestMetrics.claims !== null ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                                <ClaimsIcon className="h-3 w-3" />
                                <span>{latestMetrics.claims}</span>
                              </span>
                            ) : null}
                            {latestMetrics.questions !== null ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                                <QuestionsIcon className="h-3 w-3" />
                                <span>{latestMetrics.questions}</span>
                              </span>
                            ) : null}
                            {latestMetrics.tasks !== null ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                                <TasksIcon className="h-3 w-3" />
                                <span>{latestMetrics.tasks}</span>
                              </span>
                            ) : null}
                            {item.totalUpdates ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                                <ActivityRowIcon className="h-3 w-3" />
                                <span>{item.totalUpdates}</span>
                              </span>
                            ) : null}
                            {latestRunRuntime ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                                <span>{latestRunRuntime}</span>
                              </span>
                            ) : null}
                          </>
                        )}
                        <button
                          type="button"
                          onClick={() => handleToggleRunExpansion(item.id, turns.map((turn) => turn.id))}
                          className="inline-flex shrink-0 items-center rounded-full p-1 text-slate-500 transition hover:bg-white hover:text-slate-700"
                          aria-label={isExpanded ? "Collapse run details" : "Expand run details"}
                        >
                          {isExpanded ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                      <div className="h-px flex-1 bg-slate-200" />
                    </div>
                  );
                  return (
                    <article
                      key={item.id}
                      className="bg-transparent px-0 py-1 text-slate-700"
                    >
                      {isExpanded ? (
                        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3">
                          <div className="space-y-3">
                          {revealedCount < turns.length ? (
                            <div className="flex justify-center">
                              <button
                                type="button"
                                onClick={() =>
                                  setVisibleRunTurns((prev) => ({
                                    ...prev,
                                    [item.id]: Math.min(turns.length, revealedCount + 1),
                                  }))
                                }
                                className="inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-semibold text-slate-500 transition hover:bg-white hover:text-slate-700"
                              >
                                <ChevronRightIcon className="h-3.5 w-3.5 rotate-180" />
                                <span>Show older</span>
                              </button>
                            </div>
                          ) : null}
                          {visibleTurns.map((turn, index) => {
                            const turnEntry = turn.entry;
                            const turnNumber = Number(turnEntry?.metadata?.turnIndex ?? turn.turnIndex);
                            const turnId = turn.id;
                            const turnIsExpanded = expandedTurnDetails[turnId] === true;
                            const turnUpdates = turn.updates ?? [];
                            const workTraceUpdates = turnUpdates.filter((entry) => isWorkTraceActivity(entry));
                            const turnMetrics = parseTurnSummaryMetrics(turnEntry);
                            const turnRuntime = formatDurationCompact(turnEntry?.metadata?.turnRuntimeMs);
                            const turnChanges = turnEntry?.metadata?.turnChanges ?? {};
                            const questionChanges = turnChanges.questions ?? {};
                            const taskChanges = turnChanges.tasks ?? {};
                            const turnActivityCounts = countTurnActivityKinds(turnUpdates);
                            const openedQuestionCount =
                              Number(questionChanges.opened?.length ?? 0) > 0
                                ? Number(questionChanges.opened.length)
                                : turnActivityCounts.questionsOpened;
                            const closedQuestionCount =
                              Number(questionChanges.closed?.length ?? 0) > 0
                                ? Number(questionChanges.closed.length)
                                : turnActivityCounts.questionsClosed;
                            const openedTaskCount =
                              Number(taskChanges.opened?.length ?? 0) > 0
                                ? Number(taskChanges.opened.length)
                                : turnActivityCounts.tasksOpened;
                            const closedTaskCount =
                              Number(taskChanges.closed?.length ?? 0) > 0
                                ? Number(taskChanges.closed.length)
                                : turnActivityCounts.tasksClosed;
                            const changeSummaryRows = buildTurnChangeSummaryRows({
                              objectivesOpened: turnActivityCounts.objectivesOpened,
                              objectivesClosed: turnActivityCounts.objectivesClosed,
                              questionsOpened: openedQuestionCount,
                              questionsClosed: closedQuestionCount,
                              tasksOpened: openedTaskCount,
                              tasksClosed: closedTaskCount,
                            });
                            const isLastVisible = index === visibleTurns.length - 1;
                            return (
                              <div key={turnId} className="flex gap-3">
                                <div className="flex w-8 shrink-0 flex-col items-center">
                                  <div className="flex h-6 w-6 items-center justify-center rounded-full border border-slate-300 bg-white text-[11px] font-semibold text-slate-500">
                                    {Number.isFinite(turnNumber) ? turnNumber : "?"}
                                  </div>
                                  {!isLastVisible ? <div className="mt-1 w-px flex-1 bg-slate-200" /> : null}
                                </div>
                                <div className={`min-w-0 flex-1 ${!isLastVisible ? "pb-4" : ""}`}>
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0 flex flex-1 items-center gap-2">
                                      {!turnIsExpanded ? (
                                        <span className="shrink-0 text-[11px] font-medium text-slate-500">{`Turn ${Number.isFinite(turnNumber) ? turnNumber : "?"}`}</span>
                                      ) : null}
                                    {turnRuntime ? (
                                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                                        <span>{turnRuntime}</span>
                                      </span>
                                    ) : null}
                                    {turnMetrics.claims !== null ? (
                                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                                        <ClaimsIcon className="h-3 w-3" />
                                        <span>{turnMetrics.claims}</span>
                                      </span>
                                    ) : null}
                                    {turnMetrics.questions !== null ? (
                                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                                        <QuestionsIcon className="h-3 w-3" />
                                        <span>{turnMetrics.questions}</span>
                                      </span>
                                    ) : null}
                                    {turnMetrics.tasks !== null ? (
                                      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                                        <TasksIcon className="h-3 w-3" />
                                        <span>{turnMetrics.tasks}</span>
                                      </span>
                                    ) : null}
                                    </div>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setExpandedTurnDetails((prev) => ({
                                          ...prev,
                                          [turnId]: !turnIsExpanded,
                                        }))
                                      }
                                      className="inline-flex shrink-0 items-center rounded-full p-1 text-slate-500 transition hover:bg-white hover:text-slate-700"
                                      aria-label={turnIsExpanded ? "Collapse turn details" : "Expand turn details"}
                                    >
                                      {turnIsExpanded ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
                                    </button>
                                  </div>
                                  {turnIsExpanded ? (
                                    <div className="mt-2 pl-0.5">
                                      {changeSummaryRows.length ? (
                                        <div className="space-y-1">
                                          <div className="text-[8px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                            Changes
                                          </div>
                                          <div className="space-y-1 text-[9px] leading-4 text-slate-500">
                                            {changeSummaryRows.map((row) => {
                                              const RowIcon = row.icon;
                                              return (
                                                <div key={row.key} className="flex items-center gap-1.5">
                                                  <RowIcon className="h-3 w-3 shrink-0 text-slate-400" />
                                                  <span>{`${row.label}:`}</span>
                                                  <span>{`Open ${row.opened ?? 0}`}</span>
                                                  <span className="text-slate-300">/</span>
                                                  <span>{`Closed ${row.closed ?? 0}`}</span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      ) : null}
                                      {workTraceUpdates.length ? (
                                        <div className={`${changeSummaryRows.length ? "mt-3" : ""} space-y-1`}>
                                          <div className="text-[8px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                                            Work
                                          </div>
                                          <div className="space-y-1 text-[9px] leading-4 text-slate-500">
                                            {workTraceUpdates.map((activity) => {
                                              const ActivityIcon = pickActivityIcon(activity);
                                              return (
                                                <div key={activity.id} className="flex items-start gap-1.5">
                                                  <ActivityIcon className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
                                                  <span className="min-w-0">
                                                    {activity.content}
                                                    {Number(activity.repeatCount ?? 1) > 1 ? ` (x${activity.repeatCount})` : ""}
                                                  </span>
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      ) : null}
                                      {!changeSummaryRows.length && !workTraceUpdates.length ? (
                                        <div className="mt-2 text-[9px] leading-4 text-slate-400">
                                          No step changes captured for this turn.
                                        </div>
                                      ) : null}
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            );
                          })}
                          {runSummaryRow}
                          </div>
                        </div>
                      ) : runSummaryRow}
                    </article>
                  );
                }
                const entry = item.entry;
                const summaryPreview = entry.kind === "final_result" ? buildFinalResultPreview(entry) : "";
                return (
                  <article key={item.id} className={`rounded-2xl border p-3 shadow-sm ${getMessageTone(entry)}`}>
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-[11px] font-semibold uppercase tracking-wide">
                        {entry.kind === "question"
                            ? "Question"
                            : entry.kind === "final_result"
                              ? "Summary"
                              : entry.role === "user"
                                ? "You"
                                : "Research"}
                      </p>
                      {entry?.metadata?.pending ? (
                        <Chip size="sm" variant="flat" color="warning">
                          Awaiting reply
                        </Chip>
                      ) : null}
                    </div>
                    <div className="mt-2 text-sm leading-6">
                      {entry.kind === "final_result" ? (
                        <div className="flex items-center gap-3">
                          <span className="shrink-0 rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                            {entry?.metadata?.renderMode === "rerender" ? "Updated" : "Ready"}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleOpenSummary(entry.id)}
                            className="min-w-0 flex-1 truncate text-left text-sm font-medium text-emerald-900 hover:underline"
                          >
                            {summaryPreview || "Summary ready to open"}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOpenSummary(entry.id)}
                            className="shrink-0 rounded-full border border-emerald-300 bg-white px-3 py-1 text-xs font-semibold text-emerald-700 transition hover:border-emerald-400 hover:bg-emerald-50"
                          >
                            Open
                          </button>
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap">{entry.content}</p>
                      )}
                    </div>
                  </article>
                );
              })}
              {liveStatusRunning && activeWorkItems.length ? (
                <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-sky-900">
                  <div className="flex items-center gap-2">
                    <Spinner
                      size="sm"
                      color="primary"
                      classNames={{
                        circle1: "border-b-sky-200",
                        circle2: "border-b-sky-700",
                      }}
                    />
                    <span className="shrink-0 rounded-full bg-white/80 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700">
                      Active work
                    </span>
                    <span className="min-w-0 truncate text-[11px] leading-4 text-sky-800">
                      {activeWorkItems.length < 10
                        ? `${activeWorkItems.length} in flight`
                        : `${activeWorkItems.length} updates in flight`}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {activeWorkItems.slice(0, 8).map((item) => (
                      <span
                        key={item.id}
                        className="inline-flex max-w-full items-center rounded-full bg-white/85 px-2 py-1 text-[11px] font-medium leading-4 text-sky-900 shadow-sm"
                        title={item.detail ? `${item.content} — ${item.detail}` : item.content}
                      >
                        <span className="truncate">{item.content}</span>
                      </span>
                    ))}
                    {activeWorkItems.length > 8 ? (
                      <span className="inline-flex items-center rounded-full bg-sky-100 px-2 py-1 text-[11px] font-medium leading-4 text-sky-700">
                        {`+${activeWorkItems.length - 8} more`}
                      </span>
                    ) : null}
                  </div>
                </div>
              ) : null}
              {!visibleTimelineItems.length ? (
                <p className="text-sm text-slate-500">Start with a scoped research ask or ask what the graph already knows.</p>
              ) : null}
              <div ref={timelineEndRef} />
            </div>
          </div>

          <div className="border-t border-slate-200 px-4 py-4">
            {error ? (
              <div className="mb-3 rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                {error}
              </div>
            ) : null}
            {pendingEntry?.metadata?.pendingType ? (
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-violet-500">
                Reply required: {humanizeKey(pendingEntry.metadata.pendingType, "response")}
              </p>
            ) : null}
            <textarea
              value={composerValue}
              onChange={(event) => setComposerValue(event.target.value)}
              placeholder={
                pendingEntry?.metadata?.pendingType === "budget"
                  ? "Reply with continue or stop"
                  : pendingEntry?.metadata?.pendingType === "clarification"
                    ? "Answer the clarification question"
                    : "Ask the graph, or send a new research instruction"
              }
              className="min-h-[112px] w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 shadow-inner outline-none transition focus:border-slate-400 focus:bg-white"
            />
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">
                {pendingEntry
                  ? "Pending questions are routed back into the shared research state."
                  : "Ask = grounded orientation chat. Research = run the agent."}
              </p>
              <div className="flex items-center gap-2">
                <div className="flex items-center rounded-full border border-slate-200 bg-slate-50 p-0.5 text-xs">
                  {RUN_DEPTH_OPTIONS.map((option) => (
                    <button
                      key={option.key}
                      type="button"
                      title={option.hint}
                      onClick={() => setRunDepth(option.key)}
                      className={clsx(
                        "rounded-full px-2.5 py-1 font-medium transition",
                        runDepth === option.key
                          ? "bg-white text-slate-900 shadow-sm"
                          : "text-slate-500 hover:text-slate-700",
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
                <Button
                  variant="flat"
                  isDisabled={sending || !composerValue.trim() || Boolean(pendingEntry)}
                  onPress={() => {
                    void streamConversationRun("chat").catch((streamError) => {
                      setError(streamError.message);
                      setSending(false);
                      setSendingMode(null);
                    });
                  }}
                >
                  Ask
                </Button>
                <Button
                  color="primary"
                  isLoading={false}
                  isDisabled={sending || !composerValue.trim()}
                  startContent={
                    sending && sendingMode === "research" ? (
                      <Spinner
                        size="sm"
                        color="white"
                        classNames={{
                          circle1: "border-b-white/40",
                          circle2: "border-b-white",
                        }}
                      />
                    ) : null
                  }
                  onPress={() => {
                    void streamConversationRun("research").catch((streamError) => {
                      setError(streamError.message);
                      setSending(false);
                      setSendingMode(null);
                    });
                  }}
                >
                  {pendingEntry ? "Reply" : "Research"}
                </Button>
              </div>
            </div>
          </div>
        </section>

        <section className="flex h-full min-w-0 flex-[1.35] flex-col overflow-hidden rounded-[28px] border border-slate-200 bg-white/90 shadow-2xl backdrop-blur">
          <header className="border-b border-slate-200 px-5 py-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Inspectors</p>
                <h2 className="mt-2 text-xl font-semibold text-slate-900">
                  {activeTab === "summary" ? "Summary" : "Shared research state"}
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  {activeTab === "summary"
                    ? "Open summaries from chat history here and refresh them against the persisted state."
                    : "Inspect the live corpus without pulling the full debug payload into the page shell."}
                </p>
              </div>
            </div>
          </header>
          <div className="flex-1 overflow-hidden px-5 py-4">
            <div className="flex h-full flex-col">
              <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-3">
                {INSPECTOR_TABS.map((tab) => {
                  const isActive = activeTab === tab.key;
                   const count =
                    tab.key === "summary"
                      ? summaryEntries.length
                      : tab.key === "evidence"
                      ? tabData.evidence?.counts?.claims ?? stableTabCount?.evidence?.claims ?? 0
                      : tab.key === "graph"
                      ? (
                          Number(tabData.graph?.nodes?.length ?? 0) ||
                          Number(tabData.graph?.counts?.nodes ?? 0) ||
                          stableTabCount?.graph?.entities ||
                          0
                        )
                      : tab.key === "flow"
                        ? (
                            Number(tabData.agents?.agentFlowGraph?.nodes?.length ?? 0) ||
                            0
                          )
                        : tab.key === "flow"
                        ? (
                            Number(tabData.agents?.agentFlowGraph?.nodes?.length ?? 0) ||
                            0
                          )
                        : tab.key === "agents"
                        ? (
                            tabData.agents?.plannedAgentUnits?.length ||
                            stableTabCount?.runtime?.totalUnits ||
                            0
                          )
                      : 0;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => {
                        setActiveTab(tab.key);
                      }}
                      className={`rounded-full border px-4 py-2 text-sm font-medium transition ${
                        isActive
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900"
                      }`}
                    >
                      {`${tab.label} (${count})`}
                    </button>
                  );
                })}
              </div>
              <div className="h-[calc(100vh_-_16rem)] overflow-auto pt-4">
                {activeTab === "summary" ? (
                  <InspectorShell
                    loading={false}
                    error={null}
                    emptyMessage="Summary is not available yet."
                  >
                    <SummaryPanel
                      summaryEntries={summaryEntries}
                      selectedEntryId={selectedSummaryEntry?.id ?? null}
                      primitiveSummarySource={targetPrimitive}
                      onSelectEntry={setSelectedSummaryEntryId}
                      onRerender={handleRerenderSummary}
                      rerendering={rerenderingSummary}
                    />
                  </InspectorShell>
                ) : null}
                {activeTab === "evidence" ? (
                  <InspectorShell
                    loading={tabLoading.evidence && !tabData.evidence}
                    error={tabErrors.evidence}
                    emptyMessage="Claims are not available yet."
                  >
                    <EvidencePanel
                      data={tabData.evidence}
                      onOpenDeepDive={handleCreateDeepDive}
                      onUpdateTopicPolicy={handleUpdateTopicPolicy}
                    />
                  </InspectorShell>
                ) : null}
                {activeTab === "flow" ? (
                  <InspectorShell
                    loading={false}
                    error={null}
                    emptyMessage="Agent flow diagram is not available yet."
                  >
                    <div className="flex h-full min-h-0 flex-col">
                      <AgentFlowDiagram
                        nodes={tabData.agents?.agentFlowGraph?.nodes ?? []}
                        edges={tabData.agents?.agentFlowGraph?.edges ?? []}
                        lineageEdges={tabData.agents?.agentFlowGraph?.lineageEdges ?? []}
                        collapsedBranches={collapsedBranches}
                        onCollapseChange={setCollapsedBranches}
                        emptyMessage="No agent flow is available yet."
                      />
                    </div>
                  </InspectorShell>
                ) : null}
                {activeTab === "graph" ? (
                  <InspectorShell
                    loading={tabLoading.graph && !tabData.graph}
                    error={tabErrors.graph}
                    emptyMessage="Graph data is not available yet."
                  >
                    {hasGraphSnapshotData(tabData.graph) ? (
                      <div className="flex flex-1 flex-col min-h-0 h-full">
                        <ResearchKnowledgeGraph
                          graph={tabData.graph}
                          claims={tabData.evidence?.allClaims ?? []}
                          entities={(localResearchState ?? {}).entities ?? []}
                          onOpenDeepDive={handleCreateDeepDive}
                        />
                      </div>
                    ) : null}
                  </InspectorShell>
                ) : null}
                {activeTab === "agents" ? (
                  <InspectorShell
                    loading={tabLoading.agents && !tabData.agents}
                    error={tabErrors.agents}
                    emptyMessage="Agent activity is not available yet."
                  >
                    <AgentsPane
                      data={tabData.agents}
                      onReviewAction={handleReviewAgent}
                      reviewSubmittingAgentId={reviewSubmittingAgentId}
                    />
                  </InspectorShell>
                ) : null}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
